import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  getBrandColor,
  type BrandProfileKey,
  DEFAULT_BRAND_BY_PROFILE,
} from '../../../theme/profileBrandColors';
import { PhoneSimulatorContainer } from './PhoneSimulatorContainer';
import { SchoolEmbedMobileHeader } from './SchoolEmbedMobileHeader';
import {
  SchoolSimTelemetryStrip,
  type SchoolTurnoOpt,
} from './SchoolSimTelemetryStrip';
import { ACWalkthrough } from '../../../components/help/Walkthrough';
import { useCockpitTourOnce } from '../../training/useCockpitTourOnce';
import { notifyEmbedPanelActive } from '../../training/tourEnv';

import {
  SHIFT_WINDOWS,
  buildQueueForShift,
  buildQueueFromPlan,
  buildRfidEventIsoList,
  dateAtYmdHm,
  dateToHm,
  dateToMinutes,
  defaultSimSchoolDay,
  firstAvailableShift,
  matchTurnoToShift,
  nextAvailableShift,
  nextSchoolDay,
  parseHm,
  setDateToHm,
  ymdForDiaSemana,
  ymdToDiaSemana,
  type DiaSemanaKey,
  type QueueControl,
  type ShiftDayPlan,
  type ShiftKey,
  type SimEngineMode,
  type SimEvent,
} from '../sim/simDayTemplates';
import {
  countByPct,
  loadPresenceMix,
  normalizePresenceMix,
  savePresenceMix,
  shuffleCopy,
  type SimPresenceMix,
} from '../sim/simPresenceMix';
import { FileExplorerContainer } from './FileExplorerContainer';
import { OfficeEditorWindow } from './OfficeEditorWindow';
import { WindowControls } from './WindowControls';
import { CitiesExplorerContainer } from './CitiesExplorerContainer';
import { CockpitTaskbar } from './taskbar/CockpitTaskbar';
import { CockpitDashboardBackground, ROLE_DEFINITIONS } from './CockpitDashboardBackground';
import { SimulatedUserCardSelector, MOCK_USERS_BY_ROLE } from './SimulatedUserCardSelector';
import { CockpitCubeTransition } from './CockpitCubeTransition';
import { CockpitStartMenu } from './taskbar/CockpitStartMenu';
import type { PhoneSlotId, PhoneSlotProfile } from './taskbar/PhoneSlotStrip';
import { ROLES_LIST } from './taskbar/UserProfile';
import { Folder, Compass, Monitor, ShieldAlert, FileText, User } from 'lucide-react';
import { useSimulationStore } from '../stores/useSimulationStore';
import { useSimSessionFocus } from '../stores/useSimSessionFocus';
import { api, apiFetch, ensureCsrfToken } from '../../../api/client';
import { simulationApi } from '../services/simulationApi';
import { SimActorPairingQrModal } from './SimActorPairingQrModal';
import { SS, buscarEscolasMunicipio } from '../../sim-onboarding';
import type { Cidade } from '../../sim-onboarding';
import type { SimulationSession } from '../../sim-onboarding/types';

function focusedSimSessionId(): string | undefined {
  return useSimSessionFocus.getState().focusedSessionId || undefined;
}

function isSimStudioSession(s: any): boolean {
  const p = String(s?.user?.perfil || s?.perfil || '').toUpperCase();
  return p === 'SIM_STUDIO' || s?.user?.id === 'sim-studio' || s?.id === 'sim-studio';
}

/** Extrai mensagem útil de erro Nest/class-validator. */
function formatApiError(err: any, fallback: string): string {
  const m = err?.error?.message ?? err?.message ?? err?.error;
  if (Array.isArray(m)) return m.filter(Boolean).join('; ') || fallback;
  if (typeof m === 'string' && m.trim()) return m;
  if (m && typeof m === 'object') {
    if (typeof m.message === 'string' && m.message.trim()) return m.message;
    try {
      return JSON.stringify(m);
    } catch {
      return fallback;
    }
  }
  return fallback;
}


/** JWT para robots E2E: prioriza diretor da escola (com escolaId) — nunca usa SIM_STUDIO/ac_token do prefeito. */
function resolveSimBearerToken(preferPerfil?: string, schoolId?: string): string | null {
  let fromStore: Record<string, any> = {};
  try {
    fromStore = useSimulationStore.getState().activeSessions || {};
  } catch { /* noop */ }
  let fromLs: Record<string, any> = {};
  try {
    fromLs = JSON.parse(localStorage.getItem('ac_sessions') || '{}') || {};
  } catch { /* noop */ }
  const merged = { ...fromLs, ...fromStore };

  if (schoolId) {
    const scopedKeys = [
      `${schoolId}::DIRETOR`,
      `${schoolId}::ESCOLA_DIRETOR`,
      `${schoolId}::SECRETARIA`,
      `${schoolId}::PROFESSOR`,
    ];
    for (const key of scopedKeys) {
      const s = merged[key];
      if (
        s?.access_token &&
        !isSimStudioSession(s) &&
        (s.user?.escolaId === schoolId || s.schoolId === schoolId || s.user?.isSimulation)
      ) {
        return s.access_token;
      }
    }
  }

  const values = Object.values(merged).filter(
    (s: any) => s?.access_token && !isSimStudioSession(s),
  ) as any[];
  if (preferPerfil) {
    const want = preferPerfil.toUpperCase();
    const hit = values.find((s) => {
      const p = String(s?.user?.perfil || '').toUpperCase();
      if (want === 'ESCOLA') {
        return p === 'ESCOLA' && (!schoolId || s.user?.escolaId === schoolId || s.schoolId === schoolId);
      }
      if (want === 'DIRETOR') {
        return (
          p === 'ESCOLA' &&
          String(s?.user?.cargo || '').toUpperCase() === 'DIRETOR' &&
          (!schoolId || s.user?.escolaId === schoolId || s.schoolId === schoolId)
        );
      }
      return p === want && (!schoolId || s.user?.escolaId === schoolId || s.schoolId === schoolId);
    });
    if (hit?.access_token) return hit.access_token;
    if (want === 'ESCOLA' || want === 'DIRETOR') {
      const dir = values.find(
        (s) =>
          String(s?.user?.perfil || '').toUpperCase() === 'ESCOLA' &&
          String(s?.user?.cargo || '').toUpperCase() === 'DIRETOR' &&
          (!schoolId || s.user?.escolaId === schoolId || s.schoolId === schoolId),
      );
      if (dir?.access_token) return dir.access_token;
    }
  }

  if (schoolId) {
    const scoped = values.find(
      (s) => s?.user?.escolaId === schoolId || s?.schoolId === schoolId,
    );
    if (scoped?.access_token) return scoped.access_token;
    // Sem JWT da escola: NÃO cair no ac_token do prefeito (SIM_STUDIO → 403 no EscolaScopeGuard)
    return null;
  }

  const withSchool = values.find((s) => s?.user?.escolaId || s?.schoolId);
  if (withSchool?.access_token) return withSchool.access_token;

  const perfil = String(localStorage.getItem('ac_perfil') || '').toUpperCase();
  if (perfil === 'SIM_STUDIO') return null;
  try {
    const u = JSON.parse(localStorage.getItem('ac_user') || 'null');
    if (String(u?.perfil || '').toUpperCase() === 'SIM_STUDIO') return null;
  } catch { /* noop */ }
  return localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token');
}

function persistSimActorSession(
  schoolId: string,
  actorId: string,
  roleKey: string,
  sessionData: any,
  schoolName?: string,
) {
  const tagged = {
    ...sessionData,
    schoolId,
    schoolName: schoolName || null,
    role: roleKey,
    user: sessionData.user || { id: actorId, perfil: roleKey },
  };
  const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
  sessions[actorId] = tagged;
  sessions[`${schoolId}::${roleKey}`] = tagged;
  if (roleKey === 'DIRETOR') {
    sessions[`${schoolId}::ESCOLA_DIRETOR`] = tagged;
  }
  if (roleKey === 'PROFESSOR') {
    sessions.PROFESSOR = tagged;
  }
  localStorage.setItem('ac_sessions', JSON.stringify(sessions));
  try {
    useSimulationStore.setState({
      activeSessions: {
        ...(useSimulationStore.getState().activeSessions || {}),
        ...sessions,
      },
    });
  } catch { /* noop */ }
  return tagged;
}

/** Garante JWT do diretor da escola em ac_sessions (para RFID / APIs /escolas/:id). */
async function ensureDirectorTokenForSchool(
  schoolId: string,
  schoolName?: string,
): Promise<string | null> {
  const existing =
    resolveSimBearerToken('DIRETOR', schoolId) ||
    resolveSimBearerToken('ESCOLA', schoolId);
  if (existing) return existing;

  const actors = await simulationApi.getSchoolActors(schoolId);
  const diretor =
    actors.find(
      (a: any) =>
        String(a.perfil || '').toUpperCase() === 'ESCOLA' &&
        String(a.cargo || '').toUpperCase() === 'DIRETOR',
    ) ||
    actors.find((a: any) => String(a.perfil || '').toUpperCase() === 'ESCOLA');

  if (!diretor?.id) return null;

  const { data } = await api.post('/simulation/start', {
    escolaId: schoolId,
    actorIds: [diretor.id],
    simulationSessionId: focusedSimSessionId(),
  });
  const sessionBag = data?.sessions || data || {};
  const sessionData =
    sessionBag.ESCOLA_DIRETOR ||
    sessionBag[diretor.id] ||
    Object.values(sessionBag).find((s: any) => s?.access_token);

  if (!(sessionData as any)?.access_token) return null;

  const tagged = persistSimActorSession(
    schoolId,
    diretor.id,
    'DIRETOR',
    sessionData,
    schoolName,
  );
  return tagged.access_token as string;
}

/** Renova JWT do professor da aula (evita 401 SESSION_REPLACED / token errado). */
async function ensureProfessorTokenForSchool(
  schoolId: string,
  professorId: string,
  schoolName?: string,
): Promise<{ token: string; user: any } | null> {
  if (!professorId) return null;

  try {
    const { data } = await api.post('/simulation/start', {
      escolaId: schoolId,
      actorIds: [professorId],
      simulationSessionId: focusedSimSessionId(),
    });
    const sessionBag = data?.sessions || data || {};
    const sessionData =
      sessionBag[professorId] ||
      sessionBag.PROFESSOR ||
      Object.values(sessionBag).find(
        (s: any) => s?.user?.id === professorId && s?.access_token,
      ) ||
      Object.values(sessionBag).find((s: any) => s?.access_token);

    if (!(sessionData as any)?.access_token) {
      const skipped = data?.meta?.skipped;
      console.warn('[ensureProfessorToken]', professorId, 'sem token', {
        keys: Object.keys(sessionBag || {}),
        skipped,
      });
      return null;
    }

    const tagged = persistSimActorSession(
      schoolId,
      professorId,
      'PROFESSOR',
      sessionData,
      schoolName,
    );
    return { token: tagged.access_token as string, user: tagged.user };
  } catch (err: any) {
    console.warn(
      '[ensureProfessorToken] falha',
      professorId,
      err?.response?.data || err?.message || err,
    );
    return null;
  }
}

/** Prepara escola para Chronos/RFID sem exigir janela desktop aberta. */
async function ensureChronosSchoolReady(
  schoolId: string,
  schoolName?: string,
): Promise<{ directorToken: string | null; error?: string }> {
  try {
    await ensureCsrfToken();
  } catch {
    /* noop */
  }
  try {
    const token = await ensureDirectorTokenForSchool(schoolId, schoolName);
    if (!token) {
      return {
        directorToken: null,
        error:
          'Sem JWT do diretor — simulation/start não gerou sessão (diretor ausente ou seed incompleto). Não é necessário abrir janelas; rode Preparar / seed da escola.',
      };
    }
    return { directorToken: token };
  } catch (err: any) {
    return {
      directorToken: null,
      error: err?.response?.data?.message || err?.message || String(err),
    };
  }
}

function hydrateActiveSessionsFromStorage(): void {
  try {
    const raw = localStorage.getItem('ac_sessions');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !Object.keys(parsed).length) return;
    const current = useSimulationStore.getState().activeSessions;
    if (!current || !Object.keys(current).length) {
      useSimulationStore.setState({ activeSessions: parsed });
    }
  } catch { /* noop */ }
}

export interface AcEnvironment {
  id: string;
  name: string;
  cor: string;
  id_Rede?: string;
  id_estado?: string;
  id_cidade?: string;
  id_escola?: string;
}

type DesktopTabId =
  | 'PREFEITO'
  | 'DIRETOR'
  | 'SECRETARIA'
  | 'COORDENADOR'
  | 'PROFESSOR'
  | 'RESPONSAVEL'
  | 'ALUNO';

type SchoolRoleTab =
  | 'DIRETOR'
  | 'SECRETARIA'
  | 'COORDENADOR'
  | 'PROFESSOR'
  | 'RESPONSAVEL'
  | 'ALUNO';

const DESKTOP_WINDOW_TABS: Array<{ id: DesktopTabId; label: string; hierarchyLevel: number; needsSchool: boolean }> = [
  { id: 'PREFEITO', label: '🏛️ Prefeito', hierarchyLevel: 1, needsSchool: false },
  { id: 'DIRETOR', label: '🎓 Diretor', hierarchyLevel: 2, needsSchool: true },
  { id: 'SECRETARIA', label: '📄 Secretaria', hierarchyLevel: 3, needsSchool: true },
  { id: 'COORDENADOR', label: '👥 Coordenador', hierarchyLevel: 3, needsSchool: true },
  { id: 'PROFESSOR', label: '👨‍🏫 Professor', hierarchyLevel: 4, needsSchool: true },
  { id: 'RESPONSAVEL', label: '👨‍👩‍👦 Responsável', hierarchyLevel: 4, needsSchool: true },
  { id: 'ALUNO', label: '👦 Aluno', hierarchyLevel: 5, needsSchool: true },
];

const SCHOOL_ROLE_REDIRECT: Record<SchoolRoleTab, string> = {
  DIRETOR: '/admin/dashboard',
  SECRETARIA: '/equipe/dashboard',
  COORDENADOR: '/equipe/dashboard',
  PROFESSOR: '/professor/dashboard',
  RESPONSAVEL: '/responsavel/dashboard',
  ALUNO: '/aluno/dashboard',
};

/**
 * Tabs desktop (coluna esquerda do header da janela escola).
 * PREFEITO no início; PROF/RESP/ALUNO no final.
 * A janela continua abrindo em DIRETOR.
 */
const SCHOOL_DESKTOP_TAB_IDS: DesktopTabId[] = [
  'PREFEITO',
  'DIRETOR',
  'SECRETARIA',
  'COORDENADOR',
  'PROFESSOR',
  'RESPONSAVEL',
  'ALUNO',
];
/** Toggles de celular embutidos (coluna direita) — independente das abas desktop. */
const SCHOOL_MOBILE_TAB_IDS: Array<'PROFESSOR' | 'RESPONSAVEL'> = ['PROFESSOR', 'RESPONSAVEL'];

function isSchoolMobileToggle(tab?: string): tab is 'PROFESSOR' | 'RESPONSAVEL' {
  return tab === 'PROFESSOR' || tab === 'RESPONSAVEL';
}

/** @deprecated use isSchoolMobileToggle — mantido onde o intent era “aba só de celular” */
function isSchoolMobileTab(tab?: string): tab is 'PROFESSOR' | 'RESPONSAVEL' {
  return isSchoolMobileToggle(tab);
}

/** Ex.: "5º Ano D" → série "5º ANO", turma "D". */
function parseTurmaSerieTurma(nome: string): { serie: string; turma: string } {
  const n = String(nome || '').trim();
  const m = n.match(/^(.+?)\s+([A-Za-z0-9])$/);
  if (m) return { serie: m[1].trim().toUpperCase(), turma: m[2].toUpperCase() };
  return { serie: n.toUpperCase() || '—', turma: '—' };
}

function formatChamadaLogHeader(opts: {
  professorNome: string;
  turnoLabel: string;
  turmaNome: string;
}): string {
  const prof = String(opts.professorNome || '')
    .replace(/^\s*prof\.?\s*/i, '')
    .trim()
    .toUpperCase();
  const turno = String(opts.turnoLabel || '').trim().toUpperCase() || '—';
  const { serie, turma } = parseTurmaSerieTurma(opts.turmaNome);
  return `${prof || 'PROFESSOR'} -- ${turno} -- ${serie} -- ${turma}`;
}

/** Redirect por aba da janela desktop (inclui Prefeito / Master). */
const DESKTOP_TAB_REDIRECT: Record<DesktopTabId, string> = {
  PREFEITO: '/municipal/dashboard',
  DIRETOR: '/admin/dashboard',
  SECRETARIA: '/equipe/dashboard',
  COORDENADOR: '/equipe/dashboard',
  PROFESSOR: '/professor/dashboard',
  RESPONSAVEL: '/responsavel/dashboard',
  ALUNO: '/aluno/dashboard',
};

/** Resolve JWT da janela (subsessão por escola / actorId). */
function resolveWindowSession(win: {
  activeTab?: string;
  profile?: string;
  actorId?: string;
  schoolId?: string;
  roleActors?: Partial<Record<SchoolRoleTab, string>>;
}): { token: string; user: any; redirect: string } | null {
  try {
    const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
    const activeProfile = (win.activeTab || win.profile || 'DIRETOR') as string;
    const roleActorId =
      (win.roleActors && (win.roleActors as any)[activeProfile]) || win.actorId;

    let token = '';
    let user: any = null;

    if (roleActorId && sessions[roleActorId]?.access_token) {
      token = sessions[roleActorId].access_token;
      user = sessions[roleActorId].user;
    }
    if (!token && win.schoolId && activeProfile !== 'PREFEITO') {
      const scoped = sessions[`${win.schoolId}::${activeProfile}`];
      if (scoped?.access_token) {
        token = scoped.access_token;
        user = scoped.user;
      }
    }
    // Prefeito (gabinete municipal) — também na janela da escola (aba PREFEITO)
    if (!token && activeProfile === 'PREFEITO') {
      const municipal =
        sessions['MUNICIPAL'] ||
        sessions['MUNICIPIO'] ||
        Object.values(sessions).find((s: any) => {
          const p = (s?.user?.perfil || '').toUpperCase();
          return s?.access_token && (p === 'MUNICIPAL' || p === 'MUNICIPIO');
        });
      if ((municipal as any)?.access_token) {
        token = (municipal as any).access_token;
        user = (municipal as any).user;
      }
    }
    if (!token && !win.schoolId) {
      let sessionKey = activeProfile;
      if (activeProfile === 'DIRETOR') sessionKey = 'ESCOLA_DIRETOR';
      if (activeProfile === 'COORDENADOR') sessionKey = 'ESCOLA_COORDENACAO';
      if (activeProfile === 'SECRETARIA') sessionKey = 'ESCOLA_SECRETARIA';
      if (sessions[sessionKey]?.access_token) {
        token = sessions[sessionKey].access_token;
        user = sessions[sessionKey].user;
      }
    }
    if (!token || !user) return null;

    let redirect = '/admin/dashboard';
    if (activeProfile === 'PREFEITO') redirect = '/municipal/dashboard';
    else if (activeProfile in SCHOOL_ROLE_REDIRECT) {
      redirect = SCHOOL_ROLE_REDIRECT[activeProfile as SchoolRoleTab];
    }

    return {
      token,
      user,
      redirect,
    };
  } catch {
    return null;
  }
}

/** Mesmo padrão do admin impersonate: token na URL (confiável; sem corrida de postMessage). */
function buildSchoolIframeSrc(win: DesktopWindow): string {
  // Central admin (/master): usa JWT do admin logado no cockpit
  if (win.customUrl?.startsWith('/master') || (win.id === 'window-admin-central' && win.customUrl?.startsWith('/master'))) {
    try {
      const token = localStorage.getItem('ac_token') || '';
      const user = JSON.parse(localStorage.getItem('ac_user') || 'null');
      if (token && user) {
        const payload = {
          access_token: token,
          user,
          redirect_url: win.customUrl || '/master/dashboard',
          skip_splash: true,
          from_cockpit: true,
        };
        return `${window.location.origin}/login?impersonate=${encodeURIComponent(JSON.stringify(payload))}`;
      }
    } catch { /* fallback abaixo */ }
  }

  const resolved = resolveWindowSession(win);
  if (resolved) {
    const perfil = String(resolved.user?.perfil || win.activeTab || win.profile || '').toUpperCase();
    const cargo = String((resolved.user as { cargo?: string } | undefined)?.cargo || win.activeTab || '').toUpperCase();
    const wantPush =
      perfil === 'RESPONSAVEL' ||
      perfil === 'PROFESSOR' ||
      perfil === 'ESCOLA' ||
      perfil === 'MUNICIPAL' ||
      ['DIRETOR', 'SECRETARIA', 'COORDENADOR', 'COORDENACAO', 'PREFEITO'].includes(perfil) ||
      ['DIRETOR', 'SECRETARIA', 'COORDENADOR', 'COORDENACAO'].includes(cargo);
    const payload = {
      access_token: resolved.token,
      user: resolved.user,
      redirect_url: win.customUrl || resolved.redirect,
      skip_splash: true,
      from_cockpit: true,
      promptNotification: wantPush,
    };
    let url = `${window.location.origin}/login?impersonate=${encodeURIComponent(JSON.stringify(payload))}`;
    if (wantPush) url += '&promptNotification=1';
    return url;
  }
  return `${window.location.origin}/login?impersonate_receiver=true`;
}

/** Chave estável do painel (iframe) — permite preservar páginas entre tabs. */
function getTabPanelKey(win: Pick<DesktopWindow, 'id' | 'activeTab' | 'customUrl' | 'schoolId' | 'profile'>): string {
  if (win.customUrl?.startsWith('/master')) return 'ADMIN_MASTER';
  if (win.activeTab === 'PREFEITO') return 'PREFEITO';
  if (win.schoolId && win.activeTab) return `${win.schoolId}::${win.activeTab}`;
  if (win.activeTab) return win.activeTab;
  if (win.id === 'window-painel-executivo') return 'PREFEITO_SOLO';
  return win.profile || 'MAIN';
}

type TabPanelState = {
  src: string;
  actorId?: string;
  customUrl?: string;
  schoolId?: string;
};

/** Anexa/reusa iframe da aba sem remount (só cria src na 1ª visita). */
function withTabPanel(win: DesktopWindow, forceRemount = false): DesktopWindow {
  const key = getTabPanelKey(win);
  const panels: Record<string, TabPanelState> = { ...(win.tabPanels || {}) };
  const existing = panels[key];
  const needsSrc =
    forceRemount ||
    !existing?.src ||
    existing.src.includes('impersonate_receiver=true');

  if (needsSrc) {
    panels[key] = {
      src: buildSchoolIframeSrc(win),
      actorId: win.actorId,
      customUrl: win.customUrl,
      schoolId: win.schoolId,
    };
  }

  return { ...win, tabPanels: panels };
}

/** Nível hierárquico do papel ativo no cockpit (menor = mais alto). Folhas = 99 (sem toolbar/tabs de troca). */
function roleKeyToHierarchyLevel(roleKey: string): number {
  switch (roleKey.toLowerCase()) {
    case 'administrador': return 0;
    case 'prefeito': return 1;
    case 'diretor': return 2;
    case 'coordenador':
    case 'secretaria': return 3;
    case 'professor':
    case 'responsavel': return 4;
    default: return 99;
  }
}

/** Tabs da janela única: o papel vê a si + à direita. Folhas não trocam aba. */
function getVisibleDesktopTabs(activeRoleKey: string): typeof DESKTOP_WINDOW_TABS {
  const level = roleKeyToHierarchyLevel(activeRoleKey);
  if (level >= 99) return [];
  return DESKTOP_WINDOW_TABS.filter((t) => t.hierarchyLevel >= level);
}

export interface DesktopWindow {
  id: string;
  title: string;
  type: 'MOBILE' | 'DESKTOP' | 'FOLDER' | 'OFFICE';
  profile: 'PREFEITO' | 'DIRETOR' | 'SECRETARIA' | 'COORDENADOR' | 'CELULAR1' | 'CELULAR2' | 'CELULAR3' | 'CELULAR4' | 'PROFESSOR' | 'RESPONSAVEL' | 'ALUNO' | 'FOLDER' | 'OFFICE';
  x: number;
  y: number;
  width: number;
  height: number;
  isMinimized: boolean;
  isMaximized: boolean;
  zIndex: number;
  folderId?: string;
  officeFileId?: string;
  officeFileName?: string;
  officeFileUrl?: string;
  actorId?: string;
  activeTab?: DesktopTabId;
  customUrl?: string;
  /** Escola escolhida na janela (obrigatória para Prefeito ver tabs do domínio escolar). */
  schoolId?: string;
  /** Multi-ambiente da escola: JWT por cargo (flagado com municipioId em ac_sessions). */
  roleActors?: Partial<Record<SchoolRoleTab, string>>;
  /**
   * Exibição de celulares embutidos (toggles independentes).
   * Professor + Responsável podem ficar lado a lado (notificação → responsável).
   */
  showMobileProfessor?: boolean;
  showMobileResponsavel?: boolean;
  /** @deprecated migrado para showMobileProfessor / showMobileResponsavel */
  schoolMobileTab?: 'PROFESSOR' | 'RESPONSAVEL' | null;
  /** Painéis iframe preservados por aba (evita reload/animação ao trocar tab). */
  tabPanels?: Record<string, TabPanelState>;
  /** Força remount do iframe após reauth / troca de ambiente. */
  iframeNonce?: number;
}

interface DesktopItem {
  id: string;
  name: string;
  type: 'PROFILE' | 'FOLDER' | 'SHORTCUT';
  profileKey?: keyof typeof PROFILE_CONFIGS;
  folderId?: string;
  x: number;
  y: number;
  parentId?: string | null;
}

// Configurações de perfis com SVGs lineares premium e glows personalizados
const PROFILE_CONFIGS = {
  PREFEITO: {
    title: 'Prefeito (Painel)',
    type: 'DESKTOP',
    textColor: 'text-[#f9e2af]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(249,226,175,0.3)]',
    borderColor: 'group-hover:border-[#f9e2af]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M3 10h18M5 10v11M19 10v11M9 10v11M15 10v11M12 2v4M12 10V6M4 10l8-6 8 6" />
      </svg>
    )
  },
  DIRETOR: {
    title: 'Diretor (Painel)',
    type: 'DESKTOP',
    textColor: 'text-[#a6e3a1]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(166,227,161,0.3)]',
    borderColor: 'group-hover:border-[#a6e3a1]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
      </svg>
    )
  },
  SECRETARIA: {
    title: 'Secretaria (Painel)',
    type: 'DESKTOP',
    textColor: 'text-[#89b4fa]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(137,180,250,0.3)]',
    borderColor: 'group-hover:border-[#89b4fa]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    )
  },
  COORDENADOR: {
    title: 'Coordenador (Painel)',
    type: 'DESKTOP',
    textColor: 'text-[#b4befe]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(180,190,254,0.3)]',
    borderColor: 'group-hover:border-[#b4befe]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
      </svg>
    )
  },
  CELULAR1: {
    title: 'Celular 1',
    type: 'MOBILE',
    textColor: 'text-[#cba6f7]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(203,166,247,0.3)]',
    borderColor: 'group-hover:border-[#cba6f7]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    )
  },
  CELULAR2: {
    title: 'Celular 2',
    type: 'MOBILE',
    textColor: 'text-[#f38ba8]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(243,139,168,0.3)]',
    borderColor: 'group-hover:border-[#f38ba8]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    )
  },
  CELULAR3: {
    title: 'Celular 3',
    type: 'MOBILE',
    textColor: 'text-[#89b4fa]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(137,180,250,0.3)]',
    borderColor: 'group-hover:border-[#89b4fa]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    )
  },
  CELULAR4: {
    title: 'Celular 4',
    type: 'MOBILE',
    textColor: 'text-[#a6e3a1]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(166,227,161,0.3)]',
    borderColor: 'group-hover:border-[#a6e3a1]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    )
  },
  PROFESSOR: {
    title: 'Professor (App)',
    type: 'MOBILE',
    textColor: 'text-[#a6e3a1]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(166,227,161,0.3)]',
    borderColor: 'group-hover:border-[#a6e3a1]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    )
  },
  RESPONSAVEL: {
    title: 'Responsável (App)',
    type: 'MOBILE',
    textColor: 'text-[#f38ba8]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(243,139,168,0.3)]',
    borderColor: 'group-hover:border-[#f38ba8]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    )
  },
  ALUNO: {
    title: 'Aluno (App)',
    type: 'MOBILE',
    textColor: 'text-[#89b4fa]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(137,180,250,0.3)]',
    borderColor: 'group-hover:border-[#89b4fa]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    )
  },
  OFFICE: {
    title: 'Documentos',
    type: 'OFFICE',
    textColor: 'text-[#cba6f7]',
    shadowColor: 'group-hover:shadow-[0_0_22px_rgba(203,166,247,0.3)]',
    borderColor: 'group-hover:border-[#cba6f7]/40',
    icon: (
      <svg className="w-6 h-6 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    )
  }
} as const;

/** Perfis white-label (Master Customização) → cor de marca */
const PROFILE_BRAND_MAP: Partial<
  Record<keyof typeof PROFILE_CONFIGS, BrandProfileKey>
> = {
  PREFEITO: 'PREFEITO',
  DIRETOR: 'DIRETOR',
  SECRETARIA: 'SECRETARIA',
  COORDENADOR: 'COORDENADOR',
  PROFESSOR: 'PROFESSOR',
  RESPONSAVEL: 'RESPONSAVEL',
  ALUNO: 'ALUNO',
};

const PROFILE_LOCAL_HEX: Partial<Record<keyof typeof PROFILE_CONFIGS, string>> = {
  CELULAR1: '#cba6f7',
  CELULAR2: '#f38ba8',
  CELULAR3: '#89b4fa',
  CELULAR4: '#a6e3a1',
  OFFICE: '#cba6f7',
};

function desktopProfileHex(key: keyof typeof PROFILE_CONFIGS): string {
  const brand = PROFILE_BRAND_MAP[key];
  if (brand) return getBrandColor(brand);
  return PROFILE_LOCAL_HEX[key] || DEFAULT_BRAND_BY_PROFILE.DIRETOR;
}

interface DesktopWindowManagerProps {
  isBackendOnline?: boolean;
  stopSimulation?: () => void;
}

export const DesktopWindows: React.FC<DesktopWindowManagerProps> = ({
  isBackendOnline = true,
  stopSimulation,
}) => {
  const { selectedSchool, activeSessions, selectSchool } = useSimulationStore();
  const focusedSession = useSimSessionFocus((s) => s.focusedSession);
  const focusedSessionId = useSimSessionFocus((s) => s.focusedSessionId);
  const setFocusedSession = useSimSessionFocus((s) => s.setFocusedSession);
  const syncFromSessions = useSimSessionFocus((s) => s.syncFromSessions);
  const [sessionChoices, setSessionChoices] = useState<SimulationSession[]>([]);

  const [windows, setWindows] = useState<DesktopWindow[]>([]);
  const windowsRef = useRef<DesktopWindow[]>(windows);
  windowsRef.current = windows;
  const [activeWindowId, setActiveWindowId] = useState<string | null>(null);
  /** Taskbar auto-hide: revelada quando mouse chega perto do rodapé (janela maximizada ativa). */
  const [taskbarEdgeRevealed, setTaskbarEdgeRevealed] = useState(false);
  /** Menu sanduíche dos painéis PWA embutidos (PROF/RESP) na janela escola. */
  const [embedToolsMenu, setEmbedToolsMenu] = useState<Partial<Record<'PROFESSOR' | 'RESPONSAVEL', boolean>>>({});
  /** Re-render quando Master atualiza brandByProfile */
  const [brandTick, setBrandTick] = useState(0);
  useEffect(() => {
    const sync = () => setBrandTick((n) => n + 1);
    window.addEventListener('ac-theme-updated', sync);
    return () => window.removeEventListener('ac-theme-updated', sync);
  }, []);

  const cockpitTour = useCockpitTourOnce();

  /** Avisa só o iframe da aba ativa — evita tour do perfil em painéis ocultos. */
  useEffect(() => {
    const ping = () => {
      for (const win of windowsRef.current) {
        const activeKey = getTabPanelKey(win);
        const keyed = document.getElementById(
          `iframe-desktop-${win.id}-${activeKey}`,
        ) as HTMLIFrameElement | null;
        notifyEmbedPanelActive(keyed);
        const legacy = document.getElementById(
          `iframe-desktop-${win.id}`,
        ) as HTMLIFrameElement | null;
        if (legacy && legacy !== keyed) notifyEmbedPanelActive(legacy);
      }
    };
    ping();
    const t = window.setTimeout(ping, 350);
    return () => window.clearTimeout(t);
  }, [windows, activeWindowId]);

  // Estados da Simulação Acelerada (Time Dilation) e Robôs
  const [simulationSpeed, setSimulationSpeed] = useState<number>(240); // 240x por padrão
  /** Relógio/fila só andam após Play manual (não inicia sozinho). */
  const [isSimulationPlaying, setIsSimulationPlaying] = useState<boolean>(false);
  const [virtualTime, setVirtualTime] = useState<Date>(() => {
    const d = new Date();
    d.setHours(6, 40, 0, 0); // Começa às 06:40 virtual
    return d;
  });
  const [simulationLogs, setSimulationLogs] = useState<string[]>(['[06:40:00 V] Central de Telemetria inicializada.']);
  const [schoolMetadata, setSchoolMetadata] = useState<any>(null);
  /** Cache de metadata por escola (header de várias janelas). */
  const schoolMetadataCacheRef = useRef<Record<string, any>>({});
  /** Busy dos chips de simulação por janela. */
  const [simBusy, setSimBusy] = useState<Record<string, 'rfid' | 'chamada' | null>>({});
  /** Expansor de telemetria sob o title bar da janela escola. */
  const [simStripExpanded, setSimStripExpanded] = useState<Record<string, boolean>>({});
  /** Mix operacional RFID / sem RFID / ausentes (Ops do strip). */
  const [presenceMix, setPresenceMix] = useState<SimPresenceMix>(() => loadPresenceMix());
  const presenceMixRef = useRef(presenceMix);
  /** Alunos que já passaram na portaria (RFID) no dia sim — por escola. */
  const rfidEnteredBySchoolRef = useRef<Record<string, Set<string>>>({});
  /**
   * Fila do cockpit: contador de eventos em voo.
   * Permite paralelismo limitado no mesmo `at` (várias turmas/chamadas).
   */
  const simQueueInFlightRef = useRef(0);
  /** Ids já despachados (evita re-disparo antes do React refletir `running`). */
  const simQueueScheduledIdsRef = useRef(new Set<string>());
  /** Max POSTs concorrentes no mesmo horário da fila (pool DB staging é apertado). */
  const SIM_QUEUE_PARALLEL = 2;
  /** Status do leitor RFID por schoolId (para o strip). */
  const [readerOnlineBySchool, setReaderOnlineBySchool] = useState<Record<string, boolean>>({});
  /** Fila E2E + turno (modo COCKPIT). */
  const [activeShift, setActiveShift] = useState<ShiftKey>('MANHA');
  const defaultDay = defaultSimSchoolDay();
  const [simDateYmd, setSimDateYmd] = useState(defaultDay.ymd);
  const [simDiaSemana, setSimDiaSemana] = useState<DiaSemanaKey>(defaultDay.diaSemana);
  const [dayPlan, setDayPlan] = useState<ShiftDayPlan | null>(null);
  const [eventQueue, setEventQueue] = useState<SimEvent[]>(() => buildQueueForShift('MANHA'));
  /** Fila em Auto por padrão (só dispara quando o relógio está em Play — não inicia sozinho). */
  const [queueControl, setQueueControl] = useState<QueueControl>('auto');
  /** Loop contínuo: fim da Noite → próximo dia letivo (pula Sáb/Dom) e recomeça turnos. */
  const [dayLoopEnabled, setDayLoopEnabled] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('ac_sim_day_loop');
      if (v === '0') return false;
      if (v === '1') return true;
    } catch { /* noop */ }
    return true;
  });
  const dayLoopEnabledRef = useRef(dayLoopEnabled);
  const [simEngineMode, setSimEngineMode] = useState<SimEngineMode>('COCKPIT');
  const [simFocusSchoolId, setSimFocusSchoolId] = useState<string | null>(null);
  const [turnosBySchool, setTurnosBySchool] = useState<Record<string, SchoolTurnoOpt[]>>({});
  const [chronosBusy, setChronosBusy] = useState(false);
  const [chronosSession, setChronosSession] = useState<{
    schoolId: string;
    turnoId: string;
    clock?: string;
    fase?: string;
    paused?: boolean;
  } | null>(null);
  const eventQueueRef = useRef(eventQueue);
  const queueControlRef = useRef(queueControl);
  const simEngineModeRef = useRef(simEngineMode);
  const activeShiftRef = useRef(activeShift);
  const simFocusSchoolIdRef = useRef(simFocusSchoolId);
  const simDateYmdRef = useRef(simDateYmd);
  const simDiaSemanaRef = useRef(simDiaSemana);
  const dayPlanRef = useRef(dayPlan);
  const turnosBySchoolRef = useRef(turnosBySchool);
  /** Evita disparar avanço de turno duas vezes no mesmo fechamento. */
  const shiftTransitionRef = useRef(false);
  useEffect(() => { eventQueueRef.current = eventQueue; }, [eventQueue]);
  useEffect(() => { queueControlRef.current = queueControl; }, [queueControl]);
  useEffect(() => { simEngineModeRef.current = simEngineMode; }, [simEngineMode]);
  useEffect(() => { activeShiftRef.current = activeShift; }, [activeShift]);
  useEffect(() => { simFocusSchoolIdRef.current = simFocusSchoolId; }, [simFocusSchoolId]);
  useEffect(() => { simDateYmdRef.current = simDateYmd; }, [simDateYmd]);
  useEffect(() => { simDiaSemanaRef.current = simDiaSemana; }, [simDiaSemana]);
  useEffect(() => { dayPlanRef.current = dayPlan; }, [dayPlan]);
  useEffect(() => { turnosBySchoolRef.current = turnosBySchool; }, [turnosBySchool]);
  useEffect(() => { presenceMixRef.current = presenceMix; }, [presenceMix]);
  useEffect(() => { dayLoopEnabledRef.current = dayLoopEnabled; }, [dayLoopEnabled]);
  const [sessionSchools, setSessionSchools] = useState<Array<{
    id: string;
    nome: string;
    alunos?: number;
    responsaveis?: number;
    equipe?: number;
  }>>([]);
  const [isTelemetryOpen, setIsTelemetryOpen] = useState<boolean>(true);
  const [pairingQr, setPairingQr] = useState<{
    actorId: string;
    actorNome: string;
    sessionId: string;
    roleLabel: string;
  } | null>(null);

  // Empurra relógio virtual para a sessão (data/speed/play) — sem flood a cada tick
  useEffect(() => {
    const sessionId = focusedSessionId;
    if (!sessionId) return;
    const t = window.setTimeout(() => {
      void simulationApi
        .patchSessionClock(sessionId, {
          simDateYmd,
          virtualEpochMs: virtualTime.getTime(),
          speed: simulationSpeed,
          playing: isSimulationPlaying,
        })
        .catch(() => {
          /* sessão pode ainda não existir / rede */
        });
    }, 400);
    return () => window.clearTimeout(t);
    // virtualTime omitido de propósito — sync periódico abaixo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedSessionId, simDateYmd, simulationSpeed, isSimulationPlaying]);

  const virtualTimeRef = useRef(virtualTime);
  useEffect(() => {
    virtualTimeRef.current = virtualTime;
  }, [virtualTime]);

  /** Último minuto virtual já enviado à sessão (evita flood; a 240x 8s ≈ 32 min de atraso). */
  const lastPushedVirtualMinsRef = useRef<number | null>(null);

  useEffect(() => {
    const sessionId = focusedSessionId;
    if (!sessionId || !isSimulationPlaying) return;
    // A 60x+ o clock da sessão precisa acompanhar de perto (janela de chamada).
    const intervalMs = simulationSpeed >= 60 ? 1_200 : 8_000;
    const t = window.setInterval(() => {
      void simulationApi
        .patchSessionClock(sessionId, {
          virtualEpochMs: virtualTimeRef.current.getTime(),
          playing: true,
          speed: simulationSpeed,
          simDateYmd,
        })
        .catch(() => {});
      lastPushedVirtualMinsRef.current = dateToMinutes(virtualTimeRef.current);
    }, intervalMs);
    return () => window.clearInterval(t);
  }, [focusedSessionId, isSimulationPlaying, simulationSpeed, simDateYmd]);

  const addLog = (msg: string) => {
    setSimulationLogs((prev) => {
      const next = [...prev, msg];
      // Buffer menor: menos DOM + menos re-renders no strip (resumo já é denso).
      if (next.length > 40) next.splice(0, next.length - 40);
      return next;
    });
  };

  /**
   * Empurra clock da sessão + avisa iframes (dashboards/páginas) para refletir o dia simulado na hora.
   * Awaitável: chamadas da fila precisam do virtualEpochMs atualizado antes do POST.
   */
  const pushSimClockToSessionAndEmbeds = async (opts?: {
    simDateYmd?: string;
    virtualEpochMs?: number;
    playing?: boolean;
  }): Promise<void> => {
    const ymd = opts?.simDateYmd ?? simDateYmdRef.current;
    const epoch = opts?.virtualEpochMs ?? virtualTimeRef.current.getTime();
    const playing = opts?.playing ?? isSimulationPlaying;
    const sessionId = useSimSessionFocus.getState().focusedSessionId;
    if (sessionId) {
      try {
        await simulationApi.patchSessionClock(sessionId, {
          simDateYmd: ymd,
          virtualEpochMs: epoch,
          speed: simulationSpeed,
          playing,
        });
        lastPushedVirtualMinsRef.current = dateToMinutes(new Date(epoch));
      } catch {
        /* sessão pode ainda não existir / rede */
      }
    }
    const payload = {
      type: 'ac_sim_clock' as const,
      simDateYmd: ymd,
      virtualEpochMs: epoch,
      speed: simulationSpeed,
      playing,
    };
    try {
      document.querySelectorAll('iframe').forEach((node) => {
        const iframe = node as HTMLIFrameElement;
        iframe.contentWindow?.postMessage(payload, window.location.origin);
      });
    } catch { /* noop */ }
  };

  const loadSchoolMetadataFor = async (schoolId: string): Promise<any | null> => {
    if (schoolMetadataCacheRef.current[schoolId]) {
      const cached = schoolMetadataCacheRef.current[schoolId];
      setReaderOnlineBySchool((prev) =>
        prev[schoolId] === !!cached?.dispositivoLeituraId
          ? prev
          : { ...prev, [schoolId]: !!cached?.dispositivoLeituraId },
      );
      return cached;
    }
    if (selectedSchool?.id === schoolId && schoolMetadata?.alunos) {
      schoolMetadataCacheRef.current[schoolId] = schoolMetadata;
      setReaderOnlineBySchool((prev) => ({
        ...prev,
        [schoolId]: !!schoolMetadata?.dispositivoLeituraId,
      }));
      return schoolMetadata;
    }
    try {
      const metadata = await simulationApi.getSchoolInfo(schoolId);
      schoolMetadataCacheRef.current[schoolId] = metadata;
      setReaderOnlineBySchool((prev) => ({
        ...prev,
        [schoolId]: !!metadata?.dispositivoLeituraId,
      }));
      if (selectedSchool?.id === schoolId) setSchoolMetadata(metadata);
      return metadata;
    } catch (err: any) {
      addLog(`[SYS] Erro ao carregar dados da escola: ${err?.message || err}`);
      return null;
    }
  };
  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      api.post('/dev/client-error', {
        type: 'ERROR_EVENT',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.stack || event.error?.message || String(event.error)
      }).catch(() => {});
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      api.post('/dev/client-error', {
        type: 'UNHANDLED_REJECTION',
        reason: event.reason?.stack || event.reason?.message || String(event.reason)
      }).catch(() => {});
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Reidrata JWTs dos atores (wizard não chama startSimulation do store)
  useEffect(() => {
    hydrateActiveSessionsFromStorage();
  }, []);

  // Lista de sessões ativas → foco compartilhado com o painel Admin
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token');
        const r = await fetch('/api/v1/simulation/sessions/active', {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}`, Accept: 'application/json' } : { Accept: 'application/json' },
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        const list = Array.isArray(data) ? data : [];
        setSessionChoices(list);
        syncFromSessions(list);
      } catch { /* noop */ }
    };
    void load();
    const t = setInterval(load, 12000);
    return () => { cancelled = true; clearInterval(t); };
  }, [syncFromSessions]);

  // Relógio E2E só corre com sessão RUNNING em foco
  useEffect(() => {
    if (!focusedSession) return;
    if (focusedSession.status !== 'RUNNING' && isSimulationPlaying) {
      setIsSimulationPlaying(false);
      addLog(`[SYS] Sessão ${focusedSession.municipioNome} está ${focusedSession.status} — relógio pausado.`);
    }
  }, [focusedSession?.id, focusedSession?.status]);

  // Carrega metadados da escola para alimentar a simulação dos robôs
  useEffect(() => {
    if (selectedSchool) {
      hydrateActiveSessionsFromStorage();
      simulationApi.getSchoolInfo(selectedSchool.id)
        .then(metadata => {
          schoolMetadataCacheRef.current[selectedSchool.id] = metadata;
          setSchoolMetadata(metadata);
          setReaderOnlineBySchool((prev) => ({
            ...prev,
            [selectedSchool.id]: !!metadata?.dispositivoLeituraId,
          }));
          addLog(`[06:40:00 V] Dados da escola carregados. ${metadata.alunos.length} alunos com tags RFID prontos.`);
        })
        .catch(err => {
          console.error(err);
          addLog(`[06:40:00 V] Erro ao carregar dados da escola: ${err.message || err}`);
        });
    }
  }, [selectedSchool]);

  // Escolas da sessão ativa (para o Prefeito escolher unidade nas abas escolares)
  useEffect(() => {
    let cancelled = false;

    const enrichAndSet = async (list: Array<{ id: string; nome: string; alunos?: number }>) => {
      if (cancelled || list.length === 0) return;
      let enriched = list.map((s) => ({
        id: s.id,
        nome: s.nome,
        alunos: s.alunos,
        responsaveis: undefined as number | undefined,
        equipe: undefined as number | undefined,
      }));

      try {
        const overview = await simulationApi.getOverviewPanel(list.map((s) => s.id));
        const byId = new Map(
          (overview?.escolas || []).map((e: any) => [
            e.id,
            {
              alunos: e.alunos ?? e.alunosCount ?? e._count?.alunos,
              responsaveis: e.responsaveis,
              equipe: e.equipe,
            },
          ]),
        );
        enriched = enriched.map((s) => {
          const extra = byId.get(s.id) as
            | { alunos?: number; responsaveis?: number; equipe?: number }
            | undefined;
          return {
            ...s,
            alunos: extra?.alunos ?? s.alunos,
            responsaveis: extra?.responsaveis,
            equipe: extra?.equipe,
          };
        });
      } catch {
        // mantém lista básica
      }

      if (!cancelled) {
        setSessionSchools(enriched);
        setSchoolMetadata((prev: any) => ({ ...(prev || {}), citySchools: enriched }));
      }
    };

    (async () => {
      try {
        // 1) Cidade do wizard + escolas do município
        const raw = SS.get('sim_cidade');
        if (raw) {
          const cidade = JSON.parse(raw) as Cidade;
          if (cidade?.id) {
            const escolas = await buscarEscolasMunicipio(cidade.id);
            await enrichAndSet(escolas.map((e) => ({ id: e.id, nome: e.nome, alunos: e.alunos })));
            if (!selectedSchool && escolas[0]) {
              void selectSchool({
                id: escolas[0].id,
                nome: escolas[0].nome,
                cidade: cidade.nome,
                uf: cidade.uf,
                totalAlunos: escolas[0].alunos || 0,
                totalProfessores: 0,
                isDemo: !!escolas[0].isDemo,
              } as any);
            }
            return;
          }
        }
      } catch {
        // segue para fallback
      }

      try {
        // 2) Sessão ativa no backend
        const token = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token');
        const r = await fetch('/api/v1/simulation/sessions/active', {
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!r.ok) return;
        const data = await r.json();
        const sessions = Array.isArray(data) ? data : (data?.sessions || data || []);
        const active = sessions.find((s: any) => s.status === 'RUNNING' || s.status === 'ACTIVE') || sessions[0];
        const ids: string[] = active?.escolaIds || active?.escola_ids || [];
        if (!ids.length) return;
        const overview = await simulationApi.getOverviewPanel(ids).catch(() => null);
        const fromOverview = (overview?.escolas || overview?.schools || []).map((e: any) => ({
          id: e.id,
          nome: e.nome || e.name || e.id,
          alunos: e.alunos ?? e.alunosCount ?? e._count?.alunos,
          responsaveis: e.responsaveis,
          equipe: e.equipe,
        }));
        if (fromOverview.length) {
          if (!cancelled) {
            setSessionSchools(fromOverview);
            setSchoolMetadata((prev: any) => ({ ...(prev || {}), citySchools: fromOverview }));
          }
        } else {
          await enrichAndSet(ids.map((id) => ({ id, nome: `Escola ${id.slice(0, 8)}` })));
        }
      } catch {
        // noop
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só no mount / troca de município
  }, []);

  // Robô Aluno: RFID entrada/saída — mix configurável (% do turno)
  const triggerRfidEntrance = async (
    schoolId?: string,
    schoolNome?: string,
    opts?: { tipoEvento?: 'ENTRADA' | 'SAIDA'; atHm?: string },
  ) => {
    const schoolIdForToken = schoolId || selectedSchool?.id;
    const nomeEscola = schoolNome || selectedSchool?.nome;
    const tipoEvento = opts?.tipoEvento || 'ENTRADA';
    const labelMov = tipoEvento === 'SAIDA' ? 'saída' : 'entrada';
    if (!schoolIdForToken) {
      addLog(`[RFID] Erro: nenhuma escola selecionada.`);
      return;
    }

    const meta = await loadSchoolMetadataFor(schoolIdForToken);
    if (!meta?.dispositivoLeituraId) {
      addLog(`[RFID] Erro: Nenhum leitor RFID ativo encontrado na escola.`);
      return;
    }

    const mix = normalizePresenceMix(presenceMixRef.current);
    // Cache antigo podia ter só 15 alunos — invalida se o plano do turno tem mais tags.
    const rosterTagCount = dayPlanRef.current?.roster?.alunosComTag ?? 0;
    if (
      meta?.alunos &&
      rosterTagCount > 0 &&
      meta.alunos.length < Math.min(rosterTagCount, 50)
    ) {
      delete schoolMetadataCacheRef.current[schoolIdForToken];
    }
    const metaFresh =
      schoolMetadataCacheRef.current[schoolIdForToken] ||
      (await loadSchoolMetadataFor(schoolIdForToken)) ||
      meta;
    const rosterTags = dayPlanRef.current?.roster?.alunosComTagIds || [];
    const fromRoster = rosterTags
      .filter((a) => a.tagId && a.tagCodigo)
      .map((a) => ({ id: a.id, nome: a.nome, tagId: a.tagId, tagCodigo: a.tagCodigo }));
    const fromMeta = (metaFresh?.alunos || [])
      .filter((a: any) => a.tagId && a.tagCodigo)
      .map((a: any) => ({ id: a.id, nome: a.nome, tagId: a.tagId, tagCodigo: a.tagCodigo }));
    // Preferir roster do turno; meta completa só como fallback (antes truncava a 40/15).
    const byId = new Map<string, { id: string; nome: string; tagId: string; tagCodigo: string }>();
    const primary = fromRoster.length ? fromRoster : fromMeta;
    for (const a of primary) {
      if (a.id && a.tagId && a.tagCodigo) byId.set(a.id, a);
    }
    // Se o roster vier truncado, completa com meta.
    const rosterCount = dayPlanRef.current?.roster?.alunosComTag ?? 0;
    if (fromRoster.length > 0 && fromRoster.length < rosterCount && fromMeta.length) {
      for (const a of fromMeta) {
        if (a.id && a.tagId && a.tagCodigo) byId.set(a.id, a);
      }
    }
    const pool = [...byId.values()];
    const totalTagged = Math.max(rosterCount, pool.length);
    const totalTurno = dayPlanRef.current?.roster?.alunos ?? totalTagged;
    const nRfid = Math.min(pool.length, countByPct(Number(totalTurno) || pool.length, mix.pctRfid));

    let candidates: typeof pool;
    if (tipoEvento === 'SAIDA') {
      // Só quem já entrou no dia sim — evita presença INCONSISTENTE.
      const entered = rfidEnteredBySchoolRef.current[schoolIdForToken] || new Set<string>();
      const enteredPool = pool.filter((a) => entered.has(a.id));
      if (!enteredPool.length) {
        addLog(
          `[RFID] Saída abortada: nenhum aluno com entrada RFID no dia ${simDateYmdRef.current}. Rode a Entrada RFID antes.`,
        );
        return;
      }
      candidates = shuffleCopy(enteredPool).slice(0, Math.min(enteredPool.length, nRfid || enteredPool.length));
    } else {
      candidates = shuffleCopy(pool).slice(0, nRfid);
    }

    if (!candidates.length) {
      addLog(`[RFID] Erro: sem alunos com tag para ${nomeEscola || schoolIdForToken}.`);
      return;
    }

    if (tipoEvento === 'ENTRADA' && pool.length < countByPct(Number(totalTurno) || pool.length, mix.pctRfid)) {
      addLog(
        `[RFID] Aviso: pool com tag (${pool.length}) < alvo ${mix.pctRfid}% de ${totalTurno}. Recarregue o plano (Preparar) se o backend ainda truncar.`,
      );
    }

    hydrateActiveSessionsFromStorage();

    const ready = await ensureChronosSchoolReady(schoolIdForToken, nomeEscola);
    const sessionToken =
      ready.directorToken ||
      resolveSimBearerToken('DIRETOR', schoolIdForToken) ||
      resolveSimBearerToken('ESCOLA', schoolIdForToken);

    if (!sessionToken) {
      addLog(
        `[RFID] Erro: ${ready.error || 'sem JWT da escola (Chronos não precisa de janela aberta — falhou simulation/start).'}`,
      );
      return;
    }

    // Pré-aquece CSRF (staging/prod) — os POSTs usam apiFetch + Bearer e cookie de sessão admin.
    await ensureCsrfToken();

    // Curva real no banco: ENTRADA (pontuais + atrasados) / SAÍDA fim do turno.
    const shift = activeShiftRef.current;
    const bounds = dayPlanRef.current?.boundaries;
    const batch = buildRfidEventIsoList({
      simDateYmd: simDateYmdRef.current,
      shift,
      tipoEvento,
      count: candidates.length,
      atHm: opts?.atHm,
      virtualTime: virtualTimeRef.current,
      aberturaHm: bounds?.abertura,
      entradaHm: bounds?.entrada,
      saidaHm: bounds?.saida,
    });
    const { isoList } = batch;
    const turnoLabel = SHIFT_WINDOWS[shift].label.toUpperCase();
    addLog(
      `[RFID] ${labelMov} · ${turnoLabel} · ${candidates.length} alunos · ${batch.firstHm}→${batch.lastHm}` +
        (tipoEvento === 'ENTRADA'
          ? ` · ${batch.pontuais} pont. / ${batch.atrasados} atras.`
          : '') +
        ` · dia ${simDateYmdRef.current}` +
        (nomeEscola ? ` · ${nomeEscola}` : ''),
    );

    // Pré-marca o mix RFID antes dos POSTs — a chamada não pode rodar “no meio” com set parcial.
    if (tipoEvento === 'ENTRADA') {
      const planned = rfidEnteredBySchoolRef.current[schoolIdForToken] || new Set<string>();
      for (const a of candidates) planned.add(a.id);
      rfidEnteredBySchoolRef.current[schoolIdForToken] = planned;
    }

    const RFID_LOG_EVERY_N = 20;
    const RFID_LOG_EVERY_MS = 10_000;
    let okCount = 0;
    let dupCount = 0;
    let failCount = 0;
    let processed = 0;
    let lastProgressAt = Date.now();
    let lastProgressN = 0;

    const maybeProgressLog = () => {
      const now = Date.now();
      const byCount = processed - lastProgressN >= RFID_LOG_EVERY_N;
      const byTime = now - lastProgressAt >= RFID_LOG_EVERY_MS;
      if (!byCount && !byTime) return;
      if (processed <= lastProgressN) return;
      addLog(
        `[RFID] … ${processed}/${candidates.length} · ${okCount} ok` +
          (dupCount ? ` · ${dupCount} dup` : '') +
          (failCount ? ` · ${failCount} falha` : ''),
      );
      lastProgressAt = now;
      lastProgressN = processed;
    };

    for (let i = 0; i < candidates.length; i++) {
      const aluno = candidates[i];
      try {
        const payload = {
          alunoId: aluno.id,
          tagRfidId: aluno.tagId,
          dispositivoLeituraId: meta.dispositivoLeituraId,
          tipoEvento,
          origemEvento: 'LEITOR',
          codigoLido: aluno.tagCodigo,
          dataHoraEvento: isoList[i] || isoList[0],
        };

        const res = await apiFetch(`/api/v1/escolas/${schoolIdForToken}/eventos-acesso`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
          },
          body: JSON.stringify(payload),
        });

        if (res.ok) {
          okCount++;
          if (tipoEvento === 'ENTRADA') {
            const set = rfidEnteredBySchoolRef.current[schoolIdForToken] || new Set<string>();
            set.add(aluno.id);
            rfidEnteredBySchoolRef.current[schoolIdForToken] = set;
          } else {
            rfidEnteredBySchoolRef.current[schoolIdForToken]?.delete(aluno.id);
          }
        } else {
          const errData = await res.json().catch(() => ({}));
          const errMsg = formatApiError(errData, res.statusText);
          // Já tem presença/evento no dia (re-run ou corrida) — conta como OK na sim.
          const isDup =
            /combinacao|aluno_id|data_referencia|duplicad|unique|já existe|ja existe/i.test(
              errMsg,
            );
          if (isDup && tipoEvento === 'ENTRADA') {
            okCount++;
            dupCount++;
            const set = rfidEnteredBySchoolRef.current[schoolIdForToken] || new Set<string>();
            set.add(aluno.id);
            rfidEnteredBySchoolRef.current[schoolIdForToken] = set;
          } else {
            failCount++;
            // Erros: só amostra (evita flood) + contador no progresso/resumo.
            if (failCount <= 3) {
              addLog(`[RFID] Falha para ${aluno.nome}: ${errMsg}`);
            }
          }
        }
      } catch (err: any) {
        failCount++;
        if (failCount <= 3) {
          addLog(`[RFID] Falha de rede para ${aluno.nome}: ${err.message}`);
        }
      }
      processed++;
      maybeProgressLog();
    }

    const tracked =
      tipoEvento === 'ENTRADA'
        ? (rfidEnteredBySchoolRef.current[schoolIdForToken]?.size ?? okCount)
        : okCount;
    addLog(
      `[RFID] OK · ${labelMov} · ${okCount}/${candidates.length}` +
        (dupCount ? ` · ${dupCount} já no dia` : '') +
        (failCount > 3 ? ` · ${failCount} falhas` : failCount ? ` · ${failCount} falha(s)` : '') +
        (tipoEvento === 'ENTRADA' ? ` · portaria ${tracked} (mix ${mix.pctRfid}%)` : ''),
    );
  };

  // Robô Professor: 1 chamada por turma/professor do plano (evHint da fila)
  const triggerClassAttendance = async (
    schoolId?: string,
    schoolNome?: string,
    evHint?: Pick<
      SimEvent,
      | 'id'
      | 'turmaId'
      | 'turmaNome'
      | 'professorId'
      | 'professorNome'
      | 'materiaId'
      | 'horarioAulaId'
      | 'label'
      | 'numeroAula'
    >,
  ) => {
    const schoolIdForCall = schoolId || selectedSchool?.id;
    const nomeEscola = schoolNome || selectedSchool?.nome;
    if (!schoolIdForCall) {
      addLog(`[Chamada] Erro: nenhuma escola selecionada.`);
      return;
    }

    const meta = await loadSchoolMetadataFor(schoolIdForCall);

    hydrateActiveSessionsFromStorage();

    let professorId = evHint?.professorId;
    let classToCall: { id: string; nome: string } | null = evHint?.turmaId
      ? { id: evHint.turmaId, nome: evHint.turmaNome || evHint.label || 'Turma' }
      : null;
    let materiaId: string | undefined = evHint?.materiaId || undefined;

    // Sem professor no plano → 1ª sessão PROF da escola (atalho manual)
    if (!professorId) {
      const sessionsMap = {
        ...(JSON.parse(localStorage.getItem('ac_sessions') || '{}') || {}),
        ...(activeSessions || {}),
      };
      const fallback = Object.values(sessionsMap).find(
        (s: any) =>
          String(s?.user?.perfil || '').toUpperCase() === 'PROFESSOR' &&
          s?.access_token &&
          (!s?.user?.escolaId || s.user.escolaId === schoolIdForCall || s.schoolId === schoolIdForCall),
      ) as any;
      professorId = fallback?.user?.id;
    }

    if (!professorId) {
      addLog(`[Chamada] Erro: sem professorId no plano nem sessão PROF.`);
      return;
    }

    const fresh = await ensureProfessorTokenForSchool(
      schoolIdForCall,
      professorId,
      nomeEscola,
    );
    if (!fresh?.token) {
      addLog(
        `[Chamada] Erro: falha ao obter JWT do professor ${professorId} (sem precisar abrir janela — simulation/start não gerou sessão; confira se o ator existe na escola).`,
      );
      return;
    }
    const profToken = fresh.token;
    const profNome = evHint?.professorNome || fresh.user?.nome || professorId;
    await ensureCsrfToken();

    if (!classToCall) {
      try {
        const opts = await simulationApi.getCockpitMobileOptions(schoolIdForCall, professorId);
        if (opts.turmas?.[0]) {
          classToCall = { id: opts.turmas[0].id, nome: opts.turmas[0].nome };
        }
      } catch {
        /* fallback */
      }
    }
    if (!classToCall && meta?.turmas?.[0]) {
      classToCall = meta.turmas[0];
    }
    if (!classToCall) {
      addLog(`[Chamada] Erro: sem turma para ${profNome} em ${nomeEscola || schoolIdForCall}.`);
      return;
    }

    if (!materiaId) {
      const materiaDaEscola = (meta?.materias || []).find(
        (m: any) => m?.id && (!m.escolaId || m.escolaId === schoolIdForCall),
      );
      if (materiaDaEscola?.id) materiaId = materiaDaEscola.id;
    }

    const numeroAula = Math.max(
      1,
      Number(evHint?.numeroAula) ||
        Number(String(evHint?.id || '').match(/^chamada-(\d+)/)?.[1]) ||
        1,
    );

    const turnoLabel = SHIFT_WINDOWS[activeShiftRef.current].label;
    const chamadaHeader = formatChamadaLogHeader({
      professorNome: String(profNome),
      turnoLabel,
      turmaNome: classToCall.nome,
    });

    try {
      // Sync clock da sessão imediatamente (janela 5 min antes usa virtualEpochMs, não a UI).
      await pushSimClockToSessionAndEmbeds({
        virtualEpochMs: virtualTimeRef.current.getTime(),
        playing: true,
      });

      // foraDoHorario só após o fim da aula — na 1ª aula às 07:00 V isso gerava 400.
      // Atraso/recuperação: backend já libera em JWT isSimulation após a janela.
      const body: Record<string, unknown> = {
        turmaId: classToCall.id,
        professorId,
        foraDoHorario: false,
        dataReferencia: simDateYmdRef.current,
        numeroAula,
      };
      if (materiaId) body.materiaId = materiaId;
      if (evHint?.horarioAulaId) body.horarioAulaId = evHint.horarioAulaId;

      let chamadaId: string | null = null;
      /** Alunos com justificativa ACEITA já seedados como JUSTIFICADO na abertura. */
      const justificadosIds = new Set<string>();
      const coletarJustificados = (chamada: any) => {
        for (const ca of chamada?.alunos || []) {
          if (ca?.statusPresenca !== 'JUSTIFICADO') continue;
          const id = ca.alunoId || ca.aluno?.id;
          if (id) justificadosIds.add(String(id));
        }
      };

      let resCreate: Response | null = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        resCreate = await apiFetch(`/api/v1/escolas/${schoolIdForCall}/chamadas`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${profToken}`,
          },
          body: JSON.stringify(body),
        });
        if (resCreate.ok || resCreate.status !== 500) break;
        const peek = await resCreate.clone().json().catch(() => ({}));
        const peekMsg = formatApiError(peek, '');
        const isP2028 =
          peek?.error?.code === 'P2028' ||
          /Unable to start a transaction|P2028/i.test(peekMsg);
        if (!isP2028 || attempt === 3) break;
        addLog(`[Chamada] Pool ocupado (P2028) — retry ${attempt}/3…`);
        await new Promise((r) => setTimeout(r, 400 * attempt));
      }
      if (!resCreate) {
        addLog(`[Chamada] Erro ao abrir: sem resposta`);
        return;
      }

      if (resCreate.ok) {
        const chamada = await resCreate.json();
        chamadaId = chamada?.id || null;
        coletarJustificados(chamada);
      } else {
        const err = await resCreate.json().catch(() => ({}));
        const msg = formatApiError(err, resCreate.statusText);
        // Reusa chamada ABERTA do mesmo contexto (reexecução da fila / limpeza parcial)
        if (/existe uma chamada aberta|ja realizada/i.test(msg)) {
          try {
            const qs = new URLSearchParams({
              turmaId: classToCall.id,
              professorId,
              dataReferencia: simDateYmdRef.current,
              limite: '20',
            });
            const resList = await fetch(
              `/api/v1/escolas/${schoolIdForCall}/chamadas?${qs}`,
              { headers: { Authorization: `Bearer ${profToken}` } },
            );
            if (resList.ok) {
              const data = await resList.json();
              const items = Array.isArray(data) ? data : data?.items || data?.data || [];
              const match = items.find(
                (c: any) =>
                  c?.turmaId === classToCall!.id &&
                  c?.professorId === professorId &&
                  Number(c?.numeroAula) === numeroAula &&
                  (c?.status === 'ABERTA' || c?.status === 'CONCLUIDA'),
              );
              if (match?.id && match.status === 'ABERTA') {
                const reusedId = String(match.id);
                chamadaId = reusedId;
                try {
                  const resOne = await fetch(
                    `/api/v1/escolas/${schoolIdForCall}/chamadas/${reusedId}`,
                    { headers: { Authorization: `Bearer ${profToken}` } },
                  );
                  if (resOne.ok) coletarJustificados(await resOne.json());
                } catch {
                  /* ignore */
                }
              } else if (match?.status === 'CONCLUIDA') {
                addLog(
                  `[Chamada] ${chamadaHeader} · aula ${numeroAula} · já concluída`,
                );
                return;
              }
            }
          } catch {
            /* ignore */
          }
        }
        if (!chamadaId) {
          addLog(`[Chamada] Erro ao abrir: ${msg} (${resCreate.status})`);
          return;
        }
      }

      // Só alunos da turma da chamada (nunca amostra genérica da escola)
      let alunosTurma: Array<{ id: string }> = [];
      try {
        const resTurma = await fetch(
          `/api/v1/escolas/${schoolIdForCall}/chamadas/turma/${classToCall.id}/alunos`,
          { headers: { Authorization: `Bearer ${profToken}` } },
        );
        if (resTurma.ok) {
          const data = await resTurma.json();
          alunosTurma = (data?.alunos || data || [])
            .map((a: any) => ({ id: a.id || a.alunoId }))
            .filter((a: any) => a.id);
        }
      } catch {
        /* ignore */
      }
      if (!alunosTurma.length && meta?.alunos) {
        alunosTurma = (meta.alunos as any[])
          .filter((a) => a?.turmaId === classToCall!.id && a?.id)
          .map((a) => ({ id: a.id }));
      }

      const mix = normalizePresenceMix(presenceMixRef.current);
      const entered = rfidEnteredBySchoolRef.current[schoolIdForCall] || new Set<string>();
      const shuffled = shuffleCopy(alunosTurma);
      const n = shuffled.length;
      const nSemTarget = countByPct(n, mix.pctSemRfid);
      const nAusTarget = countByPct(n, mix.pctAusente);

      type StatusSim = 'PRESENTE' | 'AUSENTE' | 'JUSTIFICADO';
      const row = (alunoId: string, status: StatusSim) => ({
        alunoId,
        statusPresenca: justificadosIds.has(alunoId) ? ('JUSTIFICADO' as const) : status,
        justificativa: justificadosIds.has(alunoId) ? 'Abono: justificativa aceita (sim)' : '',
        observacoes: '',
      });

      let studentPayload: Array<{
        alunoId: string;
        statusPresenca: StatusSim;
        justificativa: string;
        observacoes: string;
      }>;

      if (entered.size > 0) {
        const withRfid = shuffled.filter((a) => entered.has(a.id));
        const withoutRfid = shuffled.filter((a) => !entered.has(a.id));
        const nSem = Math.min(withoutRfid.length, nSemTarget);
        const semRfid = withoutRfid.slice(0, nSem);
        const ausentes = withoutRfid.slice(nSem);
        studentPayload = [
          ...withRfid.map((a) => row(a.id, 'PRESENTE')),
          ...semRfid.map((a) => row(a.id, 'PRESENTE')),
          ...ausentes.map((a) => row(a.id, 'AUSENTE')),
        ];
      } else {
        // Sem RFID ainda: aplica mix (presentes = RFID% + s/RFID%; resto ausente)
        const nPresente = Math.min(n, countByPct(n, mix.pctRfid + mix.pctSemRfid));
        const nAus = Math.min(n - nPresente, nAusTarget || n - nPresente);
        const presentesIds = shuffled.slice(0, nPresente);
        const ausentesIds = shuffled.slice(nPresente, nPresente + nAus);
        const resto = shuffled.slice(nPresente + nAus);
        studentPayload = [
          ...presentesIds.map((a) => row(a.id, 'PRESENTE')),
          ...[...ausentesIds, ...resto].map((a) => row(a.id, 'AUSENTE')),
        ];
      }

      // Garante que todo abono ACEITA entre na chamada (mesmo se o mix não o listou).
      for (const jid of justificadosIds) {
        if (!studentPayload.some((p) => p.alunoId === jid)) {
          studentPayload.push(row(jid, 'JUSTIFICADO'));
        }
      }

      if (!studentPayload.length) {
        addLog(`[Chamada] Aberta (${chamadaId}) mas turma ${classToCall.nome} sem alunos ativos.`);
        return;
      }

      const resAlunos = await apiFetch(
        `/api/v1/escolas/${schoolIdForCall}/chamadas/${chamadaId}/alunos`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${profToken}`,
          },
          body: JSON.stringify({ alunos: studentPayload }),
        },
      );

      if (resAlunos.ok) {
        // Fecha a chamada — sem isso fica ABERTA e a Central diverge do Dashboard.
        try {
          const resConc = await apiFetch(
            `/api/v1/escolas/${schoolIdForCall}/chamadas/${chamadaId}/concluir`,
            {
              method: 'PATCH',
              headers: { Authorization: `Bearer ${profToken}` },
            },
          );
          if (!resConc.ok) {
            const errC = await resConc.json().catch(() => ({}));
            addLog(
              `[Chamada] Aviso: alunos ok mas concluir falhou (${classToCall.nome}): ${formatApiError(errC, resConc.statusText)}`,
            );
          }
        } catch (errConc: any) {
          addLog(`[Chamada] Aviso: concluir falhou (${classToCall.nome}): ${errConc?.message || errConc}`);
        }

        const presentes = studentPayload.filter((p) => p.statusPresenca === 'PRESENTE').length;
        const justif = studentPayload.filter((p) => p.statusPresenca === 'JUSTIFICADO').length;
        const faltas = studentPayload.filter((p) => p.statusPresenca === 'AUSENTE').length;
        // 1 linha pós-confirmar/concluir (fluxo aluno-a-aluno continua nos POSTs, sem flood no strip).
        addLog(
          `[Chamada] ${chamadaHeader} · aula ${numeroAula} · ${presentes} present. · ${faltas} aus.` +
            (justif ? ` · ${justif} justif.` : ''),
        );
      } else {
        const err = await resAlunos.json().catch(() => ({}));
        addLog(
          `[Chamada] Erro ao registrar alunos (${classToCall.nome}): ${formatApiError(err, resAlunos.statusText)} (${resAlunos.status})`,
        );
      }
    } catch (err: any) {
      addLog(`[Chamada] Erro de rede: ${err.message}`);
    }
  };

  const runHeaderSimAction = async (
    winId: string,
    action: 'rfid' | 'chamada',
    schoolId: string,
    schoolNome?: string,
  ) => {
    if (simBusy[winId]) return;
    if (simEngineModeRef.current === 'CHRONOS') {
      addLog(`[SYS] Aviso: Chronos ativo — chip manual pode duplicar eventos.`);
    }
    setSimBusy((prev) => ({ ...prev, [winId]: action }));
    setSimFocusSchoolId(schoolId);
    try {
      if (action === 'rfid') await triggerRfidEntrance(schoolId, schoolNome);
      else await triggerClassAttendance(schoolId, schoolNome);
    } finally {
      setSimBusy((prev) => ({ ...prev, [winId]: null }));
    }
  };

  const loadTurnosForSchool = async (schoolId: string): Promise<SchoolTurnoOpt[]> => {
    if (turnosBySchool[schoolId]?.length) return turnosBySchool[schoolId];
    try {
      const rows = await simulationApi.listSchoolTurnos(schoolId);
      const opts: SchoolTurnoOpt[] = [];
      for (const t of rows) {
        if (t.ativo === false) continue;
        const shift = matchTurnoToShift(t.nome);
        if (!shift) continue;
        opts.push({ id: t.id, nome: t.nome, shift });
      }
      setTurnosBySchool((prev) => ({ ...prev, [schoolId]: opts }));
      return opts;
    } catch (err: any) {
      addLog(`[SYS] Falha ao listar turnos: ${err?.message || err}`);
      return [];
    }
  };

  const reloadDayPlan = async (
    schoolId: string,
    shift: ShiftKey,
    dia: DiaSemanaKey,
    ymdOverride?: string,
  ) => {
    const ymd = ymdOverride || simDateYmdRef.current;
    const turnos = await loadTurnosForSchool(schoolId);
    const match = turnos.find((t) => t.shift === shift);
    if (!match) {
      setDayPlan(null);
      setEventQueue(buildQueueForShift(shift));
      const start = SHIFT_WINDOWS[shift].startHm;
      const vt = dateAtYmdHm(ymd, start);
      virtualTimeRef.current = vt;
      setVirtualTime(vt);
      addLog(`[SYS] Sem turno operacional para ${shift} — usando template fixo.`);
      return null;
    }
    try {
      const plan = (await simulationApi.getShiftDayPlan(
        schoolId,
        match.id,
        dia,
      )) as ShiftDayPlan;
      if (!plan?.success) {
        addLog(`[SYS] Plano do dia falhou: ${plan?.error || 'erro'}`);
        setDayPlan(null);
        setEventQueue(buildQueueForShift(shift));
        return null;
      }
      setDayPlan(plan);
      const queue = buildQueueFromPlan(plan);
      setEventQueue(queue.length ? queue : buildQueueForShift(shift));
      const start = plan.boundaries?.abertura || SHIFT_WINDOWS[shift].startHm;
      const vt = dateAtYmdHm(ymd, start);
      virtualTimeRef.current = vt;
      setVirtualTime(vt);
      const aulaSlots = (plan.slots || []).filter((s) => s.tipo === 'AULA').length;
      const chamadasNaFila = queue.filter((e) => e.kind === 'chamada').length;
      addLog(
        `[SYS] Plano ${plan.turnoNome} · ${dia} · ${ymd} · ${aulaSlots} horários de aula · ${chamadasNaFila} chamadas na fila · ${queue.length} eventos · roster ${plan.roster?.alunosComTag ?? 0} tags / ${plan.roster?.professores ?? 0} prof.`,
      );
      if (plan.diaForaDoTurno) {
        addLog(`[SYS] Aviso: ${dia} não está em diasFuncionamento do turno.`);
      }
      return plan;
    } catch (err: any) {
      addLog(`[SYS] Erro ao carregar plano: ${err?.message || err}`);
      setDayPlan(null);
      setEventQueue(buildQueueForShift(shift));
      return null;
    }
  };

  const applyShift = (shift: ShiftKey, schoolId?: string) => {
    setActiveShift(shift);
    const sid = schoolId || simFocusSchoolIdRef.current || selectedSchool?.id;
    if (schoolId) setSimFocusSchoolId(schoolId);
    if (sid) {
      delete rfidEnteredBySchoolRef.current[sid];
      void reloadDayPlan(sid, shift, simDiaSemanaRef.current);
    } else {
      setEventQueue(buildQueueForShift(shift));
      const vt = dateAtYmdHm(simDateYmdRef.current, SHIFT_WINDOWS[shift].startHm);
      virtualTimeRef.current = vt;
      setVirtualTime(vt);
      addLog(`[SYS] Turno ${SHIFT_WINDOWS[shift].label} — fila template (sem escola).`);
    }
  };

  /**
   * Fim da janela do turno: avança para o próximo turno da escola,
   * ou (com Loop dias) para o próximo dia letivo e recomeça os turnos.
   * Nunca reinicia o mesmo turno no mesmo dia (evita duplicar RFID/chamada).
   */
  const completeCurrentShift = async () => {
    if (shiftTransitionRef.current) return;
    shiftTransitionRef.current = true;
    try {
      const current = activeShiftRef.current;
      const sid = simFocusSchoolIdRef.current || selectedSchool?.id || undefined;
      const turnos = sid ? turnosBySchoolRef.current[sid] || [] : [];
      const available = [...new Set(turnos.map((t) => t.shift))] as ShiftKey[];
      const next = nextAvailableShift(current, available);

      if (!next) {
        if (!dayLoopEnabledRef.current) {
          setIsSimulationPlaying(false);
          addLog(
            `[SYS] Fim do dia letivo · turno ${SHIFT_WINDOWS[current].label} encerrado. ` +
              `Relatório preservado — ative «Loop dias» ou escolha outro dia / Preparar.`,
          );
          return;
        }

        const { ymd: nextYmd, diaSemana: nextDia } = nextSchoolDay(simDateYmdRef.current);
        const first = firstAvailableShift(available);
        simDateYmdRef.current = nextYmd;
        simDiaSemanaRef.current = nextDia;
        setSimDateYmd(nextYmd);
        setSimDiaSemana(nextDia);
        setActiveShift(first);
        activeShiftRef.current = first;
        if (sid) delete rfidEnteredBySchoolRef.current[sid];

        addLog(
          `[SYS] Fim do dia → próximo letivo ${nextYmd} (${nextDia}) · recomeça ${SHIFT_WINDOWS[first].label}.`,
        );

        if (sid) {
          await reloadDayPlan(sid, first, nextDia, nextYmd);
        } else {
          setEventQueue(buildQueueForShift(first));
          const vt = dateAtYmdHm(nextYmd, SHIFT_WINDOWS[first].startHm);
          virtualTimeRef.current = vt;
          setVirtualTime(vt);
        }

        pushSimClockToSessionAndEmbeds({
          simDateYmd: nextYmd,
          virtualEpochMs: virtualTimeRef.current.getTime(),
          playing: true,
        });
        // Play permanece ligado — loop contínuo.
        return;
      }

      if (sid) delete rfidEnteredBySchoolRef.current[sid];
      addLog(
        `[SYS] Fim do turno ${SHIFT_WINDOWS[current].label} → próximo: ${SHIFT_WINDOWS[next].label} ` +
          `(sem reexecutar ${SHIFT_WINDOWS[current].label}).`,
      );
      setActiveShift(next);
      activeShiftRef.current = next;
      if (sid) {
        await reloadDayPlan(sid, next, simDiaSemanaRef.current, simDateYmdRef.current);
        pushSimClockToSessionAndEmbeds({
          virtualEpochMs: virtualTimeRef.current.getTime(),
          playing: true,
        });
      } else {
        setEventQueue(buildQueueForShift(next));
        const vt = dateAtYmdHm(simDateYmdRef.current, SHIFT_WINDOWS[next].startHm);
        virtualTimeRef.current = vt;
        setVirtualTime(vt);
      }
      // Continua o Play no próximo turno (Auto); em manual só carrega a fila.
    } finally {
      shiftTransitionRef.current = false;
    }
  };

  const applySimDate = (ymd: string, schoolId?: string) => {
    const [y, m, d] = ymd.split('-').map(Number);
    const picked = new Date(y, (m || 1) - 1, d || 1, 12, 0, 0);
    const js = picked.getDay();
    let finalYmd = ymd;
    let dia = ymdToDiaSemana(ymd);
    if (js === 0 || js === 6) {
      finalYmd = ymdForDiaSemana('SEGUNDA', ymd);
      dia = 'SEGUNDA';
      addLog(`[SYS] Fim de semana → ajustado para próxima SEGUNDA (${finalYmd}).`);
    }
    simDateYmdRef.current = finalYmd;
    simDiaSemanaRef.current = dia;
    setSimDateYmd(finalYmd);
    setSimDiaSemana(dia);
    const sid = schoolId || simFocusSchoolIdRef.current || selectedSchool?.id;
    if (sid) {
      void reloadDayPlan(sid, activeShiftRef.current, dia, finalYmd).then(() => {
        pushSimClockToSessionAndEmbeds({
          simDateYmd: finalYmd,
          virtualEpochMs: virtualTimeRef.current.getTime(),
        });
      });
    } else {
      addLog(`[SYS] Dia da simulação → ${finalYmd} (${dia}).`);
      pushSimClockToSessionAndEmbeds({ simDateYmd: finalYmd });
    }
  };

  const applySimDia = (dia: DiaSemanaKey, schoolId?: string) => {
    const ymd = ymdForDiaSemana(dia, simDateYmdRef.current);
    simDiaSemanaRef.current = dia;
    simDateYmdRef.current = ymd;
    setSimDiaSemana(dia);
    setSimDateYmd(ymd);
    const sid = schoolId || simFocusSchoolIdRef.current || selectedSchool?.id;
    if (sid) {
      void reloadDayPlan(sid, activeShiftRef.current, dia, ymd).then(() => {
        pushSimClockToSessionAndEmbeds({
          simDateYmd: ymd,
          virtualEpochMs: virtualTimeRef.current.getTime(),
        });
      });
    }
    addLog(`[SYS] Dia da semana → ${dia} (${ymd}).`);
  };

  const resolveSimSchoolId = (fallback?: string) =>
    fallback || simFocusSchoolIdRef.current || selectedSchool?.id || undefined;

  const isSimQueueBusy = () =>
    simQueueInFlightRef.current > 0 ||
    simQueueScheduledIdsRef.current.size > 0 ||
    eventQueueRef.current.some((e) => e.status === 'running');

  /** Pendentes já vencidos no relógio virtual, agrupados pelo `at` mais cedo. */
  const collectSameAtDueBatch = (mins: number, limit: number): SimEvent[] => {
    if (limit <= 0) return [];
    const scheduled = simQueueScheduledIdsRef.current;
    const due = eventQueueRef.current
      .filter(
        (e) =>
          e.status === 'pending' &&
          !scheduled.has(e.id) &&
          mins >= parseHm(e.at),
      )
      .sort((a, b) => parseHm(a.at) - parseHm(b.at) || a.id.localeCompare(b.id));
    if (!due.length) return [];
    const at0 = due[0].at;
    return due.filter((e) => e.at === at0).slice(0, limit);
  };

  const runSimEventCore = async (ev: SimEvent, schoolId?: string) => {
    const sid = resolveSimSchoolId(schoolId);
    setEventQueue((prev) =>
      prev.map((e) => (e.id === ev.id ? { ...e, status: 'running' } : e)),
    );
    // Chamada/RFID já emitem resumo próprio — evita 3 linhas por turma na fila.
    if (ev.kind !== 'chamada' && ev.kind !== 'rfid' && ev.kind !== 'rfid_saida') {
      addLog(`[${ev.at} V] [Fila] Executando ${ev.label}…`);
    }
    try {
      if (ev.kind === 'recreio') {
        addLog(`[${ev.at} V] [Recreio] ${ev.label}${ev.end ? ` até ${ev.end}` : ''} — sem POST.`);
      } else if (ev.kind === 'rfid') {
        await triggerRfidEntrance(sid, undefined, { tipoEvento: 'ENTRADA', atHm: ev.at });
      } else if (ev.kind === 'rfid_saida') {
        await triggerRfidEntrance(sid, undefined, { tipoEvento: 'SAIDA', atHm: ev.at });
      } else if (ev.kind === 'chamada') {
        await triggerClassAttendance(sid, undefined, ev);
      } else if (ev.kind === 'comunicado') {
        await triggerComunicadoPrefeito(sid);
      }
      setEventQueue((prev) =>
        prev.map((e) => (e.id === ev.id ? { ...e, status: 'done' } : e)),
      );
    } catch (err: any) {
      addLog(`[${ev.at} V] [Fila] Falha em ${ev.label}: ${err?.message || err}`);
      setEventQueue((prev) =>
        prev.map((e) => (e.id === ev.id ? { ...e, status: 'pending' } : e)),
      );
    }
  };

  const runSimEventBatch = async (batch: SimEvent[], schoolId?: string) => {
    if (!batch.length) return;
    for (const ev of batch) simQueueScheduledIdsRef.current.add(ev.id);
    simQueueInFlightRef.current += batch.length;
    if (batch.length > 1) {
      addLog(`[${batch[0].at} V] [Fila] Lote ×${batch.length} (paralelo ≤${SIM_QUEUE_PARALLEL}).`);
    }
    try {
      // Backend valida janela pelo clock da sessão — não pelo relógio da UI.
      // A 240x o patch periódico atrasa; força epoch >= horário do lote antes dos POSTs.
      const atHm = batch[0]?.at;
      if (atHm) {
        const atDate = dateAtYmdHm(simDateYmdRef.current, atHm);
        if (virtualTimeRef.current.getTime() < atDate.getTime()) {
          virtualTimeRef.current = atDate;
          setVirtualTime(atDate);
        }
      }
      await pushSimClockToSessionAndEmbeds({
        virtualEpochMs: virtualTimeRef.current.getTime(),
        playing: true,
      });
      await Promise.all(batch.map((ev) => runSimEventCore(ev, schoolId)));
    } finally {
      simQueueInFlightRef.current = Math.max(0, simQueueInFlightRef.current - batch.length);
      for (const ev of batch) simQueueScheduledIdsRef.current.delete(ev.id);
    }
  };

  const executeSimEvent = async (ev: SimEvent, schoolId?: string) => {
    if (isSimQueueBusy()) {
      addLog(`[SYS] Fila ocupada — «${ev.label}» não iniciado.`);
      return;
    }
    await runSimEventBatch([ev], schoolId);
  };

  const runNextQueueEvent = () => {
    if (simEngineModeRef.current === 'CHRONOS') return;
    if (isSimQueueBusy()) {
      addLog(`[SYS] Fila: espere o lote atual terminar.`);
      return;
    }
    const next = eventQueueRef.current.find((e) => e.status === 'pending');
    if (!next) {
      addLog(`[SYS] Fila: nenhum evento pendente.`);
      return;
    }
    // «Só próximo»: um evento; Auto usa lote paralelo no tick.
    void executeSimEvent(next);
  };

  const skipNextQueueEvent = () => {
    if (simEngineModeRef.current === 'CHRONOS') return;
    const next = eventQueueRef.current.find((e) => e.status === 'pending');
    if (!next) {
      addLog(`[SYS] Fila: nada para pular.`);
      return;
    }
    setEventQueue((prev) =>
      prev.map((e) => (e.id === next.id ? { ...e, status: 'skipped' } : e)),
    );
    addLog(`[SYS] Fila: pulado ${next.label} (${next.at}).`);
  };

  const cleanTodayForSchool = async (schoolId: string) => {
    setChronosBusy(true);
    try {
      delete rfidEnteredBySchoolRef.current[schoolId];
      const res = await simulationApi.chronosClean(schoolId, simDateYmdRef.current);
      const s = res?.summary;
      addLog(
        `[SYS] [Chronos] Limpar dia ${s?.data ?? simDateYmdRef.current}: notif=${s?.notificacoes ?? '?'} chamadas=${s?.chamadas ?? '?'} RFID=${s?.eventosAcesso ?? '?'} presença=${s?.presencasDiarias ?? '?'}`,
      );
      await reloadDayPlan(schoolId, activeShiftRef.current, simDiaSemana);
    } catch (err: any) {
      addLog(`[SYS] [Chronos] Falha ao limpar: ${err?.message || err}`);
    } finally {
      setChronosBusy(false);
    }
  };

  const prepareSimulationForSchool = async (schoolId: string) => {
    setSimFocusSchoolId(schoolId);
    setChronosBusy(true);
    try {
      delete rfidEnteredBySchoolRef.current[schoolId];
      delete schoolMetadataCacheRef.current[schoolId];
      await loadTurnosForSchool(schoolId);
      await loadSchoolMetadataFor(schoolId);
      const schoolNome =
        selectedSchool?.id === schoolId
          ? selectedSchool?.nome
          : sessionSchools.find((s) => s.id === schoolId)?.nome;
      const ready = await ensureChronosSchoolReady(schoolId, schoolNome);
      if (ready.directorToken) {
        addLog(`[SYS] JWT diretor OK (sem precisar abrir janela da escola).`);
      } else if (ready.error) {
        addLog(`[SYS] Aviso bootstrap Chronos: ${ready.error}`);
      }
      await simulationApi.chronosClean(schoolId, simDateYmdRef.current);
      const plan = await reloadDayPlan(schoolId, activeShiftRef.current, simDiaSemana);
      setSimEngineMode('COCKPIT');
      setChronosSession(null);
      setIsSimulationPlaying(false);
      setQueueControl('auto');
      const r = plan?.roster;
      const mix = normalizePresenceMix(presenceMixRef.current);
      addLog(
        `[SYS] Preparado · ${simDateYmdRef.current} · ${activeShiftRef.current}` +
          (r
            ? ` · ${r.alunosComTag} tags / ${r.professores} prof / ${r.responsaveis} resp / ${r.turmas} turmas`
            : '') +
          ` · mix ${mix.pctRfid}% RFID / ${mix.pctSemRfid}% s/RFID / ${mix.pctAusente}% aus.` +
          ' · Auto marcado · pausado (Play para rodar)',
      );
    } catch (err: any) {
      addLog(`[SYS] Falha ao preparar: ${err?.message || err}`);
    } finally {
      setChronosBusy(false);
    }
  };

  const startChronosForSchool = async (schoolId: string) => {
    setChronosBusy(true);
    setSimFocusSchoolId(schoolId);
    try {
      const turnos = await loadTurnosForSchool(schoolId);
      const match = turnos.find((t) => t.shift === activeShiftRef.current);
      if (!match) {
        addLog(`[SYS] [Chronos] Sem turno operacional para ${activeShiftRef.current}.`);
        return;
      }
      // ~3 min reais; speed alto → duração menor (mín 60s)
      const realDurationSec = Math.max(60, Math.min(300, Math.round(180 * (60 / Math.max(30, simulationSpeed)))));
      setIsSimulationPlaying(false);
      const res = await simulationApi.chronosStart({
        escolaId: schoolId,
        turnoId: match.id,
        realDurationSec,
        pctEntrada: 85,
        pctAusente: 10,
        pctAtrasado: 5,
      });
      if (res?.success === false) {
        addLog(`[SYS] [Chronos] Falha ao iniciar: ${res?.error || res?.message}`);
        return;
      }
      setSimEngineMode('CHRONOS');
      setChronosSession({
        schoolId,
        turnoId: match.id,
        clock: undefined,
        fase: 'STARTING',
        paused: false,
      });
      addLog(`[SYS] [Chronos] Iniciado ${match.nome} (${realDurationSec}s reais) — fila cockpit pausada.`);
    } catch (err: any) {
      addLog(`[SYS] [Chronos] Erro: ${err?.message || err}`);
    } finally {
      setChronosBusy(false);
    }
  };

  const pauseResumeChronos = async () => {
    if (!chronosSession) return;
    setChronosBusy(true);
    try {
      if (chronosSession.paused) {
        const res = await simulationApi.chronosResume(chronosSession.schoolId, chronosSession.turnoId);
        addLog(`[SYS] [Chronos] ${res?.message || 'Retomado'}`);
        setChronosSession((s) => (s ? { ...s, paused: false } : s));
      } else {
        const res = await simulationApi.chronosPause(chronosSession.schoolId, chronosSession.turnoId);
        addLog(`[SYS] [Chronos] ${res?.message || 'Pausado'}`);
        setChronosSession((s) => (s ? { ...s, paused: true } : s));
      }
    } catch (err: any) {
      addLog(`[SYS] [Chronos] Pause/Resume falhou: ${err?.message || err}`);
    } finally {
      setChronosBusy(false);
    }
  };

  const stopChronos = async () => {
    if (!chronosSession) {
      setSimEngineMode('COCKPIT');
      return;
    }
    setChronosBusy(true);
    try {
      await simulationApi.chronosStop(chronosSession.schoolId, chronosSession.turnoId);
      addLog(`[SYS] [Chronos] Parado — voltando ao modo COCKPIT.`);
    } catch (err: any) {
      addLog(`[SYS] [Chronos] Stop: ${err?.message || err}`);
    } finally {
      setChronosSession(null);
      setSimEngineMode('COCKPIT');
      setChronosBusy(false);
    }
  };

  // Robô Prefeito: comunicado → RESPONSAVEIS (E2E Ana) + anti-duplicata no dia da sim
  const triggerComunicadoPrefeito = async (schoolId?: string) => {
    hydrateActiveSessionsFromStorage();
    const sessionsMap = {
      ...(JSON.parse(localStorage.getItem('ac_sessions') || '{}') || {}),
      ...(activeSessions || {}),
    };
    const municipalSession = Object.values(sessionsMap).find(
      (s: any) => {
        const p = String(s?.user?.perfil || '').toUpperCase();
        return (p === 'MUNICIPAL' || p === 'MUNICIPIO') && s?.access_token;
      },
    ) as any;
    if (!municipalSession) {
      addLog(`[Comunicado] Sem JWT municipal — abra o painel do prefeito antes.`);
      return;
    }

    const municipioId =
      municipalSession.user?.municipioId ||
      municipalSession.municipioId ||
      municipalSession.user?.municipio?.id;
    if (!municipioId) {
      addLog(`[Comunicado] Sessão municipal sem municipioId.`);
      return;
    }

    const targetSchoolId = schoolId || simFocusSchoolIdRef.current || selectedSchool?.id;
    const tituloAlerta = 'Alerta Municipal de Temperatura e Clima';
    const diaSim = simDateYmdRef.current;
    const token = municipalSession.access_token as string;

    // Anti-duplicata: 1 alerta igual ENVIADO por escola (reexecução da fila)
    try {
      const resList = await fetch(`/api/v1/municipios/${municipioId}/comunicados`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resList.ok) {
        const raw = await resList.json();
        const items = Array.isArray(raw) ? raw : raw?.data || [];
        const jaEnviado = items.some((c: any) => {
          if (String(c?.titulo || '') !== tituloAlerta) return false;
          if (String(c?.status || '').toUpperCase() !== 'ENVIADO') return false;
          if (targetSchoolId) {
            return !c?.escolaId || c.escolaId === targetSchoolId;
          }
          return true;
        });
        if (jaEnviado) {
          addLog(
            `[Comunicado] Já existe "${tituloAlerta}" ENVIADO` +
              (targetSchoolId ? ' para esta escola' : '') +
              ` — skip (anti-duplicata · ${diaSim}).`,
          );
          return;
        }
      }
    } catch {
      /* segue envio */
    }

    addLog(
      `[Comunicado] Prefeito(a) ${municipalSession.user.nome} → RESPONSAVEIS` +
        (targetSchoolId ? ` · escola foco` : ' · rede') +
        ` · dia ${diaSim}…`,
    );

    try {
      const payload: Record<string, unknown> = {
        titulo: tituloAlerta,
        corpo:
          'Informamos a toda a comunidade escolar que devido às previsões climáticas, o cronograma escolar segue normal com reforço na hidratação dos estudantes.',
        tipo: 'AVISO',
        destinatario: 'RESPONSAVEIS',
        status: 'ENVIADO',
      };
      if (targetSchoolId) payload.escolaIds = [targetSchoolId];

      const res = await apiFetch(`/api/v1/municipios/${municipioId}/comunicados`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        addLog(`[Comunicado] OK — enviado aos responsáveis (push/timeline).`);
      } else {
        const err = await res.json().catch(() => ({}));
        addLog(
          `[Comunicado] Erro ao enviar: ${formatApiError(err, res.statusText)} (${res.status})`,
        );
      }
    } catch (err: any) {
      addLog(`[Comunicado] Erro de rede: ${err.message}`);
    }
  };

  // Poll Chronos state (modo CHRONOS)
  useEffect(() => {
    if (simEngineMode !== 'CHRONOS' || !chronosSession) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const st = await simulationApi.chronosState(
          chronosSession.schoolId,
          chronosSession.turnoId,
        );
        if (cancelled) return;
        if (!st?.active) {
          if (st?.finished) {
            addLog(`[SYS] [Chronos] Sessão encerrada no backend.`);
            setChronosSession(null);
            setSimEngineMode('COCKPIT');
          }
          return;
        }
        setChronosSession((s) =>
          s
            ? {
                ...s,
                clock: st.clock,
                fase: st.fase,
                paused: !!st.paused,
              }
            : s,
        );
        if (Array.isArray(st.logs)) {
          for (const line of st.logs) {
            const msg = typeof line === 'string' ? line : line?.msg || JSON.stringify(line);
            const time = typeof line === 'object' && line?.time ? line.time : st.clock;
            addLog(`[${time || 'V'}] [Chronos] ${msg}`);
          }
        }
        if (st.finished) {
          addLog(`[SYS] [Chronos] Turno finalizado.`);
          setChronosSession(null);
          setSimEngineMode('COCKPIT');
        }
      } catch {
        /* noop */
      }
    };
    void poll();
    const t = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [simEngineMode, chronosSession?.schoolId, chronosSession?.turnoId]);

  // Relógio Virtual + fila COCKPIT (só com sessão RUNNING em foco)
  useEffect(() => {
    if (!isSimulationPlaying) return;
    if (simEngineModeRef.current === 'CHRONOS') return;
    const focus = useSimSessionFocus.getState().focusedSession;
    if (focus && focus.status !== 'RUNNING') return;

    let lastRealTime = performance.now();

    const tick = () => {
      const live = useSimSessionFocus.getState().focusedSession;
      if (live && live.status !== 'RUNNING') {
        setIsSimulationPlaying(false);
        return;
      }
      if (simEngineModeRef.current === 'CHRONOS') return;

      const nowRealTime = performance.now();
      const deltaMs = nowRealTime - lastRealTime;
      lastRealTime = nowRealTime;

      const shift = activeShiftRef.current;
      const win = SHIFT_WINDOWS[shift];
      const plan = dayPlanRef.current;
      const endHm = plan?.boundaries?.fechamento || win.endHm;
      const endMins = parseHm(endHm);
      const control = queueControlRef.current;

      let mins = dateToMinutes(virtualTimeRef.current);
      const hasDuePending = eventQueueRef.current.some(
        (e) => e.status === 'pending' && mins >= parseHm(e.at),
      );
      const queueBusy = isSimQueueBusy();

      // Catch-up: com backlog/HTTP em voo, congela o relógio virtual (evita "Perdida"
      // por avanço 240x enquanto a fila ainda processa o horário atual).
      const holdClock = queueBusy || hasDuePending;

      if (!holdClock) {
        let nextVirtual = new Date(virtualTimeRef.current.getTime() + deltaMs * simulationSpeed);
        mins = dateToMinutes(nextVirtual);

        if (mins >= endMins) {
          nextVirtual = setDateToHm(nextVirtual, endHm);
          mins = endMins;
          virtualTimeRef.current = nextVirtual;
          setVirtualTime(nextVirtual);

          if (shiftTransitionRef.current) return;

          // Ainda há pendentes no fechamento? Drena (lote) ou pula / encerra.
          const dueAtEnd = collectSameAtDueBatch(mins, SIM_QUEUE_PARALLEL);
          if (dueAtEnd.length && control !== 'manual') {
            if (control === 'pause_next') {
              setIsSimulationPlaying(false);
              addLog(
                `[${dateToHm(nextVirtual)} V] [Fila] Pausado antes de ${dueAtEnd[0].label} (${dueAtEnd[0].at}). Use «Só próximo» ou Auto.`,
              );
              return;
            }
            void runSimEventBatch(dueAtEnd);
            return;
          }

          const stillPending = eventQueueRef.current.some((e) => e.status === 'pending');
          if (stillPending) {
            if (control === 'manual') {
              setIsSimulationPlaying(false);
              addLog(
                `[SYS] Fim da janela ${win.label} com eventos pendentes — use «Só próximo» ou Auto. Não reinicia o turno.`,
              );
              return;
            }
            setEventQueue((prev) =>
              prev.map((e) => (e.status === 'pending' ? { ...e, status: 'skipped' as const } : e)),
            );
            addLog(`[SYS] Eventos pendentes após ${endHm} foram pulados.`);
          }

          void completeCurrentShift();
          return;
        }

        virtualTimeRef.current = nextVirtual;
        setVirtualTime(nextVirtual);

        // A cada minuto virtual novo, sincroniza sessão (a 240x o intervalo fixo não basta).
        if (lastPushedVirtualMinsRef.current !== mins) {
          lastPushedVirtualMinsRef.current = mins;
          void pushSimClockToSessionAndEmbeds({
            virtualEpochMs: nextVirtual.getTime(),
            playing: true,
          });
        }
      }

      // Drena fila: até SIM_QUEUE_PARALLEL eventos com o mesmo `at`.
      if (control === 'manual') return;

      const slotsFree = Math.max(0, SIM_QUEUE_PARALLEL - simQueueInFlightRef.current);
      if (slotsFree <= 0) return;

      const batch = collectSameAtDueBatch(mins, slotsFree);
      if (!batch.length) return;

      if (control === 'pause_next') {
        setIsSimulationPlaying(false);
        addLog(
          `[${dateToHm(virtualTimeRef.current)} V] [Fila] Pausado antes de ${batch[0].label} (${batch[0].at}). Use «Só próximo» ou Auto.`,
        );
        return;
      }

      void runSimEventBatch(batch);
    };

    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [isSimulationPlaying, simulationSpeed, schoolMetadata, activeSessions]);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [snapPreview, setSnapPreview] = useState<'left' | 'right' | 'top' | null>(null);
  const [profileSelector, setProfileSelector] = useState<{
    profileKey: keyof typeof PROFILE_CONFIGS;
    actors: any[];
  } | null>(null);

  // Estados de layout dos ícones
  const [autoArrange, setAutoArrange] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('ac_desktop_autoarrange');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const [desktopItems, setDesktopItems] = useState<DesktopItem[]>(() => {
    try {
      const saved = localStorage.getItem('ac_desktop_items');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.length > 2) {
          return parsed;
        }
      }
    } catch { }

    return [
      { id: 'item-cidades', name: 'Cidades', type: 'FOLDER' as const, folderId: 'cidades', x: 32, y: 32 },
      { id: 'item-prefeito', name: 'Prefeito', type: 'PROFILE' as const, profileKey: 'PREFEITO' as const, x: 32, y: 120 },
      { id: 'item-diretor', name: 'Diretor', type: 'PROFILE' as const, profileKey: 'DIRETOR' as const, x: 32, y: 210 },
      { id: 'item-secretaria', name: 'Secretaria', type: 'PROFILE' as const, profileKey: 'SECRETARIA' as const, x: 32, y: 300 },
      { id: 'item-coordenador', name: 'Coordenador', type: 'PROFILE' as const, profileKey: 'COORDENADOR' as const, x: 32, y: 390 },
      { id: 'item-celular1', name: 'Celular 1', type: 'PROFILE' as const, profileKey: 'CELULAR1' as const, x: 140, y: 32 },
      { id: 'item-celular2', name: 'Celular 2', type: 'PROFILE' as const, profileKey: 'CELULAR2' as const, x: 140, y: 120 },
      { id: 'item-celular3', name: 'Celular 3', type: 'PROFILE' as const, profileKey: 'CELULAR3' as const, x: 140, y: 210 },
      { id: 'item-celular4', name: 'Celular 4', type: 'PROFILE' as const, profileKey: 'CELULAR4' as const, x: 140, y: 300 },
    ];
  });

  // Estados Multi-Ambiente (Multi-Ambiente)
  const [environments, setEnvironments] = useState<AcEnvironment[]>(() => {
    try {
      const saved = localStorage.getItem('ac_environments');
      if (saved) return JSON.parse(saved);
    } catch { }
    return [{ id: 'env-neutro', name: 'Neutro', cor: '#a6adc8' }];
  });

  const [activeEnvironmentId, setActiveEnvironmentId] = useState<string>(() => {
    try {
      const saved = localStorage.getItem('ac_active_environment_id');
      if (saved) return saved;
    } catch { }
    return 'env-neutro';
  });

  const [isEnvModalOpen, setIsEnvModalOpen] = useState<boolean>(false);
  const [dbSchools, setDbSchools] = useState<any[]>([]);
  const [envForm, setEnvForm] = useState({
    name: '',
    cor: '#cba6f7',
    escolaId: '',
    municipioId: '',
  });

  // Estado do Layout dos Simuladores (Grelha, Lado a Lado, Dock Lateral, Livre)
  const [simulatorLayout, setSimulatorLayout] = useState<'GRID' | 'SIDE_BY_SIDE' | 'DOCK' | 'FREE'>('DOCK');
  const ENABLE_SIDE_DOCK = false;

  // Estado de Temas
  const [theme, setTheme] = useState<'purple' | 'dark' | 'light'>(() => {
    try {
      const saved = localStorage.getItem('ac_desktop_theme');
      return (saved as any) || 'purple';
    } catch {
      return 'purple';
    }
  });

  // Estados de Perfis Simulados e Transição 3D
  const defaultRole = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('ac_user') || 'null');
      if (user) {
        const perf = (user.perfil || '').toUpperCase();
        const carg = (user.cargo || '').toUpperCase();

        if (perf === 'ADMIN') return 'administrador';
        if (perf === 'SIM_STUDIO' || perf === 'MUNICIPAL' || perf === 'MUNICIPIO') return 'prefeito';
        if (perf === 'ESCOLA') {
          if (carg === 'COORDENACAO' || carg === 'COORDENADOR') return 'coordenador';
          if (carg === 'SECRETARIA') return 'secretaria';
          return 'diretor';
        }
        if (perf === 'PROFESSOR') return 'professor';
        if (perf === 'RESPONSAVEL') return 'responsavel';
        if (perf === 'ALUNO') return 'aluno';
      }
    } catch {}
    return 'administrador';
  })();

  const [activeRoleKey, setActiveRoleKey] = useState<string>(defaultRole);
  const [selectedRoleToChange, setSelectedRoleToChange] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [transitionRoleKeys, setTransitionRoleKeys] = useState<{ current: string; next: string }>({ current: defaultRole, next: defaultRole });
  const [isTransitionsPanelOpen, setIsTransitionsPanelOpen] = useState<boolean>(false);

  // Estados do Menu Iniciar & Localizar & Contexto
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isStartMenuOpen, setIsStartMenuOpen] = useState<boolean>(false);
  const [desktopTime, setDesktopTime] = useState<string>('12:00');

  const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number } | null>(null);
  const [iconContextMenu, setIconContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    itemId: string;
    itemType: 'PROFILE' | 'FOLDER' | 'SHORTCUT';
    itemName: string;
    profileKey?: keyof typeof PROFILE_CONFIGS;
  } | null>(null);

  // Estado de seleção e edição inline do Desktop
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');

  // 1. Busca escolas de simulação disponíveis
  useEffect(() => {
    fetch('/api/simulation/schools')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setDbSchools(data);
        }
      })
      .catch(() => { });
  }, []);

  // 2. Efeito de Migração dos Perfis Antigos no localStorage + Compactar Layout
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ac_desktop_items');
      if (saved) {
        const items = JSON.parse(saved) as DesktopItem[];
        const hasOldProfiles = items.some(item =>
          item.type === 'PROFILE' &&
          ((item.profileKey as string) === 'PROFESSOR' || (item.profileKey as string) === 'RESPONSAVEL' || (item.profileKey as string) === 'ALUNO')
        );

        if (hasOldProfiles) {
          // Remove perfis antigos e adiciona os novos Celular 1-4
          const filtered = items.filter(item =>
            item.type !== 'PROFILE' ||
            (((item.profileKey as string) !== 'PROFESSOR') && ((item.profileKey as string) !== 'RESPONSAVEL') && ((item.profileKey as string) !== 'ALUNO'))
          );

          const newProfiles = (Object.keys(PROFILE_CONFIGS) as Array<keyof typeof PROFILE_CONFIGS>).map((k, i) => ({
            id: `item-${k.toLowerCase()}`,
            name: k,
            type: 'PROFILE' as const,
            profileKey: k,
            x: 32,
            y: 80 + i * 90,
          }));

          const migrated = [...newProfiles, ...filtered.filter(f => f.type !== 'PROFILE')];
          setDesktopItems(migrated);
          localStorage.setItem('ac_desktop_items', JSON.stringify(migrated));
        }
      }
    } catch { }
  }, []);

  // 2b. Verificação de acesso aos emuladores (perfilTipo)
  const isEmulatorAllowed = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('ac_user') || 'null');
      if (!user) return false;
      const allowed = ['ADMIN', 'REDE', 'ESTADO', 'MUNICIPIO'];
      return allowed.includes(user.perfilTipo || user.perfil || '');
    } catch { return false; }
  })();

  // 2c. Celulares 1–3: criados sob demanda pelos ícones fixos da taskbar (não pré-cria)
  // (antes: auto-cria 4 instâncias minimizadas — removido para o modelo ícone-fixo / fecha-mata)

  // 3. Alternar entre Ambientes
  const handleSwitchEnvironment = (envId: string) => {
    setActiveEnvironmentId(envId);
    localStorage.setItem('ac_active_environment_id', envId);

    // Opcional: Adiciona log ao painel
    const env = environments.find(e => e.id === envId);
    if (env) {
      console.log(`[Multi-Ambiente] Sintonizado no contexto: ${env.name}`);
    }
  };

  // 4. Criar Novo Ambiente
  const handleCreateEnvironment = () => {
    if (!envForm.name.trim()) return;

    const newId = `env-${Date.now()}`;
    const schoolObj = dbSchools.find(s => s.id === envForm.escolaId);
    const municipioObj = dbSchools.find(s => s.municipio?.id === envForm.municipioId)?.municipio;

    const newEnv: AcEnvironment = {
      id: newId,
      name: envForm.name,
      cor: envForm.cor,
      id_Rede: schoolObj?.tipoRede || undefined,
      id_estado: schoolObj?.uf || municipioObj?.estadoId || undefined,
      id_cidade: envForm.municipioId || undefined,
      id_escola: envForm.escolaId || undefined,
    };

    const updated = [...environments, newEnv];
    setEnvironments(updated);
    localStorage.setItem('ac_environments', JSON.stringify(updated));

    setActiveEnvironmentId(newId);
    localStorage.setItem('ac_active_environment_id', newId);

    setIsEnvModalOpen(false);
    setEnvForm({ name: '', cor: '#cba6f7', escolaId: '', municipioId: '' });
  };

  // 4.5. Alocação Automática de Escola em Novo Ambiente
  const handleCreateSchoolEnvironment = (cityId: string, cityName: string, schoolId: string, schoolName: string) => {
    const existing = environments.find(env => env.id_escola === schoolId);
    if (existing) {
      handleSwitchEnvironment(existing.id);
      return;
    }

    const newEnvId = `env-school-${schoolId}`;
    const newEnv: AcEnvironment = {
      id: newEnvId,
      name: schoolName,
      cor: '#a6e3a1',
      id_cidade: cityId,
      id_escola: schoolId,
    };

    const updated = [...environments, newEnv];
    setEnvironments(updated);
    localStorage.setItem('ac_environments', JSON.stringify(updated));

    setActiveEnvironmentId(newEnvId);
    localStorage.setItem('ac_active_environment_id', newEnvId);
  };

  // 5. Preset Layouts de Encaixe/Miniatura
  const applyPresetLayout = (preset: 'GRID' | 'SIDE_BY_SIDE' | 'DOCK' | 'FREE') => {
    setSimulatorLayout(preset);
    localStorage.setItem('ac_simulator_layout', preset);

    if (preset === 'FREE') return;

    const mobileWins = windows.filter(w => w.type === 'MOBILE');
    if (mobileWins.length === 0) return;

    if (preset === 'GRID') {
      const screenW = window.innerWidth;
      const screenH = window.innerHeight - 48;

      // Lado Direito: Simuladores Mobile em grelha 2x2
      const startXRight = screenW / 2;
      const cellWRight = (screenW / 2) / 2 - 20;
      const cellHRight = screenH / 2 - 20;

      // Lado Esquerdo: Painéis Admin em grelha 2x2
      const desktopWins = windows.filter(w => w.type === 'DESKTOP');
      const startXLeft = 0;
      const cellWLeft = (screenW / 2) / 2 - 20;
      const cellHLeft = screenH / 2 - 20;

      setWindows(prev => prev.map(w => {
        if (w.type === 'MOBILE') {
          const idx = mobileWins.findIndex(m => m.id === w.id);
          if (idx === -1) return w;
          const row = Math.floor(idx / 2);
          const col = idx % 2;
          const x = startXRight + col * (cellWRight + 15) + 10;
          const y = row * (cellHRight + 15) + 10;
          return {
            ...w,
            x,
            y,
            width: cellWRight,
            height: cellHRight,
            isMinimized: false,
            isMaximized: false,
          };
        } else if (w.type === 'DESKTOP') {
          const idx = desktopWins.findIndex(d => d.id === w.id);
          if (idx === -1 || idx >= 4) return w;
          const row = Math.floor(idx / 2);
          const col = idx % 2;
          const x = startXLeft + col * (cellWLeft + 15) + 10;
          const y = row * (cellHLeft + 15) + 10;
          return {
            ...w,
            x,
            y,
            width: cellWLeft,
            height: cellHLeft,
            isMinimized: false,
            isMaximized: false,
          };
        }
        return w;
      }));
    } else if (preset === 'SIDE_BY_SIDE') {
      const screenW = window.innerWidth;
      const screenH = window.innerHeight - 48;
      const startX = screenW / 2;
      const cellW = (screenW / 2) / 2 - 20;
      const cellH = screenH - 40;

      setWindows(prev => prev.map(w => {
        if (w.type !== 'MOBILE') return w;
        const idx = mobileWins.findIndex(m => m.id === w.id);
        if (idx === -1) return w;

        if (idx >= 2) {
          return { ...w, isMinimized: true };
        }

        const x = startX + idx * (cellW + 15) + 10;
        const y = 20;

        return {
          ...w,
          x,
          y,
          width: cellW,
          height: cellH,
          isMinimized: false,
          isMaximized: false,
        };
      }));
    }
  };

  // 6. Encaixar Janela Individual via Windows 11 Hover Snap
  const handleSnapWindow = (
    id: string,
    snapType:
      | 'left' | 'right' | 'top' | 'grid-tl' | 'grid-tr' | 'grid-bl' | 'grid-br'
      | 'col4-1' | 'col4-2' | 'col4-3' | 'col4-4'
      | 'col25-75-l' | 'col25-75-r'
      | 'col75-25-l' | 'col75-25-r'
      | 'col50-25-25-l' | 'col50-25-25-m' | 'col50-25-25-r'
      | 'col25-25-50-l' | 'col25-25-50-m' | 'col25-25-50-r'
  ) => {
    const screenW = window.innerWidth;
    const screenH = window.innerHeight - 48;

    setWindows(prev => prev.map(w => {
      if (w.id !== id) return w;

      let x = w.x;
      let y = w.y;
      let width = w.width;
      let height = w.height;

      if (snapType === 'left') {
        x = 0;
        y = 0;
        width = screenW / 2;
        height = screenH;
      } else if (snapType === 'right') {
        x = screenW / 2;
        y = 0;
        width = screenW / 2;
        height = screenH;
      } else if (snapType === 'top') {
        x = 0;
        y = 0;
        width = screenW;
        height = screenH;
      } else if (snapType === 'grid-tl') {
        x = 0;
        y = 0;
        width = screenW / 2;
        height = screenH / 2;
      } else if (snapType === 'grid-tr') {
        x = screenW / 2;
        y = 0;
        width = screenW / 2;
        height = screenH / 2;
      } else if (snapType === 'grid-bl') {
        x = 0;
        y = screenH / 2;
        width = screenW / 2;
        height = screenH / 2;
      } else if (snapType === 'grid-br') {
        x = screenW / 2;
        y = screenH / 2;
        width = screenW / 2;
        height = screenH / 2;
      } else if (snapType === 'col4-1') {
        x = 0;
        y = 0;
        width = screenW / 4;
        height = screenH;
      } else if (snapType === 'col4-2') {
        x = screenW / 4;
        y = 0;
        width = screenW / 4;
        height = screenH;
      } else if (snapType === 'col4-3') {
        x = (screenW / 4) * 2;
        y = 0;
        width = screenW / 4;
        height = screenH;
      } else if (snapType === 'col4-4') {
        x = (screenW / 4) * 3;
        y = 0;
        width = screenW / 4;
        height = screenH;
      } else if (snapType === 'col25-75-l') {
        x = 0;
        y = 0;
        width = screenW * 0.25;
        height = screenH;
      } else if (snapType === 'col25-75-r') {
        x = screenW * 0.25;
        y = 0;
        width = screenW * 0.75;
        height = screenH;
      } else if (snapType === 'col75-25-l') {
        x = 0;
        y = 0;
        width = screenW * 0.75;
        height = screenH;
      } else if (snapType === 'col75-25-r') {
        x = screenW * 0.75;
        y = 0;
        width = screenW * 0.25;
        height = screenH;
      } else if (snapType === 'col50-25-25-l') {
        x = 0;
        y = 0;
        width = screenW * 0.50;
        height = screenH;
      } else if (snapType === 'col50-25-25-m') {
        x = screenW * 0.50;
        y = 0;
        width = screenW * 0.25;
        height = screenH;
      } else if (snapType === 'col50-25-25-r') {
        x = screenW * 0.75;
        y = 0;
        width = screenW * 0.25;
        height = screenH;
      } else if (snapType === 'col25-25-50-l') {
        x = 0;
        y = 0;
        width = screenW * 0.25;
        height = screenH;
      } else if (snapType === 'col25-25-50-m') {
        x = screenW * 0.25;
        y = 0;
        width = screenW * 0.25;
        height = screenH;
      } else if (snapType === 'col25-25-50-r') {
        x = screenW * 0.50;
        y = 0;
        width = screenW * 0.50;
        height = screenH;
      }

      return {
        ...w,
        x,
        y,
        width,
        height,
        isMinimized: false,
        isMaximized: false,
      };
    }));
  };

  // Estilos de acordo com o Tema
  const t = {
    purple: {
      taskbar: 'bg-gradient-to-r from-[#3b0764]/95 to-[#1e1b4b]/95 border-[#4c1d95]/40 text-[#cdd6f4]',
      startMenu: 'bg-[#0f0b1a]/95 border-[#4c1d95]/60 text-[#cdd6f4]',
      window: 'bg-[#1e1e2e] border-[#313244]',
      windowActiveBorder: 'border-[#4c1d95] shadow-[0_15px_45px_-10px_rgba(76,29,149,0.4)]',
      windowInactiveBorder: 'border-[#313244] shadow-2xl',
      windowHeader: 'bg-[#0b0813] border-[#313244]/60 text-[#cdd6f4]',
      contextMenu: 'bg-[#0f0b1a]/95 border-[#4c1d95]/60 text-[#cdd6f4]',
      contextMenuHover: 'hover:bg-[#4c1d95]/30 hover:text-white',
      startBtn: 'bg-[#313244]/70 hover:bg-[#4c1d95]/50 text-[#cdd6f4]',
      searchBg: 'bg-[#181825]/85 border-[#4c1d95]/40 text-[#cdd6f4]',
    },
    dark: {
      taskbar: 'bg-gradient-to-r from-[#121212]/95 to-[#1c1c1c]/95 border-[#333333]/50 text-[#e0e0e0]',
      startMenu: 'bg-[#181818]/95 border-[#333333]/60 text-[#e0e0e0]',
      window: 'bg-[#181818] border-[#2c2c2c]',
      windowActiveBorder: 'border-[#555555] shadow-[0_15px_45px_-10px_rgba(0,0,0,0.6)]',
      windowInactiveBorder: 'border-[#2c2c2c] shadow-2xl',
      windowHeader: 'bg-[#111111] border-[#2c2c2c]/60 text-[#e0e0e0]',
      contextMenu: 'bg-[#181818]/95 border-[#333333]/60 text-[#e0e0e0]',
      contextMenuHover: 'hover:bg-[#333333] hover:text-white',
      startBtn: 'bg-[#2c2c2c]/70 hover:bg-[#444444]/50 text-[#e0e0e0]',
      searchBg: 'bg-[#222222]/85 border-[#333333]/40 text-[#e0e0e0]',
    },
    light: {
      taskbar: 'bg-gradient-to-r from-[#f1f5f9]/95 to-[#cbd5e1]/95 border-[#cbd5e1]/60 text-[#1e293b]',
      startMenu: 'bg-[#ffffff]/95 border-[#cbd5e1]/80 text-[#1e293b]',
      window: 'bg-[#ffffff] border-[#e2e8f0]',
      windowActiveBorder: 'border-[#cbd5e1] shadow-[0_15px_30px_rgba(0,0,0,0.08)]',
      windowInactiveBorder: 'border-[#e2e8f0] shadow-md',
      windowHeader: 'bg-[#f8fafc] border-[#e2e8f0]/80 text-[#1e293b]',
      contextMenu: 'bg-[#ffffff]/95 border-[#cbd5e1]/80 text-[#1e293b]',
      contextMenuHover: 'hover:bg-[#f1f5f9] hover:text-[#0f172a]',
      startBtn: 'bg-[#cbd5e1]/60 hover:bg-[#cbd5e1]/90 text-[#1e293b]',
      searchBg: 'bg-[#ffffff]/90 border-[#cbd5e1]/60 text-[#1e293b]',
    },
  }[theme];

  // Relógio do Desktop
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setDesktopTime(
        now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      );
    };
    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, []);

  // Carrega layout anterior de janelas
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ac_desktop_layout');
      let restored: DesktopWindow[] = [];
      if (saved) {
        restored = JSON.parse(saved) as DesktopWindow[];
      }

      if (!restored.some(w => w.id === 'window-telemetria')) {
        restored.push({
          id: 'window-telemetria',
          title: '📡 Central de Telemetria e Simulação',
          type: 'OFFICE',
          profile: 'OFFICE' as any,
          x: window.innerWidth - 440,
          y: 40,
          width: 400,
          height: 520,
          isMinimized: true,
          isMaximized: false,
          zIndex: 100
        });
      }

      const sanitized = restored.map((w, idx) => ({
        ...w,
        zIndex: 10 + idx,
        isMinimized: (w.type === 'MOBILE' || w.id === 'window-telemetria') ? true : (w.isMinimized ?? false)
      }));
      setWindows(sanitized);

      const lastActive = sanitized[sanitized.length - 1];
      if (lastActive && lastActive.type !== 'MOBILE') {
        setActiveWindowId(lastActive.id);
      }
    } catch { }
  }, []);

  // Salva layout automaticamente
  useEffect(() => {
    if (windows.length > 0) {
      localStorage.setItem('ac_desktop_layout', JSON.stringify(windows));
    }
  }, [windows]);

  // Taskbar: esconde com janela maximizada ativa; volta ao aproximar o mouse do rodapé
  useEffect(() => {
    const active = windows.find((w) => w.id === activeWindowId);
    const maximizedActive = !!active && active.isMaximized && !active.isMinimized;
    if (!maximizedActive) {
      setTaskbarEdgeRevealed(false);
      return;
    }

    const EDGE_PX = 10;
    const KEEP_PX = 96;

    const onMove = (e: MouseEvent) => {
      const dist = window.innerHeight - e.clientY;
      setTaskbarEdgeRevealed((prev) => (prev ? dist <= KEEP_PX : dist <= EDGE_PX));
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [windows, activeWindowId]);

  // Resolve JWT da janela e envia impersonate_login ao iframe (fallback do pós-load)
  const pushImpersonateLogin = (win: DesktopWindow, target: Window) => {
    try {
      const resolved = resolveWindowSession(win);
      if (!resolved) {
        console.warn('[pushImpersonateLogin] Sem JWT para janela', {
          winId: win.id,
          schoolId: win.schoolId,
          actorId: win.actorId,
          activeTab: win.activeTab,
        });
        return;
      }
      const perfil = String(resolved.user?.perfil || win.activeTab || win.profile || '').toUpperCase();
      target.postMessage(
        {
          type: 'impersonate_login',
          data: {
            access_token: resolved.token,
            user: resolved.user,
            redirect_url: win.customUrl || resolved.redirect,
            skip_splash: true,
            from_cockpit: true,
            // Canal push ativo por perfil; alcance do comunicado continua filtrado no backend
            promptNotification:
              perfil === 'RESPONSAVEL' ||
              perfil.includes('RESP') ||
              perfil === 'PROFESSOR' ||
              perfil.includes('PROF') ||
              perfil === 'ESCOLA' ||
              perfil === 'MUNICIPAL' ||
              ['DIRETOR', 'SECRETARIA', 'COORDENADOR', 'COORDENACAO', 'PREFEITO'].includes(perfil) ||
              perfil.includes('DIRET') ||
              perfil.includes('SECRET') ||
              perfil.includes('COORD'),
          },
        },
        window.location.origin,
      );
    } catch (err) {
      console.warn('[pushImpersonateLogin] erro', err);
    }
  };

  // Impersonate Login Handshake — usa ref para não perder iframe_ready na corrida do setState
  useEffect(() => {
    const handleReadyMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'iframe_ready') return;

      windowsRef.current.forEach((win) => {
        if (win.type === 'FOLDER' || win.type === 'OFFICE') return;
        // Suporta iframe único legado e painéis por tab
        const nodes = document.querySelectorAll(`iframe[id^="iframe-desktop-${win.id}"]`);
        nodes.forEach((node) => {
          const iframe = node as HTMLIFrameElement;
          if (iframe?.contentWindow && iframe.contentWindow === event.source) {
            pushImpersonateLogin(win, iframe.contentWindow);
          }
        });
      });
    };

    window.addEventListener('message', handleReadyMessage);
    return () => window.removeEventListener('message', handleReadyMessage);
  }, []);

  // Keyboard Navigation and Window Management (Alt+Tab, Tab focus, Arrows, Enter, Delete, F2)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 1. Alt+Tab Window Switching
      if (e.altKey && e.key === 'Tab') {
        e.preventDefault();
        if (windows.length > 0) {
          const activeIdx = windows.findIndex(w => w.id === activeWindowId);
          const nextIdx = (activeIdx + 1) % windows.length;
          const nextWin = windows[nextIdx];
          focusWindow(nextWin.id);
        }
        return;
      }

      // Se estiver editando nome, não altera a navegação dos ícones
      if (editingItemId) return;

      const visibleItems = desktopItems.filter(item => !item.parentId);

      // 2. Tab cycling para navegar na grade de ícones
      if (e.key === 'Tab') {
        e.preventDefault();
        if (visibleItems.length === 0) return;
        const currentIdx = visibleItems.findIndex(it => it.id === selectedItemId);
        const nextIdx = (currentIdx + 1) % visibleItems.length;
        setSelectedItemId(visibleItems[nextIdx].id);
        return;
      }

      // 3. Setas para navegar nos ícones do desktop
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (visibleItems.length === 0) return;
        const currentIdx = visibleItems.findIndex(it => it.id === selectedItemId);
        let nextIdx = 0;
        if (currentIdx !== -1) {
          if (e.key === 'ArrowDown') {
            nextIdx = (currentIdx + 1) % visibleItems.length;
          } else {
            nextIdx = (currentIdx - 1 + visibleItems.length) % visibleItems.length;
          }
        }
        setSelectedItemId(visibleItems[nextIdx].id);
        return;
      }

      // 4. Atalhos de Ação nos ícones selecionados (Protegendo atalhos do sistema)
      if (selectedItemId) {
        const selectedItem = visibleItems.find(it => it.id === selectedItemId);
        if (!selectedItem) return;

        if (e.key === 'Enter') {
          e.preventDefault();
          openItem(selectedItem.id, selectedItem.type, selectedItem.name, selectedItem.profileKey);
        } else if (e.key === 'F2') {
          e.preventDefault();
          if (selectedItem.type !== 'PROFILE') {
            renameDesktopItem(selectedItemId);
          }
        } else if (e.key === 'Delete') {
          e.preventDefault();
          if (selectedItem.type !== 'PROFILE') {
            deleteDesktopItem(selectedItemId);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [windows, activeWindowId, desktopItems, selectedItemId, editingItemId]);

  // Foca em uma janela e aumenta o zIndex
  const focusWindow = (id: string) => {
    setActiveWindowId(id);
    setWindows(prev => {
      const maxZIndex = prev.reduce((max, w) => Math.max(max, w.zIndex), 10);
      return prev.map(w => {
        if (w.id === id) {
          return { ...w, zIndex: maxZIndex + 1, isMinimized: false };
        }
        return w;
      });
    });
  };

  // Abre ou ativa uma janela para um ator específico (suporta múltiplas instâncias)
  const openWindowForActor = (
    profileKey: keyof typeof PROFILE_CONFIGS,
    actorId: string,
    actorName: string,
    startMaximized?: boolean,
    customUrl?: string
  ) => {
    const existing = windows.find(w => w.actorId === actorId);
    if (existing) {
      focusWindow(existing.id);
      return;
    }

    const config = PROFILE_CONFIGS[profileKey];
    const title = `${config.title} - ${actorName}`;
    const type = config.type;
    const isMobile = type === 'MOBILE';
    const width = isMobile ? 350 : 840;
    const height = isMobile ? 690 : 540;

    const offset = windows.length * 25;
    const x = Math.max(50, Math.min(window.innerWidth - width - 50, (window.innerWidth - width) / 2 + offset));
    const y = Math.max(50, Math.min(window.innerHeight - height - 80, (window.innerHeight - height - 80) / 2 + offset));

    const defaultMax = startMaximized !== undefined ? startMaximized : (type !== 'MOBILE');

    const newWindow: DesktopWindow = {
      id: `win-${profileKey.toLowerCase()}-${actorId}-${Date.now()}`,
      title,
      type,
      profile: profileKey,
      actorId,
      x,
      y,
      width,
      height,
      isMinimized: false,
      isMaximized: defaultMax,
      zIndex: 10 + windows.length,
      customUrl,
    };

    setWindows(prev => [...prev, newWindow]);
    setActiveWindowId(newWindow.id);
  };

  const openTelemetryWindow = () => {
    setWindows(prev => {
      if (prev.some(w => w.id === 'window-telemetria')) {
        return prev.map(w => w.id === 'window-telemetria' ? { ...w, isMinimized: false } : w);
      }
      return [...prev, {
        id: 'window-telemetria',
        title: '📡 Central de Telemetria e Simulação',
        type: 'OFFICE',
        profile: 'OFFICE' as any,
        x: window.innerWidth - 440,
        y: 40,
        width: 400,
        height: 520,
        isMinimized: false,
        isMaximized: false,
        zIndex: 100
      }];
    });
    setActiveWindowId('window-telemetria');
  };

  /** Central administrativa do ADMIN: /master com tabs de ambientes. */
  const openAdminCentralPanel = () => {
    try {
      const token = localStorage.getItem('ac_token');
      const user = JSON.parse(localStorage.getItem('ac_user') || 'null');
      if (token && user) {
        const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
        sessions['ADMIN'] = { access_token: token, user };
        sessions[user.id] = { access_token: token, user };
        localStorage.setItem('ac_sessions', JSON.stringify(sessions));
      }
    } catch { /* noop */ }

    const existing = windows.find((w) => w.id === 'window-admin-central');
    if (existing) {
      setWindows((prev) =>
        prev.map((w) =>
          w.id === existing.id
            ? withTabPanel({
                ...w,
                isMinimized: false,
                activeTab: 'PREFEITO',
                customUrl: '/master/dashboard',
              })
            : w,
        ),
      );
      focusWindow(existing.id);
      return;
    }

    let actorId: string | undefined;
    try {
      const user = JSON.parse(localStorage.getItem('ac_user') || 'null');
      actorId = user?.id;
    } catch { /* noop */ }

    const maxZ = windows.reduce((m, w) => Math.max(m, w.zIndex), 10);
    const newWin = withTabPanel({
      id: 'window-admin-central',
      title: 'Central do Administrador',
      type: 'DESKTOP',
      profile: 'PREFEITO',
      actorId,
      activeTab: 'PREFEITO',
      customUrl: '/master/dashboard',
      x: 48,
      y: 36,
      width: Math.min(1120, window.innerWidth - 96),
      height: Math.min(740, window.innerHeight - 110),
      isMinimized: false,
      isMaximized: true,
      zIndex: maxZ + 1,
    });
    setWindows((prev) => [...prev, newWin]);
    setActiveWindowId(newWin.id);
  };

  /** Só o botão "Abrir painel executivo": janela limpa do prefeito, sem tabs. */
  const openPrefeitoExecutivePanel = async () => {
    addLog('[Simulador] Abrindo painel executivo do prefeito...');

    const persistMunicipal = (municipal: any) => {
      if (!municipal?.access_token || !municipal?.user?.id) return null;
      const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
      const actorId = municipal.user.id as string;
      const actorName = (municipal.user.nome || 'Prefeito') as string;
      sessions[actorId] = municipal;
      sessions['MUNICIPAL'] = municipal;
      localStorage.setItem('ac_sessions', JSON.stringify(sessions));
      localStorage.setItem(
        'ac_selected_actor_prefeito',
        JSON.stringify({ userId: actorId, userName: actorName }),
      );
      return { actorId, actorName };
    };

    const findMunicipalInStorage = () => {
      try {
        const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
        const saved = JSON.parse(localStorage.getItem('ac_selected_actor_prefeito') || 'null');
        if (saved?.userId && sessions[saved.userId]?.access_token) {
          return sessions[saved.userId];
        }
        if (sessions['MUNICIPAL']?.access_token) return sessions['MUNICIPAL'];
        if (sessions['MUNICIPIO']?.access_token) return sessions['MUNICIPIO'];
        return Object.values(sessions).find((s: any) => {
          const p = (s?.user?.perfil || '').toUpperCase();
          return s?.access_token && (p === 'MUNICIPAL' || p === 'MUNICIPIO');
        }) || null;
      } catch {
        return null;
      }
    };

    let municipal = findMunicipalInStorage();

    // 1) Bypass login demo municipal
    if (!municipal?.access_token) {
      const demoEmails = [
        'gestor.bh@alunoconectado.com.br',
        'gabinete.cabo@municipio.gov.br',
      ];
      for (const email of demoEmails) {
        try {
          const { data } = await api.post('/auth/login', { email, senha: 'Admin@2026' });
          const token = data?.access_token || data?.accessToken || data?.token;
          if (token && data?.user) {
            municipal = { access_token: token, user: data.user };
            addLog(`[Simulador] Sessão municipal via login ${email}.`);
            break;
          }
        } catch {
          /* tenta próximo */
        }
      }
    }

    // 2) Via atores da escola da sessão + /simulation/start
    if (!municipal?.access_token) {
      const escolaId =
        selectedSchool?.id ||
        sessionSchools[0]?.id ||
        schoolMetadata?.citySchools?.[0]?.id;
      if (escolaId) {
        try {
          const resActors = await api.get(`/simulation/schools/${escolaId}/users`);
          const gestor = (resActors.data || []).find((a: any) => {
            const p = (a.perfil || '').toUpperCase();
            return p === 'MUNICIPAL' || p === 'MUNICIPIO';
          });
          if (gestor?.id) {
            const resSession = await api.post('/simulation/start', {
              escolaId,
              actorIds: [gestor.id],
              simulationSessionId: focusedSimSessionId(),
            });
            const bag = resSession.data?.sessions || resSession.data || {};
            municipal =
              bag['MUNICIPAL'] ||
              bag['MUNICIPIO'] ||
              bag[gestor.id] ||
              Object.values(bag).find((s: any) => s?.user?.id === gestor.id) ||
              null;
            if (municipal?.access_token) {
              addLog(`[Simulador] Sessão municipal via simulação (${gestor.nome}).`);
            }
          }
        } catch (err: any) {
          console.warn('[painel executivo] start municipal falhou', err);
        }
      }
    }

    // 3) Admin: impersonate se souber o userId salvo/mock
    if (!municipal?.access_token) {
      try {
        const saved = JSON.parse(localStorage.getItem('ac_selected_actor_prefeito') || 'null');
        const candidateId = saved?.userId;
        if (candidateId && !String(candidateId).startsWith('usr-')) {
          const { data } = await api.post(`/admin/impersonate/${candidateId}`);
          if (data?.access_token && data?.user) {
            municipal = { access_token: data.access_token, user: data.user };
            addLog('[Simulador] Sessão municipal via impersonate admin.');
          }
        }
      } catch {
        /* noop */
      }
    }

    const ensured = persistMunicipal(municipal);
    if (!ensured) {
      addLog('[Simulador] Falha: sem JWT MUNICIPAL para o painel executivo.');
      toast.error('Não foi possível abrir o painel do prefeito. Sessão municipal indisponível. Inicie a simulação da cidade ou verifique o usuário gestor (Admin@2026).');
      return;
    }

    const applyWin = (w: DesktopWindow): DesktopWindow =>
      withTabPanel({
        ...w,
        title: `Gabinete do Prefeito — ${ensured.actorName}`,
        type: 'DESKTOP',
        profile: 'PREFEITO',
        actorId: ensured.actorId,
        activeTab: undefined, // sem tabs
        customUrl: '/municipal/dashboard',
        isMinimized: false,
        isMaximized: true,
      });

    const existing = windows.find((w) => w.id === 'window-painel-executivo');
    if (existing) {
      setWindows((prev) => prev.map((w) => (w.id === existing.id ? applyWin(w) : w)));
      focusWindow(existing.id);
      return;
    }

    const maxZ = windows.reduce((m, w) => Math.max(m, w.zIndex), 10);
    const newWin: DesktopWindow = applyWin({
      id: 'window-painel-executivo',
      title: `Gabinete do Prefeito — ${ensured.actorName}`,
      type: 'DESKTOP',
      profile: 'PREFEITO',
      x: 48,
      y: 36,
      width: Math.min(1120, window.innerWidth - 96),
      height: Math.min(740, window.innerHeight - 110),
      isMinimized: false,
      isMaximized: true,
      zIndex: maxZ + 1,
    });
    setWindows((prev) => [...prev, newWin]);
    setActiveWindowId(newWin.id);
    addLog(`[Simulador] Painel executivo aberto (${ensured.actorName}).`);
  };

  const handleLaunchSchoolActor = async (schoolId: string, schoolName: string, role: string, type: 'DESKTOP' | 'MOBILE') => {
    try {
      addLog(`[Simulador] Buscando usuários para a escola: ${schoolName}...`);
      
      const resActors = await api.get(`/simulation/schools/${schoolId}/users`);
      const actorsList = resActors.data || [];
      
      const actor = actorsList.find((a: any) => {
        if (role === 'PREFEITO') return a.perfil === 'MUNICIPAL';
        if (role === 'DIRETOR') return a.perfil === 'ESCOLA' && a.cargo === 'DIRETOR';
        if (role === 'SECRETARIA') return a.perfil === 'ESCOLA' && a.cargo === 'SECRETARIA';
        if (role === 'COORDENADOR') return a.perfil === 'ESCOLA' && (a.cargo === 'COORDENACAO' || a.cargo === 'COORDENADOR');
        if (role === 'PROFESSOR') return a.perfil === 'PROFESSOR';
        if (role === 'RESPONSAVEL') return a.perfil === 'RESPONSAVEL';
        if (role === 'ALUNO') return a.perfil === 'ALUNO';
        return false;
      });

      if (!actor) {
        addLog(`[Simulador] Erro: perfil ${role} não encontrado na escola.`);
        return;
      }

      addLog(`[Simulador] Iniciando sessão para ${actor.nome} (${role})...`);
      
      const resSession = await api.post('/simulation/start', {
        escolaId: schoolId,
        actorIds: [actor.id],
        simulationSessionId: focusedSimSessionId(),
      });

      const sessionKeyMap: Record<string, string> = {
        PREFEITO: 'MUNICIPAL',
        DIRETOR: 'ESCOLA_DIRETOR',
        SECRETARIA: 'ESCOLA_SECRETARIA',
        COORDENADOR: 'ESCOLA_COORDENACAO',
        PROFESSOR: 'PROFESSOR',
        RESPONSAVEL: 'RESPONSAVEL',
        ALUNO: 'ALUNO'
      };

      const keyInPayload = sessionKeyMap[role] || role;
      const sessionBag = resSession.data?.sessions || resSession.data || {};
      const sessionData = sessionBag[keyInPayload] || sessionBag[actor.id];

      if (sessionData && sessionData.access_token) {
        const cidadeRaw = SS.get('sim_cidade');
        let cidadeMeta: { id?: string; nome?: string; uf?: string } = {};
        try {
          if (cidadeRaw) cidadeMeta = JSON.parse(cidadeRaw) as Cidade;
        } catch { /* noop */ }

        const schoolRole = ([
          'DIRETOR',
          'SECRETARIA',
          'COORDENADOR',
          'PROFESSOR',
          'RESPONSAVEL',
          'ALUNO',
        ].includes(role)
          ? role
          : null) as SchoolRoleTab | null;

        const tagged = {
          ...sessionData,
          schoolId,
          schoolName,
          municipioId: cidadeMeta.id || null,
          municipioNome: cidadeMeta.nome || null,
          municipioUf: cidadeMeta.uf || null,
          role,
        };

        const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
        sessions[actor.id] = tagged;
        if (schoolRole) sessions[`${schoolId}::${schoolRole}`] = tagged;
        localStorage.setItem('ac_sessions', JSON.stringify(sessions));

        addLog(`[Simulador] Ambiente ${role} aberto para ${actor.nome} (${schoolName}).`);

        const redirect = schoolRole
          ? SCHOOL_ROLE_REDIRECT[schoolRole]
          : '/admin/dashboard';
        openWindowForActor(role as any, actor.id, actor.nome, true, redirect);
      } else {
        addLog(`[Simulador] Falha ao gerar sessão JWT no backend.`);
        console.error('[handleLaunchSchoolActor] sessions payload:', sessionBag);
      }
    } catch (err: any) {
      console.error('Erro ao abrir ambiente:', err);
      addLog(`[Simulador] Erro ao carregar ambiente: ${err.message || err}`);
    }
  };

  // Rotaciona as dimensões da janela móvel preservando a proporção de tela
  const handleWindowRotate = (id: string, isLandscape: boolean) => {
    setWindows(prev => prev.map(w => {
      if (w.id === id) {
        const HEADER_FOOTER_OFFSET = 40; // 20px top + 20px bottom header/footer offsets

        if (isLandscape) {
          // De Vertical para Horizontal (Pilar para Paisagem)
          const usableH = w.height - HEADER_FOOTER_OFFSET;
          const usableW = w.width;
          const newWidth = usableH;
          const newHeight = usableW + HEADER_FOOTER_OFFSET;
          return {
            ...w,
            width: newWidth,
            height: newHeight
          };
        } else {
          // De Horizontal para Vertical (Paisagem para Pilar)
          const usableH = w.width;
          const usableW = w.height - HEADER_FOOTER_OFFSET;
          const newWidth = usableW;
          const newHeight = usableH + HEADER_FOOTER_OFFSET;
          return {
            ...w,
            width: newWidth,
            height: newHeight
          };
        }
      }
      return w;
    }));
  };

  // Subsessão por escola (flag da cidade). Cada escola = ambiente/login próprio.
  const openSchoolDesktopWindow = async (school: { id: string; nome: string; alunos?: number }) => {
    const existing = windows.find((w) => w.schoolId === school.id && w.type === 'DESKTOP');

    const cidadeRaw = SS.get('sim_cidade');
    let cidadeMeta: { id?: string; nome?: string; uf?: string } = {};
    try {
      if (cidadeRaw) cidadeMeta = JSON.parse(cidadeRaw) as Cidade;
    } catch { /* noop */ }

    const ensureSchoolSubSession = async () => {
      addLog(`[Simulador] Abrindo subsessão da escola ${school.nome}...`);

      const resActors = await api.get(`/simulation/schools/${school.id}/users`);
      const actorsList = resActors.data || [];

      const pick = (role: string) => {
        const matches = actorsList.filter((a: any) => {
          if (role === 'DIRETOR') return a.perfil === 'ESCOLA' && a.cargo === 'DIRETOR';
          if (role === 'SECRETARIA') return a.perfil === 'ESCOLA' && a.cargo === 'SECRETARIA';
          if (role === 'COORDENADOR') {
            return a.perfil === 'ESCOLA' && (a.cargo === 'COORDENACAO' || a.cargo === 'COORDENADOR');
          }
          if (role === 'PROFESSOR') return a.perfil === 'PROFESSOR';
          // Responsável: só conta com e-mail (= usuário real; id da tabela responsavel não autentica)
          if (role === 'RESPONSAVEL') return a.perfil === 'RESPONSAVEL' && !!a.email;
          if (role === 'ALUNO') return a.perfil === 'ALUNO';
          return false;
        });
        // Prefere ator fixo de simulação (UUID 00000001-…)
        return matches.find((a: any) => String(a.id).startsWith('00000001-')) || matches[0];
      };

      const diretor = pick('DIRETOR');
      const secretaria = pick('SECRETARIA');
      const coordenador = pick('COORDENADOR');
      const professor = pick('PROFESSOR');
      const aluno = pick('ALUNO');
      // Responsável de aluno da turma do professor (cadeia chamada → push)
      let responsavel = pick('RESPONSAVEL');
      try {
        const paired = await simulationApi.getPairedResponsavel(school.id, professor?.id);
        if (paired?.found && paired.responsavel?.id && paired.responsavel.email) {
          responsavel = {
            id: paired.responsavel.id,
            nome: paired.responsavel.nome,
            email: paired.responsavel.email,
            perfil: 'RESPONSAVEL',
            fotoUrl: paired.responsavel.fotoUrl,
          };
        }
      } catch (err) {
        console.warn('[openSchoolDesktopWindow] paired-responsavel', err);
      }

      if (!diretor) {
        addLog(`[Simulador] Erro: diretor não encontrado em ${school.nome}.`);
        return null;
      }

      const actorIds = [
        diretor.id,
        secretaria?.id,
        coordenador?.id,
        professor?.id,
        responsavel?.id,
        aluno?.id,
      ].filter(Boolean) as string[];

      const resSession = await api.post('/simulation/start', {
        escolaId: school.id,
        actorIds,
        simulationSessionId: focusedSimSessionId(),
      });

      const sessionBag = resSession.data?.sessions || resSession.data || {};
      const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
      const mapRole: Array<{ role: SchoolRoleTab; legacyKey: string; actor: any }> = [
        { role: 'DIRETOR', legacyKey: 'ESCOLA_DIRETOR', actor: diretor },
        { role: 'SECRETARIA', legacyKey: 'ESCOLA_SECRETARIA', actor: secretaria },
        { role: 'COORDENADOR', legacyKey: 'ESCOLA_COORDENACAO', actor: coordenador },
        { role: 'PROFESSOR', legacyKey: 'PROFESSOR', actor: professor },
        { role: 'RESPONSAVEL', legacyKey: 'RESPONSAVEL', actor: responsavel },
        { role: 'ALUNO', legacyKey: 'ALUNO', actor: aluno },
      ];

      const roleActors: NonNullable<DesktopWindow['roleActors']> = {};
      let diretorOk = false;

      for (const m of mapRole) {
        if (!m.actor) continue;
        const sessionData = sessionBag[m.legacyKey] || sessionBag[m.actor.id];
        if (!sessionData?.access_token) continue;

        // Subsessão da escola, flagada com a cidade (sessão pai)
        const tagged = {
          ...sessionData,
          schoolId: school.id,
          schoolName: school.nome,
          municipioId: cidadeMeta.id || null,
          municipioNome: cidadeMeta.nome || null,
          municipioUf: cidadeMeta.uf || null,
          citySessionId: SS.get('sim_session_id') || localStorage.getItem('ac_sim_session_id') || null,
          role: m.role,
        };

        sessions[m.actor.id] = tagged;
        sessions[`${school.id}::${m.role}`] = tagged;
        roleActors[m.role] = m.actor.id;
        if (m.role === 'DIRETOR') diretorOk = true;
      }

      localStorage.setItem('ac_sessions', JSON.stringify(sessions));

      if (!diretorOk) {
        addLog(`[Simulador] Falha: JWT do diretor não gerado para ${school.nome}.`);
        console.error('[openSchoolDesktopWindow] sessions payload:', sessionBag);
        return null;
      }

      const ambientes = Object.keys(roleActors).join(', ');
      addLog(`[Simulador] Subsessão ${school.nome} pronta [${ambientes}] · cidade ${cidadeMeta.nome || '—'}`);
      return { diretor, roleActors };
    };

    try {
      const ready = await ensureSchoolSubSession();
      if (!ready) return;

      void selectSchool({
        id: school.id,
        nome: school.nome,
        cidade: cidadeMeta.nome || '',
        uf: cidadeMeta.uf || '',
        totalAlunos: school.alunos || 0,
        totalProfessores: 0,
        isDemo: true,
      } as any);

      if (existing) {
        // Reabre ambiente: atualiza atores; remonta só se painel ainda inválido
        setWindows((prev) =>
          prev.map((w) =>
            w.id === existing.id
              ? withTabPanel({
                  ...w,
                  actorId: ready.diretor.id,
                  roleActors: ready.roleActors,
                  activeTab: 'DIRETOR',
                  customUrl: '/admin/dashboard',
                  schoolId: school.id,
                  isMinimized: false,
                })
              : w,
          ),
        );
        focusWindow(existing.id);
        addLog(`[Simulador] Subsessão ${school.nome} reativada.`);
        return;
      }

      const width = 920;
      const height = 600;
      const offset = windows.length * 25;
      const x = Math.max(50, Math.min(window.innerWidth - width - 50, (window.innerWidth - width) / 2 + offset));
      const y = Math.max(50, Math.min(window.innerHeight - height - 80, (window.innerHeight - height - 80) / 2 + offset));

      const newWindow = withTabPanel({
        id: `win-school-${school.id}`,
        title: school.nome,
        type: 'DESKTOP',
        profile: 'DIRETOR',
        actorId: ready.diretor.id,
        roleActors: ready.roleActors,
        activeTab: 'DIRETOR',
        customUrl: '/admin/dashboard',
        schoolId: school.id,
        x,
        y,
        width,
        height,
        isMinimized: false,
        isMaximized: true,
        zIndex: 10 + windows.length,
      });

      setWindows((prev) => [...prev, newWindow]);
      setActiveWindowId(newWindow.id);
      addLog(`[Simulador] Subsessão ${school.nome} aberta como ${ready.diretor.nome}.`);
    } catch (err: any) {
      console.error('Erro ao abrir escola:', err);
      addLog(`[Simulador] Falha ao autenticar escola: ${err?.message || err}`);
    }
  };

  // Abre ou ativa uma janela
  const openWindow = (
    profileOrId: string,
    customTitle?: string,
    isFolder = false,
    folderIdParam?: string,
    startMaximized?: boolean,
    customUrl?: string
  ) => {
    const activeFolderId = isFolder ? (folderIdParam || profileOrId) : undefined;
    const existing = isFolder
      ? windows.find(w => w.folderId === activeFolderId)
      : windows.find(w => w.profile === profileOrId);

    if (existing) {
      focusWindow(existing.id);
      return;
    }

    let title = '';
    let type: 'MOBILE' | 'DESKTOP' | 'FOLDER' | 'OFFICE' = 'DESKTOP';
    let profileKey: any = 'PREFEITO';
    let width = 840;
    let height = 540;

    if (isFolder) {
      title = customTitle || 'Nova Pasta';
      type = 'FOLDER';
      profileKey = 'FOLDER';
      width = 900;
      height = 580;
    } else {
      const profile = profileOrId as keyof typeof PROFILE_CONFIGS;
      const config = PROFILE_CONFIGS[profile];
      title = config.title;
      type = config.type;
      profileKey = profile;
      const isMobile = type === 'MOBILE';
      width = isMobile ? 350 : 840;
      height = isMobile ? 690 : 540;
    }

    const offset = windows.length * 25;
    const x = Math.max(50, Math.min(window.innerWidth - width - 50, (window.innerWidth - width) / 2 + offset));
    const y = Math.max(50, Math.min(window.innerHeight - height - 80, (window.innerHeight - height - 80) / 2 + offset));

    const defaultMax = startMaximized !== undefined ? startMaximized : (type !== 'MOBILE');

    const newWindow = withTabPanel({
      id: isFolder ? `win-folder-${activeFolderId}` : `win-${profileOrId.toLowerCase()}-${Date.now()}`,
      title,
      type,
      profile: profileKey,
      x,
      y,
      width,
      height,
      isMinimized: false,
      isMaximized: defaultMax,
      zIndex: 10 + windows.length,
      folderId: activeFolderId,
      activeTab: (type === 'DESKTOP' && (profileKey === 'PREFEITO' || profileKey === 'DIRETOR' || profileKey === 'SECRETARIA' || profileKey === 'COORDENADOR')) ? profileKey : undefined,
      customUrl,
    });

    setWindows(prev => [...prev, newWindow]);
    setActiveWindowId(newWindow.id);
  };

  // Abre janela do ONLYOFFICE Docs
  const openOfficeWindow = (fileId: string, fileName: string, fileUrl: string) => {
    const existing = windows.find(w => w.officeFileId === fileId);
    if (existing) {
      focusWindow(existing.id);
      return;
    }

    const width = 1040;
    const height = 680;
    const offset = windows.length * 25;
    const x = Math.max(50, Math.min(window.innerWidth - width - 50, (window.innerWidth - width) / 2 + offset));
    const y = Math.max(50, Math.min(window.innerHeight - height - 80, (window.innerHeight - height - 80) / 2 + offset));

    const newWindow: DesktopWindow = {
      id: `win-office-${fileId}-${Date.now()}`,
      title: fileName,
      type: 'OFFICE',
      profile: 'OFFICE',
      x,
      y,
      width,
      height,
      isMinimized: false,
      isMaximized: false,
      zIndex: 10 + windows.length,
      officeFileId: fileId,
      officeFileName: fileName,
      officeFileUrl: fileUrl,
    };

    setWindows(prev => [...prev, newWindow]);
    setActiveWindowId(newWindow.id);
  };

  // Minimiza uma janela
  const minimizeWindow = (id: string) => {
    setWindows(prev => prev.map(w => {
      if (w.id === id) {
        return { ...w, isMinimized: true };
      }
      return w;
    }));
    setActiveWindowId(prev => (prev === id ? null : prev));
  };

  // Maximiza ou restaura
  const toggleMaximizeWindow = (id: string) => {
    setWindows(prev => prev.map(w => {
      if (w.id === id) {
        return { ...w, isMaximized: !w.isMaximized };
      }
      return w;
    }));
  };

  // Fecha a janela. MOBILE: remove de verdade (ícones 1–3 ficam fixos na taskbar).
  const closeWindow = (id: string) => {
    setWindows(prev => prev.filter(w => w.id !== id));
    setActiveWindowId(prev => (prev === id ? null : prev));
  };

  /** Toggle Celular 1/2/3 — ícones sempre fixos; fechar mata a janela. */
  const togglePhoneSlot = (slot: PhoneSlotId, profile: PhoneSlotProfile, schoolId?: string) => {
    const existing = windows.find((w) => w.type === 'MOBILE' && w.profile === profile);
    if (existing) {
      if (!existing.isMinimized && (!schoolId || existing.schoolId === schoolId)) {
        closeWindow(existing.id);
        return;
      }
      // Reabre / foca; se veio da escola, garante schoolId
      setWindows((prev) =>
        prev.map((w) =>
          w.id === existing.id
            ? { ...w, isMinimized: false, schoolId: schoolId || w.schoolId }
            : w,
        ),
      );
      focusWindow(existing.id);
      return;
    }

    const config = PROFILE_CONFIGS[profile];
    const offset = (slot - 1) * 30;
    const width = 350;
    const height = 690;
    const x = Math.max(40, window.innerWidth - width - 80 - offset);
    const y = Math.max(40, 60 + (slot - 1) * 40);
    const maxZ = windows.reduce((m, w) => Math.max(m, w.zIndex), 10);
    const newWin: DesktopWindow = {
      id: `win-${profile.toLowerCase()}-${Date.now()}`,
      title: schoolId ? `${config.title} · Escola` : config.title,
      type: 'MOBILE',
      profile,
      x,
      y,
      width,
      height,
      isMinimized: false,
      isMaximized: false,
      zIndex: maxZ + 1,
      schoolId,
    };
    setWindows((prev) => [...prev, newWin]);
    setActiveWindowId(newWin.id);
  };

  // Drag handler da janela com suporte a Snapping (Encaixe de bordas)
  const handleDragStart = (id: string, e: React.MouseEvent) => {
    focusWindow(id);
    const win = windows.find(w => w.id === id);
    if (!win || win.isMaximized) return;

    e.preventDefault();
    setDraggingId(id);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialX = win.x;
    const initialY = win.y;

    let currentSnap: 'left' | 'right' | 'top' | null = null;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      // Detecção de zona de encaixe (snapping) nas bordas
      const cursorX = moveEvent.clientX;
      const cursorY = moveEvent.clientY;

      let newSnap: 'left' | 'right' | 'top' | null = null;
      if (cursorX < 20) {
        newSnap = 'left';
      } else if (cursorX > window.innerWidth - 20) {
        newSnap = 'right';
      } else if (cursorY < 20) {
        newSnap = 'top';
      }

      if (newSnap !== currentSnap) {
        currentSnap = newSnap;
        setSnapPreview(newSnap);
      }

      setWindows(prev => prev.map(w => {
        if (w.id === id) {
          const nextX = Math.max(0, Math.min(window.innerWidth - 100, initialX + deltaX));
          const nextY = Math.max(0, Math.min(window.innerHeight - 80, initialY + deltaY));
          return { ...w, x: nextX, y: nextY };
        }
        return w;
      }));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      setDraggingId(null);
      setSnapPreview(null);

      if (currentSnap) {
        setWindows(prev => prev.map(w => {
          if (w.id === id) {
            let x = w.x;
            let y = w.y;
            let width = w.width;
            let height = w.height;
            let isMaximized = w.isMaximized;

            if (currentSnap === 'left') {
              x = 0;
              y = 0;
              width = window.innerWidth / 2;
              height = window.innerHeight - 48; // Desconto da taskbar
            } else if (currentSnap === 'right') {
              x = window.innerWidth / 2;
              y = 0;
              width = window.innerWidth / 2;
              height = window.innerHeight - 48;
            } else if (currentSnap === 'top') {
              isMaximized = true;
            }

            return {
              ...w,
              x,
              y,
              width,
              height,
              isMaximized,
            };
          }
          return w;
        }));
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Resize handler da janela
  const handleResizeStart = (id: string, direction: 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'se' | 'sw', e: React.MouseEvent) => {
    focusWindow(id);
    const win = windows.find(w => w.id === id);
    if (!win || win.isMaximized) return;

    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const initialWidth = win.width;
    const initialHeight = win.height;
    const initialX = win.x;
    const initialY = win.y;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      setWindows(prev => prev.map(w => {
        if (w.id === id) {
          const minW = w.type === 'MOBILE' ? 340 : 450;
          const minH = w.type === 'MOBILE' ? 680 : 350;

          let newX = w.x;
          let newY = w.y;
          let newWidth = w.width;
          let newHeight = w.height;

          if (w.type === 'MOBILE') {
            const R = 330 / 770; // 0.428571

            if (direction === 'e' || direction === 's' || direction === 'se') {
              newWidth = Math.max(minW, initialWidth + (direction === 's' ? deltaY * R : deltaX));
              newHeight = newWidth / R;
            } else if (direction === 'w' || direction === 'sw') {
              newWidth = Math.max(minW, initialWidth - (direction === 'sw' ? deltaY * R : deltaX));
              newHeight = newWidth / R;
              newX = initialX + (initialWidth - newWidth);
            } else if (direction === 'n' || direction === 'ne') {
              newHeight = Math.max(minH, initialHeight - (direction === 'ne' ? deltaX / R : deltaY));
              newWidth = newHeight * R;
              newY = initialY + (initialHeight - newHeight);
            } else if (direction === 'nw') {
              newWidth = Math.max(minW, initialWidth - deltaX);
              newHeight = newWidth / R;
              newX = initialX + (initialWidth - newWidth);
              newY = initialY + (initialHeight - newHeight);
            }
          } else {
            // Janela normal (redimensionamento livre)
            if (direction.includes('e')) {
              newWidth = Math.max(minW, initialWidth + deltaX);
            }
            if (direction.includes('w')) {
              const potentialWidth = initialWidth - deltaX;
              if (potentialWidth >= minW) {
                newWidth = potentialWidth;
                newX = initialX + deltaX;
              }
            }
            if (direction.includes('s')) {
              newHeight = Math.max(minH, initialHeight + deltaY);
            }
            if (direction.includes('n')) {
              const potentialHeight = initialHeight - deltaY;
              if (potentialHeight >= minH) {
                newHeight = potentialHeight;
                newY = initialY + deltaY;
              }
            }
          }

          return {
            ...w,
            x: newX,
            y: newY,
            width: newWidth,
            height: newHeight
          };
        }
        return w;
      }));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleMouseUp);
  };

  // Verifica colisão 2D entre ícones para aninhamento de pastas
  const checkCollision = (x1: number, y1: number, x2: number, y2: number) => {
    return Math.abs(x1 - x2) < 70 && Math.abs(y1 - y2) < 70;
  };

  // Drag handler dos Ícones
  const handleItemDragStart = (id: string, e: React.MouseEvent) => {
    if (autoArrange) return;
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const item = desktopItems.find(it => it.id === id);
    if (!item) return;

    const initialX = item.x;
    const initialY = item.y;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      setDesktopItems(prev => {
        const next = prev.map(it => {
          if (it.id === id) {
            const nextX = Math.max(10, Math.min(window.innerWidth - 130, initialX + deltaX));
            const nextY = Math.max(60, Math.min(window.innerHeight - 180, initialY + deltaY));
            return { ...it, x: nextX, y: nextY };
          }
          return it;
        });
        return next;
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      setDesktopItems(prev => {
        const dragged = prev.find(it => it.id === id);
        if (!dragged) return prev;

        // Encontra se colidiu com alguma pasta
        const targetFolder = prev.find(it => it.id !== id && it.type === 'FOLDER' && checkCollision(dragged.x, dragged.y, it.x, it.y));

        if (targetFolder) {
          // Move o item para dentro da pasta na hierarquia virtual
          const next = prev.map(it => {
            if (it.id === id) {
              return { ...it, parentId: targetFolder.id };
            }
            return it;
          });
          localStorage.setItem('ac_desktop_items', JSON.stringify(next));
          return next;
        }

        localStorage.setItem('ac_desktop_items', JSON.stringify(prev));
        return prev;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Gerenciamento de menu de contexto (clique direito)
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY
    });
  };

  // Fechar menu de contexto no clique comum
  useEffect(() => {
    const handleGlobalClick = () => {
      setContextMenu(null);
      setIconContextMenu(null);
      setSelectedItemId(null);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  const handleItemContextMenu = (e: React.MouseEvent, item: DesktopItem) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu(null);
    setIconContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      itemId: item.id,
      itemType: item.type,
      itemName: item.name,
      profileKey: item.profileKey,
    });
  };

  const openItem = (itemId: string, itemType: 'PROFILE' | 'FOLDER' | 'SHORTCUT', itemName: string, profileKey?: keyof typeof PROFILE_CONFIGS) => {
    if (itemType === 'PROFILE' && profileKey) {
      try {
        const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
        let allSessions = Object.values(sessions).filter((s: any) => s && s.user);

        // Filtro Multi-Ambiente (Multi-Ambiente Context Isolation)
        if (activeEnvironmentId !== 'env-neutro') {
          const activeEnv = environments.find(e => e.id === activeEnvironmentId);
          if (activeEnv) {
            allSessions = allSessions.filter((s: any) => {
              if (activeEnv.id_escola) {
                return s.user.escolaId === activeEnv.id_escola;
              } else if (activeEnv.id_cidade) {
                return s.user.municipioId === activeEnv.id_cidade;
              }
              return true;
            });
          }
        }

        // Filtra por ID único para evitar duplicações
        const uniqueSessions: any[] = [];
        const seenIds = new Set();
        for (const s of allSessions as any[]) {
          if (s.user && s.user.id && !seenIds.has(s.user.id)) {
            seenIds.add(s.user.id);
            uniqueSessions.push(s);
          }
        }

        // Determina a chave de busca do perfil correspondente
        let targetKey: string = profileKey;
        if (profileKey === 'PREFEITO') targetKey = 'MUNICIPAL';
        if (profileKey === 'DIRETOR') targetKey = 'ESCOLA_DIRETOR';
        if (profileKey === 'COORDENADOR') targetKey = 'ESCOLA_COORDENACAO';
        if (profileKey === 'SECRETARIA') targetKey = 'ESCOLA_SECRETARIA';

        const profileSessions = uniqueSessions.filter((s: any) => {
          let sessionKey = s.user.perfil;
          if (s.user.cargo) sessionKey = `${s.user.perfil}_${s.user.cargo}`;
          return sessionKey === targetKey;
        });

        if (profileSessions.length > 1) {
          // Mais de um ator do mesmo perfil cadastrado na simulação -> abre seletor
          setProfileSelector({ profileKey, actors: profileSessions });
          return;
        } else if (profileSessions.length === 1) {
          // Apenas um ator -> inicia diretamente passando o actorId
          openWindowForActor(profileKey, profileSessions[0].user.id, profileSessions[0].user.nome);
          return;
        }
      } catch (err) {
        console.error('Erro ao mapear atores:', err);
      }

      openWindow(profileKey);
    } else if (itemId === 'item-word') {
      openOfficeWindow('new-doc', 'Documento.docx', 'data:word');
    } else if (itemId === 'item-excel') {
      openOfficeWindow('new-cell', 'Planilha.xlsx', 'data:cell');
    } else if (itemId === 'item-powerpoint') {
      openOfficeWindow('new-slide', 'Apresentação.pptx', 'data:slide');
    } else {
      const desktopItem = desktopItems.find(it => it.id === itemId);
      openWindow(itemId, itemName, true, desktopItem?.folderId);
    }
  };

  const finishRenameItem = (itemId: string) => {
    if (!editingName.trim()) {
      setEditingItemId(null);
      return;
    }
    setDesktopItems(prev => {
      const next = prev.map(it => (it.id === itemId ? { ...it, name: editingName.trim() } : it));
      localStorage.setItem('ac_desktop_items', JSON.stringify(next));
      return next;
    });
    setEditingItemId(null);
  };

  const renameDesktopItem = (itemId: string) => {
    const item = desktopItems.find(it => it.id === itemId);
    if (!item) return;
    setEditingName(item.name);
    setEditingItemId(itemId);
  };

  const deleteDesktopItem = (itemId: string) => {
    if (itemId === 'item-cidades') {
      toast.warning('A pasta Cidades no desktop não pode ser removida.');
      return;
    }
    if (!confirm('Deseja realmente remover este atalho da área de trabalho?')) return;

    setDesktopItems(prev => {
      const next = prev.filter(it => it.id !== itemId);
      localStorage.setItem('ac_desktop_items', JSON.stringify(next));
      return next;
    });
  };

  const createFolder = () => {
    const id = `folder-${Date.now()}`;
    const name = `Nova Pasta`;
    const offset = desktopItems.length * 15;
    const newItem: DesktopItem = {
      id,
      name,
      type: 'FOLDER',
      x: Math.max(100, Math.min(window.innerWidth - 200, window.innerWidth / 3 + offset)),
      y: Math.max(150, Math.min(window.innerHeight - 200, window.innerHeight / 3 + offset)),
    };
    const nextItems = [...desktopItems, newItem];
    setDesktopItems(nextItems);
    localStorage.setItem('ac_desktop_items', JSON.stringify(nextItems));
    setEditingName(name);
    setEditingItemId(id);
  };

  const createShortcut = () => {
    const id = `shortcut-${Date.now()}`;
    const name = `Músicas`;
    const offset = desktopItems.length * 15;
    const newItem: DesktopItem = {
      id,
      name,
      type: 'SHORTCUT',
      x: Math.max(100, Math.min(window.innerWidth - 200, window.innerWidth / 3 + offset)),
      y: Math.max(150, Math.min(window.innerHeight - 200, window.innerHeight / 3 + offset)),
    };
    const nextItems = [...desktopItems, newItem];
    setDesktopItems(nextItems);
    localStorage.setItem('ac_desktop_items', JSON.stringify(nextItems));
    setEditingName(name);
    setEditingItemId(id);
  };

  const changeTheme = (newTheme: 'purple' | 'dark' | 'light') => {
    setTheme(newTheme);
    localStorage.setItem('ac_desktop_theme', newTheme);
    window.dispatchEvent(new Event('ac_theme_changed'));
  };

  const toggleTheme = () => {
    if (theme === 'purple') {
      changeTheme('dark');
    } else if (theme === 'dark') {
      changeTheme('light');
    } else {
      changeTheme('purple');
    }
  };

  const activeRoleConfig = ROLE_DEFINITIONS[activeRoleKey.toLowerCase() as keyof typeof ROLE_DEFINITIONS] || ROLE_DEFINITIONS.administrador;

  const getBackgroundStyle = () => {
    if (theme === 'light') {
      return { background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #cbd5e1 100%)' };
    }
    try {
      const fromHex = activeRoleConfig.backgrounds[0].match(/#([0-9a-fA-F]{3,6})/)?.[0] || '#0b0928';
      const viaHex = activeRoleConfig.backgrounds[1].match(/#([0-9a-fA-F]{3,6})/)?.[0] || '#21145b';
      const toHex = activeRoleConfig.backgrounds[2].match(/#([0-9a-fA-F]{3,6})/)?.[0] || '#3d2a91';
      return { background: `linear-gradient(135deg, ${fromHex} 0%, ${viaHex} 50%, ${toHex} 100%)` };
    } catch (e) {
      return { background: 'linear-gradient(135deg, #0b0928 0%, #21145b 50%, #3d2a91 100%)' };
    }
  };

  return (
    <div
      data-sim-root
      data-tour-sim="sim.root"
      onContextMenu={handleContextMenu}
      className="w-full h-full relative overflow-hidden select-none flex flex-col transition-all duration-500"
    >
      {/* Wallpaper de Fundo parametrizado por Perfil / Tema (Estilos nativos inline) */}
      <div
        className="absolute inset-0 transition-all duration-1000 z-0"
        style={getBackgroundStyle()}
      />
      <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.06)_1.5px,transparent_1.5px)] [background-size:24px_24px] pointer-events-none z-[1]" />

      {/* Central do Administrador Dashboard / Portal de Entrada */}
      <CockpitDashboardBackground
        activeRoleKey={activeRoleKey}
        theme={theme}
        onPrimaryActionClick={() => {
          const role = activeRoleKey.toLowerCase();
          if (role === 'administrador') {
            openAdminCentralPanel();
          } else if (role === 'prefeito') {
            // Isolado: só este botão — ambiente do prefeito sem tabs
            void openPrefeitoExecutivePanel();
          } else if (role === 'diretor') {
            openWindow('DIRETOR', 'Direção Escolar', false, undefined, true, '/admin/dashboard');
          } else if (role === 'coordenador') {
            openWindow('COORDENADOR', 'Coordenação Pedagógica', false, undefined, true, '/admin/dashboard');
          } else if (role === 'secretaria') {
            openWindow('SECRETARIA', 'Secretaria Escolar', false, undefined, true, '/admin/dashboard');
          } else {
            openItem('shortcut-cities', 'FOLDER', 'Cidades', undefined);
          }
        }}
        onOpenTransitionsPanel={() => setIsTransitionsPanelOpen(true)}
        onOpenTelemetryClick={openTelemetryWindow}
      >
        {activeRoleKey === 'administrador' && selectedSchool && (
          <div className="flex flex-col gap-4 w-full h-[82%] max-h-[500px] overflow-y-auto pr-2 pointer-events-auto select-text font-sans">
            {/* Top Stats Cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#1e1e2e]/85 border border-[#313244]/80 p-3 rounded-xl flex flex-col gap-1 shadow-lg backdrop-blur-sm">
                <span className="text-[9px] text-[#a6adc8] font-bold uppercase tracking-wider">Município</span>
                <span className="text-sm font-black text-white">{selectedSchool.cidade || 'Belo Horizonte'}</span>
              </div>
              <div className="bg-[#1e1e2e]/85 border border-[#313244]/80 p-3 rounded-xl flex flex-col gap-1 shadow-lg backdrop-blur-sm">
                <span className="text-[9px] text-[#a6adc8] font-bold uppercase tracking-wider">Escolas</span>
                <span className="text-sm font-black text-[#cba6f7]">{(sessionSchools.length || schoolMetadata?.citySchools?.length) || '0'}</span>
              </div>
              <div className="bg-[#1e1e2e]/85 border border-[#313244]/80 p-3 rounded-xl flex flex-col gap-1 shadow-lg backdrop-blur-sm">
                <span className="text-[9px] text-[#a6adc8] font-bold uppercase tracking-wider">Presença Geral</span>
                <span className="text-sm font-black text-[#a6e3a1]">87%</span>
              </div>
            </div>

            {/* Escola Cards Grid */}
            <div className="flex flex-col gap-2.5">
              <span className="text-[10px] text-[#a6adc8] font-bold uppercase tracking-wider">Acesso e Simulação Multiescola:</span>
              
              {(sessionSchools.length > 0 ? sessionSchools : (schoolMetadata?.citySchools || [])).map((school: any) => (
                <div key={school.id} className="bg-[#11111b]/80 border border-[#313244]/80 rounded-xl p-3.5 flex flex-col gap-3 shadow-xl backdrop-blur-md hover:border-[#cba6f7]/40 transition-all">
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col">
                      <span className="font-extrabold text-sm text-white">{school.nome}</span>
                      <span className="text-[9px] text-[#a6adc8]">Alunos Matriculados: {school.alunosCount ?? school.alunos ?? '—'}</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[8px] font-bold border ${
                      school.hasReader 
                        ? 'bg-green-500/10 text-green-400 border-green-500/20' 
                        : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                    }`}>
                      {school.hasReader ? '📡 Leitor OK' : '⚠️ Sem Leitor'}
                    </span>
                  </div>

                  {/* Role opening buttons grid */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[8px] text-[#a6adc8] font-bold uppercase tracking-wider">Abrir no Painel de Controle:</span>
                    
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { role: 'PREFEITO', label: '🏛️ Prefeito', type: 'DESKTOP' },
                        { role: 'DIRETOR', label: '🎓 Diretor', type: 'DESKTOP' },
                        { role: 'SECRETARIA', label: '📄 Secretaria', type: 'DESKTOP' },
                        { role: 'COORDENADOR', label: '👥 Coordenador', type: 'DESKTOP' },
                        { role: 'PROFESSOR', label: '👨‍🏫 Professor', type: 'MOBILE' },
                        { role: 'RESPONSAVEL', label: '👨‍👩‍👦 Responsável', type: 'MOBILE' },
                        { role: 'ALUNO', label: '👦 Aluno', type: 'MOBILE' },
                      ].map(act => (
                        <button
                          key={act.role}
                          onClick={() => handleLaunchSchoolActor(school.id, school.nome, act.role, act.type as any)}
                          className="px-2.5 py-1 rounded bg-[#313244]/60 hover:bg-[#cba6f7]/20 border border-solid border-white/5 hover:border-[#cba6f7]/30 text-[9px] font-bold text-[#cdd6f4] hover:text-white cursor-pointer active:scale-95 transition-all"
                        >
                          {act.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Prefeito: cards compactos com wrap (3–5 laterais conforme largura) */}
        {activeRoleKey === 'prefeito' && (
          <div className="flex flex-col gap-3 w-full select-text font-sans">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span
                  className="w-8 h-[2px] rounded-full"
                  style={{ background: 'linear-gradient(90deg, #d8ad54, transparent)' }}
                />
                <span className="text-[12px] text-white/70 font-extrabold uppercase tracking-[0.14em]">
                  Escolas da rede · clique para abrir
                </span>
              </div>
              <span className="text-[13px] font-black text-[#f9e2af]">
                {sessionSchools.length} unidade{sessionSchools.length !== 1 ? 's' : ''}
              </span>
            </div>

            {sessionSchools.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-4 text-[13px] text-white/50">
                Nenhuma escola carregada nesta sessão. Volte ao wizard e selecione as unidades, ou aguarde o carregamento.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2.5 content-start">
                {sessionSchools.map((school) => {
                  const isOpen = windows.some((w) => w.schoolId === school.id && !w.isMinimized);
                  const displayName = school.nome.replace(/^\[DEMO\]\s*/i, '').trim() || school.nome;
                  const fmt = (n?: number) =>
                    typeof n === 'number' ? n.toLocaleString('pt-BR') : '—';
                  return (
                    <button
                      key={school.id}
                      type="button"
                      onClick={() => { void openSchoolDesktopWindow(school); }}
                      title={school.nome}
                      className={`group text-left rounded-xl px-3 py-4 flex items-start gap-2.5 cursor-pointer transition-all active:scale-[0.98] border backdrop-blur-md w-[200px] max-w-[200px] min-w-[180px] min-h-[88px] flex-shrink-0 ${
                        isOpen
                          ? 'border-[#f9e2af]/50 bg-[#f9e2af]/12 shadow-[0_0_20px_rgba(216,173,84,0.2)]'
                          : 'border-white/10 bg-white/[0.07] hover:border-[#d8ad54]/45 hover:bg-white/[0.11]'
                      }`}
                      style={{ boxShadow: '0 8px 20px rgba(0,0,0,0.25)' }}
                    >
                      <span className="w-9 h-9 mt-0.5 rounded-lg flex items-center justify-center flex-shrink-0 border border-white/15 bg-white/10 text-white">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                          <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
                        </svg>
                      </span>
                      <div className="min-w-0 flex-1 flex flex-col gap-1.5">
                        <div className="flex items-start gap-1.5 min-w-0">
                          <span className="font-extrabold text-[12px] text-white leading-snug line-clamp-2">
                            {displayName}
                          </span>
                          {isOpen && (
                            <span className="text-[8px] font-extrabold px-1 py-px rounded bg-[#f9e2af]/25 text-[#f9e2af] flex-shrink-0 mt-0.5">
                              ON
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-1 pt-0.5 border-t border-white/10">
                          <div className="min-w-0">
                            <span className="block text-[8px] font-bold uppercase tracking-wide text-white/40 truncate">Resp.</span>
                            <span className="block text-[11px] font-extrabold text-[#f9e2af] leading-none mt-0.5">{fmt(school.responsaveis)}</span>
                          </div>
                          <div className="min-w-0">
                            <span className="block text-[8px] font-bold uppercase tracking-wide text-white/40 truncate">Alunos</span>
                            <span className="block text-[11px] font-extrabold text-white leading-none mt-0.5">{fmt(school.alunos)}</span>
                          </div>
                          <div className="min-w-0">
                            <span className="block text-[8px] font-bold uppercase tracking-wide text-white/40 truncate">Equipe</span>
                            <span className="block text-[11px] font-extrabold text-white/85 leading-none mt-0.5">{fmt(school.equipe)}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CockpitDashboardBackground>



      {/* 1. Desktop Icons Area (Ocultado temporariamente a pedido do usuário) */}
      {false && autoArrange ? (
        // Organização Automática com wrap inteligente na esquerda (Apenas itens sem parentId)
        <div className="absolute left-8 top-20 flex flex-col flex-wrap max-h-[calc(100vh-160px)] gap-6 content-start items-start z-10 p-2.5 pb-4 scrollbar-none pointer-events-auto">
          {desktopItems
            .filter(item => !item.parentId)
            .filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
            .map(item => {
              const isProfile = item.type === 'PROFILE';
              const cfg = isProfile ? PROFILE_CONFIGS[item.profileKey!] : null;
              const isOpened = isProfile
                ? windows.some(w => w.profile === item.profileKey)
                : windows.some(w => w.folderId === item.id);

              return (
                <div
                  key={item.id}
                  onDoubleClick={() => openItem(item.id, item.type, item.name, item.profileKey)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedItemId(item.id);
                  }}
                  onContextMenu={(e) => {
                    e.stopPropagation();
                    setSelectedItemId(item.id);
                    handleItemContextMenu(e, item);
                  }}
                  className={`flex flex-col items-center justify-center p-3 rounded-2xl w-40 hover:scale-105 active:scale-95 cursor-pointer transition-all duration-300 select-none text-center group border ${selectedItemId === item.id ? 'bg-white/10 border-white/20 shadow-lg scale-105' : 'border-transparent bg-transparent'
                    }`}
                >
                  <div
                    className={`w-26 h-26 rounded-3xl bg-white/5 border border-white/10 ${cfg ? cfg.shadowColor : 'group-hover:shadow-[0_0_22px_rgba(249,226,175,0.3)]'} flex items-center justify-center shadow-2xl mb-3 relative transition-all duration-300 backdrop-blur-md [&_svg]:w-11 [&_svg]:h-11`}
                    style={{
                      color: isProfile && item.profileKey
                        ? desktopProfileHex(item.profileKey)
                        : '#f9e2af',
                      // brandTick força reavaliação após Customização Visual
                      opacity: brandTick >= 0 ? 1 : 1,
                    }}
                  >
                    {item.type === 'FOLDER' ? <Folder size={44} className="text-[#f9e2af] stroke-[1.5]" /> : item.type === 'SHORTCUT' ? (
                      item.id === 'item-word' ? <FileText size={44} className="text-[#89b4fa]" /> :
                        item.id === 'item-excel' ? <FileText size={44} className="text-[#a6e3a1]" /> :
                          item.id === 'item-powerpoint' ? <FileText size={44} className="text-[#f38ba8]" /> :
                            <Compass size={44} className="text-[#a6e3a1] stroke-[1.5]" />
                    ) : cfg?.icon}
                    {isOpened && (
                      <span className="absolute bottom-2 right-2 w-3.5 h-3.5 rounded-full bg-[#a6e3a1] border border-[#0d0a15]" />
                    )}
                  </div>

                  {editingItemId === item.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => finishRenameItem(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') finishRenameItem(item.id);
                        if (e.key === 'Escape') setEditingItemId(null);
                      }}
                      className="text-[12px] font-semibold text-white bg-[#1e1e2e] border border-[#a78bfa] rounded px-1.5 py-0.5 focus:outline-none w-32 text-center"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.target.select()}
                    />
                  ) : (
                    <span className="text-[15px] font-semibold text-[#cdd6f4] tracking-wider uppercase truncate max-w-full drop-shadow-md">
                      {item.name}
                    </span>
                  )}
                </div>
              );
            })}
        </div>
      ) : false ? (
        // Organização Manual - Ícones Livres e arrastáveis
        <div className="absolute inset-0 pointer-events-none z-10">
          {desktopItems
            .filter(item => !item.parentId)
            .filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
            .map(item => {
              const isProfile = item.type === 'PROFILE';
              const cfg = isProfile ? PROFILE_CONFIGS[item.profileKey!] : null;
              const isOpened = isProfile
                ? windows.some(w => w.profile === item.profileKey)
                : windows.some(w => w.folderId === item.id);

              return (
                <div
                  key={item.id}
                  style={{ position: 'absolute', left: `${item.x}px`, top: `${item.y}px` }}
                  onMouseDown={(e) => {
                    setSelectedItemId(item.id);
                    handleItemDragStart(item.id, e);
                  }}
                  onDoubleClick={() => openItem(item.id, item.type, item.name, item.profileKey)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedItemId(item.id);
                  }}
                  onContextMenu={(e) => {
                    e.stopPropagation();
                    setSelectedItemId(item.id);
                    handleItemContextMenu(e, item);
                  }}
                  className={`flex flex-col items-center justify-center p-3 rounded-2xl w-40 hover:scale-105 active:scale-95 cursor-pointer transition-all duration-300 select-none text-center group pointer-events-auto border ${selectedItemId === item.id ? 'bg-white/10 border-white/20 shadow-lg scale-105' : 'border-transparent bg-transparent'
                    }`}
                >
                  <div
                    className={`w-26 h-26 rounded-3xl bg-white/5 border border-white/10 ${cfg ? cfg.shadowColor : 'group-hover:shadow-[0_0_22px_rgba(249,226,175,0.3)]'} flex items-center justify-center shadow-2xl mb-3 relative transition-all duration-300 backdrop-blur-md [&_svg]:w-11 [&_svg]:h-11`}
                    style={{
                      color: isProfile && item.profileKey
                        ? desktopProfileHex(item.profileKey)
                        : '#f9e2af',
                      opacity: brandTick >= 0 ? 1 : 1,
                    }}
                  >
                    {item.type === 'FOLDER' ? <Folder size={44} className="text-[#f9e2af] stroke-[1.5]" /> : item.type === 'SHORTCUT' ? (
                      item.id === 'item-word' ? <FileText size={44} className="text-[#89b4fa]" /> :
                        item.id === 'item-excel' ? <FileText size={44} className="text-[#a6e3a1]" /> :
                          item.id === 'item-powerpoint' ? <FileText size={44} className="text-[#f38ba8]" /> :
                            <Compass size={44} className="text-[#a6e3a1] stroke-[1.5]" />
                    ) : cfg?.icon}
                    {isOpened && (
                      <span className="absolute bottom-2 right-2 w-3.5 h-3.5 rounded-full bg-[#a6e3a1] border border-[#0d0a15]" />
                    )}
                  </div>

                  {editingItemId === item.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => finishRenameItem(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') finishRenameItem(item.id);
                        if (e.key === 'Escape') setEditingItemId(null);
                      }}
                      className="text-[12px] font-semibold text-white bg-[#1e1e2e] border border-[#a78bfa] rounded px-1.5 py-0.5 focus:outline-none w-32 text-center"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.target.select()}
                    />
                  ) : (
                    <span className="text-[15px] font-semibold text-[#cdd6f4] tracking-wider uppercase truncate max-w-full drop-shadow-md">
                      {item.name}
                    </span>
                  )}
                </div>
              );
            })}
        </div>
      ) : null}

      {/* Spacer para empurrar o layout e manter a flexbox */}
      <div className="flex-1 pointer-events-none" />

      {/* 2. Floating Windows Render */}
      {windows.map(win => {
        if (win.isMinimized) return null;

        const isFocused = activeWindowId === win.id;
        const isFolder = win.type === 'FOLDER';
        const config = isFolder
          ? {
            title: win.title,
            textColor: 'text-[#f9e2af]',
            icon: <Folder size={16} className="text-[#f9e2af] stroke-[1.5]" />,
          }
          : win.type === 'OFFICE' ? {
            title: win.title,
            textColor: win.title.endsWith('.xlsx') ? 'text-[#a6e3a1]' : win.title.endsWith('.pptx') ? 'text-[#f38ba8]' : 'text-[#89b4fa]',
            icon: <FileText size={16} className="stroke-[1.5]" />,
          } : PROFILE_CONFIGS[win.profile as keyof typeof PROFILE_CONFIGS];

        let windowStyle: React.CSSProperties = {};
        if (win.isMaximized) {
          if (win.type === 'MOBILE') {
            // Janela Mobile Maximizada:
            // 1. Calcula as 4 colunas de 1/4 da tela
            const colWidth = window.innerWidth / 4;
            const columnsOccupied = [false, false, false, false];

            // 2. Verifica quais colunas estão ocupadas por OUTRAS janelas visíveis
            windows.forEach(w => {
              if (w.id === win.id || w.isMinimized) return;
              if (w.isMaximized) {
                if (w.type === 'MOBILE') {
                  const col = Math.floor(w.x / colWidth);
                  if (col >= 0 && col <= 3) columnsOccupied[col] = true;
                } else {
                  // Janela desktop maximizada ocupa as colunas 0, 1 e 2 (deixando a 3 para os celulares)
                  columnsOccupied[0] = true;
                  columnsOccupied[1] = true;
                  columnsOccupied[2] = true;
                }
              } else {
                const startCol = Math.floor(w.x / colWidth);
                const endCol = Math.floor((w.x + w.width) / colWidth);
                for (let c = Math.max(0, startCol); c <= Math.min(3, endCol); c++) {
                  columnsOccupied[c] = true;
                }
              }
            });

            // 3. Escolhe a coluna da direita para a esquerda: Coluna 4 (idx 3) -> Coluna 3 (idx 2) -> Coluna 2 (idx 1) -> Coluna 1 (idx 0)
            let chosenCol = 3;
            if (columnsOccupied[3]) {
              if (!columnsOccupied[2]) chosenCol = 2;
              else if (!columnsOccupied[1]) chosenCol = 1;
              else if (!columnsOccupied[0]) chosenCol = 0;
              else chosenCol = 3; // fallback
            }

            const finalWidth = Math.min(350, colWidth);
            const colCenter = (chosenCol * colWidth) + (colWidth / 2);
            const finalLeft = colCenter - (finalWidth / 2);

            windowStyle = {
              position: 'absolute',
              top: '16px',
              left: `${finalLeft}px`,
              width: `${finalWidth}px`,
              bottom: '94px',
              zIndex: win.zIndex,
            };
          } else {
            // Janela normal maximizada: fullscreen total sem margem
            windowStyle = {
              position: 'absolute',
              top: '0px',
              left: '0px',
              right: '0px',
              bottom: '0px',
              zIndex: win.zIndex,
            };
          }
        } else {
          windowStyle = {
            position: 'absolute',
            top: `${win.y}px`,
            left: `${win.x}px`,
            width: `${win.width}px`,
            height: `${win.height}px`,
            zIndex: win.zIndex,
          };
        }

        const isMobile = win.type === 'MOBILE';

        return (
          <div
            key={win.id}
            style={windowStyle}
            onClick={() => focusWindow(win.id)}
            className={isMobile
              ? `flex flex-col bg-[#313244] rounded-[36px] border-none outline-none select-none transition-shadow duration-300`
              : `flex flex-col border ${isFocused ? t.windowActiveBorder : t.windowInactiveBorder} ${t.window} rounded-xl overflow-visible transition-shadow duration-300`}
          >
            {/* Window Header */}
            {!isMobile && (() => {
              const isSchoolWin = !!win.schoolId && win.id !== 'window-admin-central';
              const filterSchoolTabs = (ids: readonly DesktopTabId[]) =>
                DESKTOP_WINDOW_TABS.filter((t) => ids.includes(t.id));
              const schoolDesktopTabs = isSchoolWin ? filterSchoolTabs(SCHOOL_DESKTOP_TAB_IDS) : [];
              const schoolMobileTabs = isSchoolWin ? filterSchoolTabs(SCHOOL_MOBILE_TAB_IDS) : [];

              // Desktop à esquerda (todas as abas); celulares à direita como toggles
              const desktopActiveTab = (win.activeTab as DesktopTabId) || 'DIRETOR';
              // Celulares: só flags de exibição — abas PROF/RESP no header mudam o iframe desktop
              const showProf =
                !!win.showMobileProfessor || win.schoolMobileTab === 'PROFESSOR';
              const showResp =
                !!win.showMobileResponsavel || win.schoolMobileTab === 'RESPONSAVEL';

              const switchSchoolDesktopTab = (role: DesktopTabId, enterCube = false) => {
                void (async () => {
                  let nextActor =
                    role === 'PREFEITO'
                      ? undefined
                      : (win.roleActors?.[role as SchoolRoleTab] || win.actorId);
                  const nextUrl =
                    role === 'PREFEITO'
                      ? DESKTOP_TAB_REDIRECT.PREFEITO
                      : (SCHOOL_ROLE_REDIRECT[role as SchoolRoleTab] || '/admin/dashboard');

                  // Aba Prefeito: garante JWT MUNICIPAL (comunicados → responsável)
                  if (role === 'PREFEITO') {
                    try {
                      const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
                      let municipal =
                        sessions['MUNICIPAL'] ||
                        sessions['MUNICIPIO'] ||
                        Object.values(sessions).find((s: any) => {
                          const p = (s?.user?.perfil || '').toUpperCase();
                          return s?.access_token && (p === 'MUNICIPAL' || p === 'MUNICIPIO');
                        });

                      if (!(municipal as any)?.access_token && win.schoolId) {
                        try {
                          const resActors = await api.get(`/simulation/schools/${win.schoolId}/users`);
                          const gestor = (resActors.data || []).find((a: any) => {
                            const p = String(a.perfil || '').toUpperCase();
                            return p === 'MUNICIPAL' || p === 'MUNICIPIO';
                          });
                          if (gestor?.id) {
                            const resSession = await api.post('/simulation/start', {
                              escolaId: win.schoolId,
                              actorIds: [gestor.id],
                              simulationSessionId: focusedSimSessionId(),
                            });
                            const bag = resSession.data?.sessions || resSession.data || {};
                            municipal =
                              bag.MUNICIPAL ||
                              bag.MUNICIPIO ||
                              bag[gestor.id] ||
                              Object.values(bag).find((s: any) => s?.user?.id === gestor.id);
                          }
                        } catch (err) {
                          console.warn('[school tabs] municipal via escola', err);
                        }
                      }

                      if (!(municipal as any)?.access_token) {
                        const cidadeRaw = SS.get('sim_cidade');
                        let ufHint = '';
                        try {
                          ufHint = (JSON.parse(cidadeRaw || '{}') as Cidade)?.uf || '';
                        } catch { /* noop */ }
                        const demoEmails = [
                          ufHint === 'SP'
                            ? 'gestor.sp.capital@alunoconectado.com.br'
                            : 'gestor.bh@alunoconectado.com.br',
                          'gestor.bh@alunoconectado.com.br',
                          'gestor.campinas@alunoconectado.com.br',
                        ];
                        for (const email of demoEmails) {
                          try {
                            const { data } = await api.post('/auth/login', {
                              email,
                              senha: 'Admin@2026',
                            });
                            const token = data?.access_token || data?.accessToken;
                            if (token && data?.user) {
                              municipal = { access_token: token, user: data.user };
                              break;
                            }
                          } catch { /* próximo */ }
                        }
                      }

                      if ((municipal as any)?.access_token && (municipal as any)?.user?.id) {
                        const tagged = municipal as any;
                        sessions[tagged.user.id] = tagged;
                        sessions.MUNICIPAL = tagged;
                        localStorage.setItem('ac_sessions', JSON.stringify(sessions));
                        nextActor = tagged.user.id;
                        localStorage.setItem(
                          'ac_selected_actor_prefeito',
                          JSON.stringify({
                            userId: tagged.user.id,
                            userName: tagged.user.nome || 'Prefeito',
                          }),
                        );
                        addLog(`[Simulador] Aba Prefeito — ${tagged.user.nome || 'gestor municipal'}`);
                      } else {
                        addLog('[Simulador] Aba Prefeito: sem JWT municipal. Comunicados podem falhar.');
                      }
                    } catch (err) {
                      console.warn('[school tabs] PREFEITO', err);
                    }
                  } else if (win.schoolId && role in SCHOOL_ROLE_REDIRECT) {
                    // Garante JWT do cargo (PROF/RESP/ALUNO inclusive)
                    try {
                      const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
                      const scoped = sessions[`${win.schoolId}::${role}`];
                      if (scoped?.user?.id && scoped?.access_token) {
                        nextActor = scoped.user.id;
                      } else if (!nextActor || !sessions[nextActor]?.access_token) {
                        const resActors = await api.get(`/simulation/schools/${win.schoolId}/users`);
                        const actorsList = resActors.data || [];
                        const pick = actorsList.find((a: any) => {
                          if (role === 'DIRETOR') return a.perfil === 'ESCOLA' && a.cargo === 'DIRETOR';
                          if (role === 'SECRETARIA') return a.perfil === 'ESCOLA' && a.cargo === 'SECRETARIA';
                          if (role === 'COORDENADOR') {
                            return a.perfil === 'ESCOLA' && (a.cargo === 'COORDENACAO' || a.cargo === 'COORDENADOR');
                          }
                          if (role === 'PROFESSOR') return a.perfil === 'PROFESSOR';
                          if (role === 'RESPONSAVEL') return a.perfil === 'RESPONSAVEL' && !!a.email;
                          if (role === 'ALUNO') return a.perfil === 'ALUNO';
                          return false;
                        });
                        if (pick?.id) {
                          const resSession = await api.post('/simulation/start', {
                            escolaId: win.schoolId,
                            actorIds: [pick.id],
                            simulationSessionId: focusedSimSessionId(),
                          });
                          const bag = resSession.data?.sessions || {};
                          const keyMap: Record<string, string> = {
                            DIRETOR: 'ESCOLA_DIRETOR',
                            SECRETARIA: 'ESCOLA_SECRETARIA',
                            COORDENADOR: 'ESCOLA_COORDENACAO',
                            PROFESSOR: 'PROFESSOR',
                            RESPONSAVEL: 'RESPONSAVEL',
                            ALUNO: 'ALUNO',
                          };
                          const keyed = bag[keyMap[role]] || bag[pick.id];
                          if (keyed?.access_token) {
                            sessions[pick.id] = keyed;
                            sessions[`${win.schoolId}::${role}`] = keyed;
                            localStorage.setItem('ac_sessions', JSON.stringify(sessions));
                            nextActor = pick.id;
                          }
                        }
                      }
                      const byActor = nextActor ? sessions[nextActor] : null;
                      const actorName = scoped?.user?.nome || byActor?.user?.nome || role;
                      if (nextActor) {
                        localStorage.setItem(
                          `ac_selected_actor_${role.toLowerCase()}`,
                          JSON.stringify({ userId: nextActor, userName: actorName }),
                        );
                      }
                    } catch (err) {
                      console.warn('[school tabs] role session', role, err);
                    }
                  }

                  setWindows((prev) =>
                    prev.map((w) => {
                      if (w.id !== win.id) return w;
                      const roleActors = { ...(w.roleActors || {}) };
                      if (nextActor && role !== 'PREFEITO') {
                        roleActors[role as SchoolRoleTab] = nextActor;
                      }
                      return withTabPanel({
                        ...w,
                        activeTab: role,
                        actorId:
                          nextActor ||
                          (role === 'PREFEITO'
                            ? w.actorId
                            : roleActors[role as SchoolRoleTab] || w.actorId),
                        roleActors,
                        customUrl: nextUrl,
                        // Abas do header = sempre desktop (iframe). Celular só pelo toggle "Exibir".
                        showMobileProfessor: false,
                        showMobileResponsavel: false,
                        schoolMobileTab: null,
                      });
                    }),
                  );

                  if (enterCube) {
                    setTransitionRoleKeys({ current: activeRoleKey, next: role.toLowerCase() });
                    setIsTransitioning(true);
                  }
                })();
              };

              const toggleSchoolMobileDisplay = (role: 'PROFESSOR' | 'RESPONSAVEL') => {
                setWindows((prev) =>
                  prev.map((w) => {
                    if (w.id !== win.id) return w;
                    const deskTab = (w.activeTab || 'DIRETOR') as DesktopTabId;
                    const deskActor =
                      deskTab === 'PREFEITO'
                        ? w.actorId
                        : (w.roleActors?.[deskTab as SchoolRoleTab] || w.actorId);
                    const curProf = !!w.showMobileProfessor || w.schoolMobileTab === 'PROFESSOR';
                    const curResp = !!w.showMobileResponsavel || w.schoolMobileTab === 'RESPONSAVEL';
                    return {
                      ...w,
                      activeTab: deskTab,
                      actorId: deskActor,
                      customUrl:
                        deskTab === 'PREFEITO'
                          ? DESKTOP_TAB_REDIRECT.PREFEITO
                          : (SCHOOL_ROLE_REDIRECT[deskTab as SchoolRoleTab] || w.customUrl),
                      showMobileProfessor: role === 'PROFESSOR' ? !curProf : curProf,
                      showMobileResponsavel: role === 'RESPONSAVEL' ? !curResp : curResp,
                      schoolMobileTab: null,
                    };
                  }),
                );
              };

              const renderSchoolTabBtn = (
                tab: (typeof DESKTOP_WINDOW_TABS)[number],
                variant: 'desktop' | 'mobile',
              ) => {
                const isTabActive = variant === 'mobile'
                  ? (tab.id === 'PROFESSOR' ? showProf : showResp)
                  : desktopActiveTab === tab.id;
                const roleMeta = ROLES_LIST.find((r) => r.id === tab.id.toLowerCase());
                const tabName = (roleMeta?.name || tab.id).toUpperCase();
                const activeCls = variant === 'mobile'
                  ? 'bg-[#89b4fa]/25 border-[#89b4fa]/60 text-white shadow-[0_0_0_1px_rgba(137,180,250,0.35)]'
                  : 'bg-[#cba6f7]/20 border-[#cba6f7]/50 text-white shadow-[inset_0_-2px_0_0_#cba6f7]';
                return (
                  <button
                    key={tab.id}
                    type="button"
                    data-tour-sim={
                      variant === 'mobile'
                        ? `sim.mobile.${tab.id.toLowerCase()}`
                        : `sim.tab.${tab.id.toLowerCase()}`
                    }
                    onClick={() => {
                      if (variant === 'mobile') {
                        toggleSchoolMobileDisplay(tab.id as 'PROFESSOR' | 'RESPONSAVEL');
                      } else {
                        switchSchoolDesktopTab(tab.id, false);
                      }
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (variant === 'mobile') {
                        toggleSchoolMobileDisplay(tab.id as 'PROFESSOR' | 'RESPONSAVEL');
                        return;
                      }
                      switchSchoolDesktopTab(tab.id, true);
                    }}
                    className={`shrink-0 px-2 py-1 rounded-lg text-[9px] font-extrabold tracking-wide border cursor-pointer transition-all flex items-center gap-1.5 ${
                      isTabActive
                        ? activeCls
                        : 'bg-transparent border-transparent text-[#a6adc8] hover:text-white hover:bg-white/5'
                    }`}
                    title={
                      variant === 'mobile'
                        ? `${tabName} — ${isTabActive ? 'ocultar' : 'exibir'} celular (opcional)`
                        : `${tabName} — painel desktop (não abre celular)`
                    }
                  >
                    <span className="[&_svg]:w-3.5 [&_svg]:h-3.5 flex items-center opacity-90">
                      {roleMeta?.icon}
                    </span>
                    <span>{tabName}</span>
                    {variant === 'mobile' && (
                      <span className={`ml-0.5 w-1.5 h-1.5 rounded-full ${isTabActive ? 'bg-[#a6e3a1]' : 'bg-[#585b70]'}`} />
                    )}
                  </button>
                );
              };

              return (
              <div
                onMouseDown={(e) => handleDragStart(win.id, e)}
                onDoubleClick={() => toggleMaximizeWindow(win.id)}
                className={`${isSchoolWin ? 'h-10 min-h-10' : 'h-9'} border-b border-[#313244]/60 flex items-center gap-2 px-2 cursor-grab active:cursor-grabbing select-none flex-shrink-0 relative z-10 ${t.windowHeader}`}
              >
                {isSchoolWin ? (
                  <>
                    {/* Coluna esquerda: tabs desktop */}
                    <div
                      data-tour-sim="sim.tabs.desktop"
                      className="flex items-center gap-1 min-w-0 flex-1 overflow-x-auto scrollbar-none"
                      onMouseDown={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-1 p-0.5 rounded-xl bg-black/20 border border-white/5">
                        {schoolDesktopTabs.map((tab) => renderSchoolTabBtn(tab, 'desktop'))}
                      </div>
                    </div>

                    {/* Centro: título + Play/Pausa (demais controles ficam no strip) */}
                    <div
                      className="absolute left-1/2 -translate-x-1/2 max-w-[48%] px-2 flex items-center gap-1.5 justify-center min-w-0"
                      title={win.title}
                    >
                      <span className="text-[10px] font-extrabold tracking-wider uppercase text-[#cdd6f4] truncate text-center pointer-events-none shrink min-w-0 max-w-[70%]">
                        {(win.title || 'ESCOLA').replace(/^\[DEMO\]\s*/i, '').toUpperCase()}
                      </span>
                      {win.schoolId && (
                        <button
                          type="button"
                          data-tour-sim="sim.play"
                          onMouseDown={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => e.stopPropagation()}
                          onClick={() => setIsSimulationPlaying((prev) => !prev)}
                          className={`px-1.5 py-0.5 rounded-full bg-[#313244] text-[8px] font-bold uppercase tracking-wide border cursor-pointer hover:bg-[#45475a] shrink-0 ${
                            isSimulationPlaying
                              ? 'text-[#f9e2af] border-[#f9e2af]/30'
                              : 'text-[#a6e3a1] border-[#a6e3a1]/30'
                          }`}
                          title={isSimulationPlaying ? 'Pausar tempo virtual' : 'Iniciar tempo virtual'}
                        >
                          {isSimulationPlaying ? '⏸' : '▶'}
                        </button>
                      )}
                    </div>

                    {/* Coluna direita: tabs mobile (PROFESSOR / RESPONSÁVEL) + controles */}
                    <div
                      className="flex items-center gap-2 shrink-0 flex-1 justify-end"
                      onMouseDown={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}
                    >
                      <div
                        data-tour-sim="sim.tabs.mobile"
                        className="flex items-center gap-1 p-0.5 rounded-xl bg-[#89b4fa]/08 border border-[#89b4fa]/25"
                        title="Ativar/desativar celulares na janela"
                      >
                        <span className="text-[8px] font-bold uppercase tracking-wider text-[#89b4fa]/80 px-1 hidden xl:inline">
                          Exibir
                        </span>
                        {schoolMobileTabs.map((tab) => renderSchoolTabBtn(tab, 'mobile'))}
                      </div>
                      <button
                        type="button"
                        data-tour-sim="sim.help"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          void cockpitTour.startNow();
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void cockpitTour.regenerateNow();
                        }}
                        className="px-1.5 py-0.5 rounded-full bg-[#313244] text-[9px] font-bold text-[#cba6f7] border border-[#cba6f7]/35 cursor-pointer hover:bg-[#45475a] shrink-0"
                        title="Tour do simulador (clique direito = regenerar com IA)"
                      >
                        ?
                      </button>
                      {(() => {
                        const mobileRole: 'PROFESSOR' | 'RESPONSAVEL' | null =
                          win.schoolMobileTab === 'PROFESSOR' || win.showMobileProfessor
                            ? 'PROFESSOR'
                            : win.schoolMobileTab === 'RESPONSAVEL' || win.showMobileResponsavel
                              ? 'RESPONSAVEL'
                              : null;
                        if (!mobileRole || !focusedSessionId) return null;
                        const actorId =
                          win.roleActors?.[mobileRole] ||
                          (mobileRole === 'PROFESSOR' || mobileRole === 'RESPONSAVEL'
                            ? win.actorId
                            : undefined);
                        if (!actorId) return null;
                        const sess =
                          (activeSessions as any)?.[actorId] ||
                          (typeof localStorage !== 'undefined'
                            ? (() => {
                                try {
                                  const raw = localStorage.getItem('ac_sessions');
                                  return raw ? JSON.parse(raw)?.[actorId] : null;
                                } catch {
                                  return null;
                                }
                              })()
                            : null);
                        const actorNome =
                          sess?.user?.nome ||
                          (mobileRole === 'PROFESSOR' ? 'Professor' : 'Responsável');
                        return (
                          <button
                            type="button"
                            title={`QR PWA — ${actorNome}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPairingQr({
                                actorId,
                                actorNome,
                                sessionId: focusedSessionId,
                                roleLabel: mobileRole,
                              });
                            }}
                            className="px-1.5 py-0.5 rounded-full bg-[#313244] text-[#cba6f7] text-[8px] font-bold uppercase tracking-wide border border-[#cba6f7]/30 hover:bg-[#45475a] cursor-pointer"
                          >
                            QR
                          </button>
                        );
                      })()}
                      <WindowControls
                        winId={win.id}
                        isMaximized={win.isMaximized}
                        onMinimize={() => minimizeWindow(win.id)}
                        onMaximize={() => toggleMaximizeWindow(win.id)}
                        onClose={() => closeWindow(win.id)}
                        onSnapLayout={(snapType) => handleSnapWindow(win.id, snapType)}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center space-x-2 min-w-0">
                      <span
                        className={`inline-flex ${
                          win.profile && win.profile in PROFILE_CONFIGS
                            ? ''
                            : (config as { textColor?: string }).textColor || ''
                        }`}
                        style={
                          win.profile && win.profile in PROFILE_CONFIGS
                            ? {
                                color: desktopProfileHex(
                                  win.profile as keyof typeof PROFILE_CONFIGS,
                                ),
                                opacity: brandTick >= 0 ? 1 : 1,
                              }
                            : undefined
                        }
                      >
                        {win.id === 'window-telemetria' ? '📡' : config.icon}
                      </span>
                      <span className="text-[10px] font-bold tracking-wide uppercase select-none truncate max-w-[280px]" title={win.title || config.title}>
                        {win.id === 'window-telemetria' || win.title
                          ? win.title
                          : config.title}
                      </span>
                    </div>
                    <WindowControls
                      winId={win.id}
                      isMaximized={win.isMaximized}
                      onMinimize={() => minimizeWindow(win.id)}
                      onMaximize={() => toggleMaximizeWindow(win.id)}
                      onClose={() => closeWindow(win.id)}
                      onSnapLayout={(snapType) => handleSnapWindow(win.id, snapType)}
                    />
                  </>
                )}
              </div>
              );
            })()}

            {/* Expansor de telemetria (mesma fonte da janela Telemetria) */}
            {!isMobile && !!win.schoolId && win.id !== 'window-admin-central' && (
              <SchoolSimTelemetryStrip
                schoolNome={(win.title || 'ESCOLA').replace(/^\[DEMO\]\s*/i, '')}
                expanded={!!simStripExpanded[win.id]}
                onToggle={() => {
                  setSimStripExpanded((prev) => {
                    const next = !prev[win.id];
                    if (next && win.schoolId) {
                      setSimFocusSchoolId(win.schoolId);
                      void loadSchoolMetadataFor(win.schoolId);
                      void (async () => {
                        await loadTurnosForSchool(win.schoolId!);
                        await reloadDayPlan(win.schoolId!, activeShiftRef.current, simDiaSemana);
                      })();
                    }
                    return { ...prev, [win.id]: next };
                  });
                }}
                virtualTime={virtualTime}
                simulationSpeed={simulationSpeed}
                setSimulationSpeed={setSimulationSpeed}
                isSimulationPlaying={isSimulationPlaying}
                setIsSimulationPlaying={setIsSimulationPlaying}
                logs={simulationLogs}
                onClearLogs={() => setSimulationLogs([])}
                readerOnline={
                  win.schoolId in readerOnlineBySchool
                    ? readerOnlineBySchool[win.schoolId]
                    : selectedSchool?.id === win.schoolId
                      ? !!schoolMetadata?.dispositivoLeituraId
                      : null
                }
                busy={simBusy[win.id] ?? null}
                activeShift={activeShift}
                onShiftChange={(s) => {
                  if (simEngineMode === 'CHRONOS') {
                    addLog('[SYS] Pare o Chronos antes de trocar o turno.');
                    return;
                  }
                  applyShift(s, win.schoolId);
                }}
                turnosDisponiveis={turnosBySchool[win.schoolId] || []}
                eventQueue={eventQueue}
                queueControl={queueControl}
                onQueueControl={setQueueControl}
                onRunNext={runNextQueueEvent}
                onSkipNext={skipNextQueueEvent}
                simMode={
                  chronosSession?.schoolId === win.schoolId ? simEngineMode : 'COCKPIT'
                }
                chronosBusy={chronosBusy}
                chronosClock={
                  chronosSession?.schoolId === win.schoolId ? chronosSession.clock : null
                }
                chronosFase={
                  chronosSession?.schoolId === win.schoolId ? chronosSession.fase : null
                }
                chronosPaused={
                  chronosSession?.schoolId === win.schoolId ? chronosSession.paused : false
                }
                onPrepare={() => void prepareSimulationForSchool(win.schoolId!)}
                onCleanToday={() => void cleanTodayForSchool(win.schoolId!)}
                onChronosStart={() => void startChronosForSchool(win.schoolId!)}
                onChronosPauseResume={() => void pauseResumeChronos()}
                onChronosStop={() => void stopChronos()}
                onManualRfid={() =>
                  void runHeaderSimAction(
                    win.id,
                    'rfid',
                    win.schoolId!,
                    (win.title || '').replace(/^\[DEMO\]\s*/i, ''),
                  )
                }
                onManualChamada={() =>
                  void runHeaderSimAction(
                    win.id,
                    'chamada',
                    win.schoolId!,
                    (win.title || '').replace(/^\[DEMO\]\s*/i, ''),
                  )
                }
                simDateYmd={simDateYmd}
                simDiaSemana={simDiaSemana}
                onSimDateChange={(ymd) => applySimDate(ymd, win.schoolId)}
                onSimDiaChange={(dia) => applySimDia(dia, win.schoolId)}
                dayLoopEnabled={dayLoopEnabled}
                onDayLoopChange={(enabled) => {
                  setDayLoopEnabled(enabled);
                  try {
                    localStorage.setItem('ac_sim_day_loop', enabled ? '1' : '0');
                  } catch { /* noop */ }
                  addLog(
                    enabled
                      ? '[SYS] Loop dias ON — fim da Noite avança para o próximo dia letivo.'
                      : '[SYS] Loop dias OFF — fim da Noite pausa o Play.',
                  );
                }}
                dayPlan={dayPlan}
                presenceMix={presenceMix}
                onPresenceMixChange={(mix) => {
                  const next = normalizePresenceMix(mix);
                  setPresenceMix(next);
                  savePresenceMix(next);
                }}
                onVirtualTimeChange={(hm) => {
                  if (simEngineMode === 'CHRONOS') {
                    addLog('[SYS] Pare o Chronos antes de editar o relógio.');
                    return;
                  }
                  const vt = dateAtYmdHm(simDateYmdRef.current, hm);
                  virtualTimeRef.current = vt;
                  setVirtualTime(vt);
                  setIsSimulationPlaying(false);
                  lastPushedVirtualMinsRef.current = dateToMinutes(vt);
                  void pushSimClockToSessionAndEmbeds({
                    virtualEpochMs: vt.getTime(),
                    playing: false,
                  });
                  addLog(
                    `[SYS] Relógio virtual → ${String(vt.getHours()).padStart(2, '0')}:${String(vt.getMinutes()).padStart(2, '0')}:${String(vt.getSeconds()).padStart(2, '0')} (manual · pausado)`,
                  );
                }}
              />
            )}

            {/* Window Content */}
            <div className={isMobile ? "flex-1 relative" : "flex-1 overflow-hidden relative bg-[#181825]"}>
              {win.type === 'MOBILE' ? (
                <PhoneSimulatorContainer
                  device={win.profile as any}
                  actorId={win.actorId}
                  schoolId={win.schoolId}
                  onClose={() => closeWindow(win.id)}
                  onMinimize={() => minimizeWindow(win.id)}
                  onMaximize={() => toggleMaximizeWindow(win.id)}
                  isMaximized={win.isMaximized}
                  onSnapLayout={(snapType) => handleSnapWindow(win.id, snapType)}
                  onDragStart={(e) => handleDragStart(win.id, e)}
                  onRotate={(isLandscape) => handleWindowRotate(win.id, isLandscape)}
                  isBackendOnline={isBackendOnline}
                />
              ) : win.id === 'window-telemetria' ? (
                <div className="flex flex-col h-full bg-[#1e1e2e]/95 text-xs text-[#cdd6f4] p-4 select-text relative font-sans">
                  {/* Sessão municipal em foco (alinhada ao painel SSE) */}
                  <div className="bg-[#11111b]/60 border border-[#313244] p-2.5 rounded-xl flex flex-col gap-1.5 mb-3">
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-[#a6adc8] font-bold text-[9px] uppercase tracking-wider">Sessão em foco</span>
                      {focusedSession ? (
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${
                          focusedSession.status === 'RUNNING'
                            ? 'text-[#a6e3a1] border-[#a6e3a1]/40 bg-[#a6e3a1]/10'
                            : focusedSession.status === 'PAUSED'
                              ? 'text-[#89b4fa] border-[#89b4fa]/40 bg-[#89b4fa]/10'
                              : 'text-[#f9e2af] border-[#f9e2af]/40 bg-[#f9e2af]/10'
                        }`}>
                          {focusedSession.status}
                        </span>
                      ) : (
                        <span className="text-[9px] text-red-400 font-bold">SEM FOCO</span>
                      )}
                    </div>
                    {sessionChoices.length > 1 ? (
                      <select
                        value={focusedSessionId || ''}
                        onChange={(e) => {
                          const next = sessionChoices.find((s) => s.id === e.target.value) || null;
                          setFocusedSession(next);
                          if (next) addLog(`[SYS] Foco → ${next.municipioNome} (${next.status}) #${next.id.slice(0, 8)}`);
                        }}
                        className="w-full bg-[#181825] border border-[#313244] rounded-lg text-[10px] text-[#cdd6f4] px-2 py-1.5 outline-none"
                      >
                        <option value="">Selecionar sessão…</option>
                        {sessionChoices.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.municipioNome}/{s.municipioUf} · {s.status} · {s.escolaIds?.length || 0} esc.
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-[11px] font-bold text-[#cdd6f4] truncate">
                        {focusedSession
                          ? `${focusedSession.municipioNome} — ${focusedSession.municipioUf}`
                          : sessionChoices.length === 0
                            ? 'Nenhuma sessão ativa no backend'
                            : 'Aguardando sincronização…'}
                      </div>
                    )}
                    {focusedSession && (
                      <div className="text-[9px] text-[#585b70] font-mono">
                        #{focusedSession.id.slice(0, 8)} · {focusedSession.escolaIds?.length || 0} escola(s) · {focusedSession.actorIds?.length || 0} ator(es)
                      </div>
                    )}
                  </div>

                  {/* Telemetry Console inner layout */}
                  <div className="bg-[#11111b]/50 border border-[#313244] p-3 rounded-xl flex flex-col gap-2 mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[#a6adc8] font-bold text-[9px] uppercase tracking-wider">Tempo Virtual Acelerado</span>
                      <div className="flex items-center gap-1.5 bg-[#4c1d95]/20 border border-[#cba6f7]/30 px-2 py-0.5 rounded text-[#cba6f7] font-mono font-bold text-[10px]">
                        {simulationSpeed}x
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 mt-1">
                      <div className="text-xl font-mono font-bold text-white tracking-widest bg-[#181825] px-3 py-1.5 rounded-lg border border-[#313244]">
                        {String(virtualTime.getHours()).padStart(2, '0')}:
                        {String(virtualTime.getMinutes()).padStart(2, '0')}:
                        {String(virtualTime.getSeconds()).padStart(2, '0')}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => setIsSimulationPlaying(prev => !prev)}
                          className={`px-3 py-1.5 rounded-lg font-bold cursor-pointer transition-all border border-solid ${
                            isSimulationPlaying
                              ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                              : 'bg-green-500/20 text-green-400 border-green-500/30'
                          }`}
                        >
                          {isSimulationPlaying ? '⏸ Pausar' : '▶ Iniciar'}
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1 mt-1">
                      <div className="flex justify-between text-[9px] text-[#a6adc8]">
                        <span>1x (Real)</span>
                        <span>100x</span>
                        <span>240x (1 dia = 6m)</span>
                      </div>
                      <input
                        type="range"
                        min={1}
                        max={240}
                        value={simulationSpeed}
                        onChange={(e) => setSimulationSpeed(Number(e.target.value))}
                        className="w-full h-1 bg-[#313244] rounded-lg appearance-none cursor-pointer accent-[#cba6f7]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 bg-[#11111b]/30 p-2.5 rounded-xl border border-[#313244]/80 text-[10px] mb-3 font-mono">
                    <div className="flex flex-col">
                      <span className="opacity-60 text-[8px] uppercase tracking-wider">Escola Selecionada</span>
                      <span className="font-bold text-[#cdd6f4] truncate">{selectedSchool?.nome || 'Nenhuma'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="opacity-60 text-[8px] uppercase tracking-wider">Leitor Portaria</span>
                      <span className={`font-bold ${schoolMetadata?.dispositivoLeituraId ? 'text-[#a6e3a1]' : 'text-red-400'}`}>
                        {schoolMetadata?.dispositivoLeituraId ? 'ONLINE' : 'NENHUM'}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5 flex-1 min-h-0">
                    <div className="flex justify-between items-center text-[9px] text-[#a6adc8] uppercase tracking-wider font-bold">
                      <span>Log de Atividades E2E</span>
                      <button
                        onClick={() => setSimulationLogs([])}
                        className="text-[9px] text-[#a6adc8]/60 hover:text-[#cdd6f4] bg-transparent border border-none cursor-pointer"
                      >
                        Limpar
                      </button>
                    </div>
                    <div
                      className="bg-[#11111b] border border-[#313244] rounded-xl p-2.5 flex-1 overflow-y-auto font-mono text-[9px] text-[#a6adc8] flex flex-col gap-1.5 scrollbar-thin select-text"
                    >
                      {simulationLogs.length === 0 ? (
                        <div className="opacity-40 italic text-center py-4">Sem atividades de simulação no momento.</div>
                      ) : (
                        simulationLogs.map((log, idx) => (
                          <div
                            key={idx}
                            className={
                              log.includes('[RFID]') ? 'text-[#cba6f7]' :
                              log.includes('[Chamada]') ? 'text-[#89b4fa]' :
                              log.includes('[Comunicado]') ? 'text-[#f9e2af]' : 'text-[#a6adc8]'
                            }
                          >
                            {log}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ) : win.type === 'OFFICE' ? (
                (() => {
                  const activeSimUser = (() => {
                    try {
                      const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
                      // Tenta resgatar o usuário do primeiro simulador de perfil aberto/ativo
                      const profileWin = windows.find(w => w.type !== 'FOLDER' && w.type !== 'OFFICE' && w.actorId && sessions[w.actorId]);
                      if (profileWin?.actorId && sessions[profileWin.actorId]) {
                        return sessions[profileWin.actorId].user;
                      }
                      const adminUser = JSON.parse(localStorage.getItem('ac_user') || 'null');
                      if (adminUser) return adminUser;
                    } catch { }
                    return null;
                  })();

                  return (
                    <OfficeEditorWindow
                      fileId={win.officeFileId!}
                      fileName={win.officeFileName!}
                      fileUrl={win.officeFileUrl!}
                      user={activeSimUser ? { id: activeSimUser.id, name: activeSimUser.nome } : undefined}
                    />
                  );
                })()
              ) : (
                <div className="flex flex-col w-full h-full">
                  {win.activeTab && (() => {
                    // Janela de escola: tabs nomeadas + celulares ficam no HEADER da janela
                    if (win.schoolId && win.id !== 'window-admin-central') {
                      return null;
                    }

                    const visibleTabs = getVisibleDesktopTabs(activeRoleKey);
                    // Folhas / papel sem troca: janela única sem barra de tabs
                    if (visibleTabs.length <= 1) return null;

                    const activeTabMeta = DESKTOP_WINDOW_TABS.find((t) => t.id === win.activeTab);
                    const roleLevel = roleKeyToHierarchyLevel(activeRoleKey);
                    const needsSchoolPicker =
                      !!activeTabMeta?.needsSchool &&
                      roleLevel <= 1 && // admin ou prefeito
                      !(win.schoolId || selectedSchool?.id);

                    const citySchools: Array<{ id: string; nome: string }> =
                      (sessionSchools.length > 0 ? sessionSchools : null) ||
                      schoolMetadata?.citySchools ||
                      (selectedSchool ? [{ id: selectedSchool.id, nome: selectedSchool.nome }] : []);


                    return (
                      <>
                        <div className="flex items-center bg-[#181825] border-b border-[#313244] px-2 py-1 gap-1 flex-wrap">
                          {visibleTabs.map((tab) => {
                            const isTabActive = win.activeTab === tab.id;
                            // Admin na aba Prefeito com /master: destaca como hub admin
                            const isAdminHub =
                              win.id === 'window-admin-central' &&
                              tab.id === 'PREFEITO' &&
                              !!win.customUrl?.startsWith('/master');
                            return (
                              <button
                                key={tab.id}
                                type="button"
                                onClick={() => {
                                  void (async () => {
                                    const roleLevelClick = roleKeyToHierarchyLevel(activeRoleKey);
                                    const needsSchool = !!tab.needsSchool;
                                    const nextSchoolId = win.schoolId || selectedSchool?.id;
                                    let nextCustomUrl = DESKTOP_TAB_REDIRECT[tab.id];

                                    // Janela admin: Prefeito/Admin alterna hub master ↔ municipal
                                    if (win.id === 'window-admin-central' && tab.id === 'PREFEITO') {
                                      if (win.customUrl?.startsWith('/municipal')) {
                                        nextCustomUrl = '/master/dashboard';
                                      } else if (win.customUrl?.startsWith('/master')) {
                                        nextCustomUrl = '/municipal/dashboard';
                                      } else {
                                        nextCustomUrl = '/municipal/dashboard';
                                      }
                                    }

                                    if (needsSchool && roleLevelClick <= 1 && !nextSchoolId) {
                                      setWindows((prev) =>
                                        prev.map((w) =>
                                          w.id === win.id
                                            ? withTabPanel({
                                                ...w,
                                                activeTab: tab.id,
                                                customUrl: nextCustomUrl,
                                              })
                                            : w,
                                        ),
                                      );
                                      return;
                                    }

                                    let nextActor =
                                      (needsSchool && nextSchoolId
                                        ? win.roleActors?.[tab.id as SchoolRoleTab]
                                        : undefined) || win.actorId;

                                    // Garante JWT da aba (municipal / escola) — não sobrescreve hub /master
                                    try {
                                      const sessions = JSON.parse(localStorage.getItem('ac_sessions') || '{}');
                                      if (tab.id === 'PREFEITO' && nextCustomUrl.startsWith('/master')) {
                                        try {
                                          const adminUser = JSON.parse(localStorage.getItem('ac_user') || 'null');
                                          if (adminUser?.id) nextActor = adminUser.id;
                                        } catch { /* noop */ }
                                      } else if (tab.id === 'PREFEITO' && nextCustomUrl.startsWith('/municipal')) {
                                        const municipal =
                                          sessions['MUNICIPAL'] ||
                                          sessions['MUNICIPIO'] ||
                                          Object.values(sessions).find((s: any) => {
                                            const p = (s?.user?.perfil || '').toUpperCase();
                                            return s?.access_token && (p === 'MUNICIPAL' || p === 'MUNICIPIO');
                                          });
                                        if (municipal?.user?.id) {
                                          nextActor = municipal.user.id;
                                          sessions[municipal.user.id] = municipal;
                                          sessions['MUNICIPAL'] = municipal;
                                          localStorage.setItem('ac_sessions', JSON.stringify(sessions));
                                        } else {
                                          // tenta login demo municipal
                                          try {
                                            const { data } = await api.post('/auth/login', {
                                              email: 'gestor.bh@alunoconectado.com.br',
                                              senha: 'Admin@2026',
                                            });
                                            const token = data?.access_token || data?.accessToken;
                                            if (token && data?.user) {
                                              const tagged = { access_token: token, user: data.user };
                                              sessions[data.user.id] = tagged;
                                              sessions['MUNICIPAL'] = tagged;
                                              localStorage.setItem('ac_sessions', JSON.stringify(sessions));
                                              nextActor = data.user.id;
                                            }
                                          } catch { /* noop */ }
                                        }
                                      } else if (needsSchool && nextSchoolId) {
                                        const scoped = sessions[`${nextSchoolId}::${tab.id}`];
                                        if (scoped?.user?.id) {
                                          nextActor = scoped.user.id;
                                        } else {
                                          // Abre/reusa JWT da escola para o cargo
                                          try {
                                            const resActors = await api.get(`/simulation/schools/${nextSchoolId}/users`);
                                            const actorsList = resActors.data || [];
                                            const pick = actorsList.find((a: any) => {
                                              if (tab.id === 'DIRETOR') return a.perfil === 'ESCOLA' && a.cargo === 'DIRETOR';
                                              if (tab.id === 'SECRETARIA') return a.perfil === 'ESCOLA' && a.cargo === 'SECRETARIA';
                                              if (tab.id === 'COORDENADOR') {
                                                return a.perfil === 'ESCOLA' && (a.cargo === 'COORDENACAO' || a.cargo === 'COORDENADOR');
                                              }
                                              if (tab.id === 'PROFESSOR') return a.perfil === 'PROFESSOR';
                                              if (tab.id === 'RESPONSAVEL') return a.perfil === 'RESPONSAVEL' && !!a.email;
                                              if (tab.id === 'ALUNO') return a.perfil === 'ALUNO';
                                              return false;
                                            });
                                            if (pick?.id) {
                                              const resSession = await api.post('/simulation/start', {
                                                escolaId: nextSchoolId,
                                                actorIds: [pick.id],
                                                simulationSessionId: focusedSimSessionId(),
                                              });
                                              const bag = resSession.data?.sessions || {};
                                              const keyMap: Record<string, string> = {
                                                DIRETOR: 'ESCOLA_DIRETOR',
                                                SECRETARIA: 'ESCOLA_SECRETARIA',
                                                COORDENADOR: 'ESCOLA_COORDENACAO',
                                                PROFESSOR: 'PROFESSOR',
                                                RESPONSAVEL: 'RESPONSAVEL',
                                                ALUNO: 'ALUNO',
                                              };
                                              const keyed = bag[keyMap[tab.id]] || bag[pick.id];
                                              if (keyed?.access_token) {
                                                sessions[pick.id] = keyed;
                                                sessions[`${nextSchoolId}::${tab.id}`] = keyed;
                                                localStorage.setItem('ac_sessions', JSON.stringify(sessions));
                                                nextActor = pick.id;
                                              }
                                            }
                                          } catch (err) {
                                            console.warn('[admin tabs] falha ao preparar sessão', err);
                                          }
                                        }
                                      }
                                    } catch { /* noop */ }

                                    setWindows((prev) =>
                                      prev.map((w) => {
                                        if (w.id !== win.id) return w;
                                        const roleActors = { ...(w.roleActors || {}) };
                                        if (nextActor && tab.id !== 'PREFEITO') {
                                          roleActors[tab.id as SchoolRoleTab] = nextActor;
                                        }
                                        return withTabPanel({
                                          ...w,
                                          activeTab: tab.id,
                                          actorId: nextActor || w.actorId,
                                          schoolId: needsSchool ? nextSchoolId : w.schoolId,
                                          roleActors,
                                          customUrl: nextCustomUrl,
                                        });
                                      }),
                                    );
                                  })();
                                }}
                                className={`px-3 py-1.5 rounded-lg text-[9px] font-extrabold transition-all border-none cursor-pointer ${
                                  isTabActive || isAdminHub
                                    ? 'bg-[#cba6f7] text-[#11111b] scale-105'
                                    : 'bg-[#313244]/55 text-[#cdd6f4] hover:bg-[#313244]/80'
                                }`}
                              >
                                {win.id === 'window-admin-central' && tab.id === 'PREFEITO' && win.customUrl?.startsWith('/master')
                                  ? '🏛️ Admin'
                                  : tab.label}
                              </button>
                            );
                          })}
                        </div>
                        {needsSchoolPicker && (
                          <div className="flex flex-col gap-2 p-4 bg-[#11111b] border-b border-[#313244] text-[#cdd6f4]">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#a6adc8]">
                              Selecione uma escola para visualizar as abas do domínio escolar
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {citySchools.length === 0 ? (
                                <span className="text-[11px] text-[#64748b]">
                                  Nenhuma escola disponível nesta sessão. Use o wizard ou o painel do administrador.
                                </span>
                              ) : (
                                citySchools.map((school) => (
                                  <button
                                    key={school.id}
                                    type="button"
                                    onClick={() => {
                                      setWindows((prev) =>
                                        prev.map((w) =>
                                          w.id === win.id ? { ...w, schoolId: school.id } : w,
                                        ),
                                      );
                                    }}
                                    className="px-3 py-1.5 rounded-lg text-[10px] font-bold border border-[#cba6f7]/30 bg-[#cba6f7]/10 text-[#cba6f7] cursor-pointer hover:bg-[#cba6f7]/20"
                                  >
                                    {school.nome}
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {win.id === 'window-telemetria' ? (
                    <div className="flex flex-col h-full bg-[#1e1e2e]/95 text-xs text-[#cdd6f4] p-4 select-text relative font-sans">
                      {/* Telemetry Console inner layout */}
                      <div className="bg-[#11111b]/50 border border-[#313244] p-3 rounded-xl flex flex-col gap-2 mb-3">
                        <div className="flex justify-between items-center">
                          <span className="text-[#a6adc8] font-bold text-[9px] uppercase tracking-wider">Tempo Virtual Acelerado</span>
                          <div className="flex items-center gap-1.5 bg-[#4c1d95]/20 border border-[#cba6f7]/30 px-2 py-0.5 rounded text-[#cba6f7] font-mono font-bold text-[10px]">
                            {simulationSpeed}x
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 mt-1">
                          <div className="text-xl font-mono font-bold text-white tracking-widest bg-[#181825] px-3 py-1.5 rounded-lg border border-[#313244]">
                            {String(virtualTime.getHours()).padStart(2, '0')}:
                            {String(virtualTime.getMinutes()).padStart(2, '0')}:
                            {String(virtualTime.getSeconds()).padStart(2, '0')}
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => setIsSimulationPlaying(prev => !prev)}
                              className={`px-3 py-1.5 rounded-lg font-bold cursor-pointer transition-all border border-solid ${
                                isSimulationPlaying
                                  ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                                  : 'bg-green-500/20 text-green-400 border-green-500/30'
                              }`}
                            >
                              {isSimulationPlaying ? '⏸ Pausar' : '▶ Iniciar'}
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-col gap-1 mt-1">
                          <div className="flex justify-between text-[9px] text-[#a6adc8]">
                            <span>1x (Real)</span>
                            <span>100x</span>
                            <span>240x (1 dia = 6m)</span>
                          </div>
                          <input
                            type="range"
                            min={1}
                            max={240}
                            value={simulationSpeed}
                            onChange={(e) => setSimulationSpeed(Number(e.target.value))}
                            className="w-full h-1 bg-[#313244] rounded-lg appearance-none cursor-pointer accent-[#cba6f7]"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 bg-[#11111b]/30 p-2.5 rounded-xl border border-[#313244]/80 text-[10px] mb-3 font-mono">
                        <div className="flex flex-col">
                          <span className="opacity-60 text-[8px] uppercase tracking-wider">Escola Selecionada</span>
                          <span className="font-bold text-[#cdd6f4] truncate">{selectedSchool?.nome || 'Nenhuma'}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="opacity-60 text-[8px] uppercase tracking-wider">Leitor Portaria</span>
                          <span className={`font-bold ${schoolMetadata?.dispositivoLeituraId ? 'text-[#a6e3a1]' : 'text-red-400'}`}>
                            {schoolMetadata?.dispositivoLeituraId ? 'ONLINE' : 'NENHUM'}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col gap-1.5 flex-1 min-h-0">
                        <div className="flex justify-between items-center text-[9px] text-[#a6adc8] uppercase tracking-wider font-bold">
                          <span>Log de Atividades E2E</span>
                          <button
                            onClick={() => setSimulationLogs([])}
                            className="text-[9px] text-[#a6adc8]/60 hover:text-[#cdd6f4] bg-transparent border border-none cursor-pointer"
                          >
                            Limpar
                          </button>
                        </div>
                        <div
                          className="bg-[#11111b] border border-[#313244] rounded-xl p-2.5 flex-1 overflow-y-auto font-mono text-[9px] text-[#a6adc8] flex flex-col gap-1.5 scrollbar-thin select-text"
                        >
                          {simulationLogs.length === 0 ? (
                            <div className="opacity-40 italic text-center py-4">Sem atividades de simulação no momento.</div>
                          ) : (
                            simulationLogs.map((log, idx) => (
                              <div
                                key={idx}
                                className={
                                  log.includes('[RFID]') ? 'text-[#cba6f7]' :
                                  log.includes('[Chamada]') ? 'text-[#89b4fa]' :
                                  log.includes('[Comunicado]') ? 'text-[#f9e2af]' : 'text-[#a6adc8]'
                                }
                              >
                                {log}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (() => {
                    const isSchoolSplit = !!win.schoolId && win.id !== 'window-admin-central';
                    // Celulares só via toggle "Exibir" — abas PROF/RESP/ALUNO carregam no iframe desktop
                    const showProfPane =
                      !!win.showMobileProfessor || win.schoolMobileTab === 'PROFESSOR';
                    const showRespPane =
                      !!win.showMobileResponsavel || win.schoolMobileTab === 'RESPONSAVEL';
                    const mobilePanes = (
                      [
                        showProfPane ? 'PROFESSOR' : null,
                        showRespPane ? 'RESPONSAVEL' : null,
                      ] as const
                    ).filter(Boolean) as Array<'PROFESSOR' | 'RESPONSAVEL'>;
                    const deskTab = (win.activeTab || 'DIRETOR') as DesktopTabId;
                    const winDesk: DesktopWindow = isSchoolSplit
                      ? {
                          ...win,
                          activeTab: deskTab,
                          actorId:
                            deskTab === 'PREFEITO'
                              ? win.actorId
                              : (win.roleActors?.[deskTab as SchoolRoleTab] || win.actorId),
                          customUrl:
                            deskTab === 'PREFEITO'
                              ? DESKTOP_TAB_REDIRECT.PREFEITO
                              : (SCHOOL_ROLE_REDIRECT[deskTab as SchoolRoleTab] ||
                                win.customUrl ||
                                '/admin/dashboard'),
                        }
                      : win;

                    const activeTabMeta = DESKTOP_WINDOW_TABS.find((t) => t.id === winDesk.activeTab);
                    const roleLevel = roleKeyToHierarchyLevel(activeRoleKey);
                    const blockIframe =
                      !!activeTabMeta?.needsSchool &&
                      roleLevel <= 1 &&
                      !(winDesk.schoolId || selectedSchool?.id);
                    if (blockIframe) {
                      return (
                        <div className="flex-1 flex items-center justify-center text-[11px] text-[#64748b] bg-[#11111b]">
                          Escolha uma escola acima para abrir o ambiente.
                        </div>
                      );
                    }
                    if (winDesk.id === 'window-painel-executivo' && !resolveWindowSession(winDesk)) {
                      return (
                        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-[12px] text-[#f38ba8] bg-[#11111b] px-6 text-center">
                          <span className="font-bold">Sem sessão do prefeito</span>
                          <span className="text-[#64748b] text-[11px]">
                            Feche e clique de novo em Abrir painel executivo. Se persistir, confira o gestor municipal (Admin@2026).
                          </span>
                        </div>
                      );
                    }
                    if (
                      (winDesk.schoolId || winDesk.actorId) &&
                      !resolveWindowSession(winDesk) &&
                      winDesk.id !== 'window-painel-executivo' &&
                      winDesk.id !== 'window-admin-central' &&
                      !winDesk.customUrl?.startsWith('/master')
                    ) {
                      return (
                        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-[12px] text-[#f38ba8] bg-[#11111b] px-6 text-center">
                          <span className="font-bold">Sem JWT desta escola</span>
                          <span className="text-[#64748b] text-[11px]">
                            Feche a janela e clique de novo no card. Se persistir, o diretor pode estar sem seed nesta unidade.
                          </span>
                        </div>
                      );
                    }
                    const useImpersonateSrc =
                      !!winDesk.schoolId ||
                      !!winDesk.actorId ||
                      winDesk.id === 'window-painel-executivo' ||
                      winDesk.id === 'window-admin-central' ||
                      !!winDesk.customUrl?.startsWith('/master') ||
                      (!!winDesk.customUrl && winDesk.profile === 'PREFEITO' && !winDesk.activeTab);

                    const activePanelKey = getTabPanelKey(winDesk);
                    const panels = winDesk.tabPanels || {};
                    // Garante pelo menos o painel ativo (janelas antigas sem tabPanels)
                    const ensured = panels[activePanelKey]?.src
                      ? winDesk
                      : withTabPanel(winDesk);
                    const panelMap = ensured.tabPanels || {};

                    const desktopPane = (
                      <div className="relative flex-1 w-full h-full min-h-0 bg-[#11111b]">
                        {Object.entries(panelMap).map(([panelKey, panel]) => {
                          const isActive = panelKey === activePanelKey;
                          return (
                            <iframe
                              key={`${winDesk.id}-panel-${panelKey}`}
                              id={`iframe-desktop-${winDesk.id}-${panelKey}`}
                              src={panel.src}
                              title={`Panel ${panelKey}`}
                              className="absolute inset-0 w-full h-full border-none bg-[#11111b]"
                              style={{
                                visibility: isActive ? 'visible' : 'hidden',
                                pointerEvents: isActive ? 'auto' : 'none',
                                zIndex: isActive ? 2 : 1,
                              }}
                            />
                          );
                        })}
                        {useImpersonateSrc && Object.keys(panelMap).length === 0 && (
                          <iframe
                            id={`iframe-desktop-${winDesk.id}`}
                            src={buildSchoolIframeSrc(winDesk)}
                            title={`Panel ${winDesk.activeTab || winDesk.profile}`}
                            className="w-full h-full border-none bg-[#11111b]"
                          />
                        )}
                      </div>
                    );

                    // Escola: desktop à esquerda + 1–2 celulares à direita (toggles independentes)
                    if (isSchoolSplit) {
                      const phoneColW = mobilePanes.length >= 2
                        ? 'w-[min(720px,58%)]'
                        : mobilePanes.length === 1
                          ? 'w-[min(380px,42%)]'
                          : '';
                      return (
                        <div className="flex-1 flex min-h-0 min-w-0 overflow-hidden">
                          <div className="flex-1 min-w-0 min-h-0 relative flex flex-col">
                            {desktopPane}
                          </div>
                          {mobilePanes.length > 0 && (
                            <div className={`${phoneColW} shrink-0 border-l border-[#313244] bg-[#0b0d14] flex min-h-0`}>
                              {mobilePanes.map((role) => {
                                // Header PROF: professor à esquerda + responsáveis (turmas) à direita
                                // Header RESP: responsáveis agrupados por turma do professor logado
                                const showProfessorSelect = role === 'PROFESSOR';
                                const showResponsavelSelect = true;
                                const actorKey = `${win.roleActors?.PROFESSOR || 'noprof'}:${win.roleActors?.[role] || 'none'}`;
                                return (
                                <div
                                  key={`${win.id}-mobile-col-${role}`}
                                  className={`flex flex-col min-h-0 min-w-0 flex-1 ${
                                    mobilePanes.length > 1 ? 'border-r border-[#313244]/60 last:border-r-0' : ''
                                  }`}
                                >
                                  <SchoolEmbedMobileHeader
                                    schoolId={win.schoolId!}
                                    role={role}
                                    professorId={win.roleActors?.PROFESSOR}
                                    responsavelId={win.roleActors?.RESPONSAVEL}
                                    showProfessorSelect={showProfessorSelect}
                                    showResponsavelSelect={showResponsavelSelect}
                                    toolsMenuOpen={!!embedToolsMenu[role]}
                                    onToggleTools={() => {
                                      setEmbedToolsMenu((prev) => ({
                                        ...prev,
                                        [role]: !prev[role],
                                      }));
                                    }}
                                    onHide={() => {
                                      setEmbedToolsMenu((prev) => ({ ...prev, [role]: false }));
                                      setWindows((prev) =>
                                        prev.map((w) => {
                                          if (w.id !== win.id) return w;
                                          const profOn =
                                            !!w.showMobileProfessor || w.schoolMobileTab === 'PROFESSOR';
                                          const respOn =
                                            !!w.showMobileResponsavel || w.schoolMobileTab === 'RESPONSAVEL';
                                          return {
                                            ...w,
                                            showMobileProfessor: role === 'PROFESSOR' ? false : profOn,
                                            showMobileResponsavel: role === 'RESPONSAVEL' ? false : respOn,
                                            schoolMobileTab: null,
                                          };
                                        }),
                                      );
                                    }}
                                    onProfessorChange={(nextProfessorId) => {
                                      setWindows((prev) =>
                                        prev.map((w) => {
                                          if (w.id !== win.id) return w;
                                          return {
                                            ...w,
                                            roleActors: {
                                              ...(w.roleActors || {}),
                                              PROFESSOR: nextProfessorId,
                                            },
                                          };
                                        }),
                                      );
                                    }}
                                    onResponsavelChange={(nextResponsavelId) => {
                                      setWindows((prev) =>
                                        prev.map((w) => {
                                          if (w.id !== win.id) return w;
                                          const nextActors = { ...(w.roleActors || {}) };
                                          if (nextResponsavelId) {
                                            nextActors.RESPONSAVEL = nextResponsavelId;
                                          } else {
                                            delete nextActors.RESPONSAVEL;
                                          }
                                          return { ...w, roleActors: nextActors };
                                        }),
                                      );
                                    }}
                                  />
                                  <div className="flex-1 relative min-h-0 overflow-hidden">
                                    <PhoneSimulatorContainer
                                      key={`${win.id}-${role}-${actorKey}`}
                                      device={role}
                                      actorId={win.roleActors?.[role]}
                                      schoolId={win.schoolId}
                                      isBackendOnline={isBackendOnline}
                                      compactEmbed
                                      toolsMenuOpen={!!embedToolsMenu[role]}
                                      onToolsMenuOpenChange={(open) => {
                                        setEmbedToolsMenu((prev) => ({ ...prev, [role]: open }));
                                      }}
                                      pairedProfessorId={
                                        role === 'RESPONSAVEL'
                                          ? win.roleActors?.PROFESSOR
                                          : undefined
                                      }
                                    />
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    return desktopPane;
                  })()}
                </div>
              )}
            </div>

            {/* 8-way Resize Handles */}
            {!win.isMaximized && (
              <>
                {/* Bordas Laterais */}
                <div
                  onMouseDown={(e) => handleResizeStart(win.id, 'n', e)}
                  className="absolute top-0 left-0 right-0 h-1.5 cursor-n-resize z-30"
                  title="Redimensionar Altura (Topo)"
                />
                <div
                  onMouseDown={(e) => handleResizeStart(win.id, 's', e)}
                  className="absolute bottom-0 left-0 right-0 h-1.5 cursor-s-resize z-30"
                  title="Redimensionar Altura (Base)"
                />
                {!isMobile && (
                  <>
                    <div
                      onMouseDown={(e) => handleResizeStart(win.id, 'e', e)}
                      className="absolute top-0 bottom-0 right-0 w-1.5 cursor-e-resize z-30"
                      title="Redimensionar Largura (Direita)"
                    />
                    <div
                      onMouseDown={(e) => handleResizeStart(win.id, 'w', e)}
                      className="absolute top-0 bottom-0 left-0 w-1.5 cursor-w-resize z-30"
                      title="Redimensionar Largura (Esquerda)"
                    />
                    {/* Cantos */}
                    <div
                      onMouseDown={(e) => handleResizeStart(win.id, 'nw', e)}
                      className="absolute top-0 left-0 w-3 h-3 cursor-nwse-resize z-30"
                      title="Redimensionar Diagonal (Superior Esquerdo)"
                    />
                    <div
                      onMouseDown={(e) => handleResizeStart(win.id, 'ne', e)}
                      className="absolute top-0 right-0 w-3 h-3 cursor-nesw-resize z-30"
                      title="Redimensionar Diagonal (Superior Direito)"
                    />
                    <div
                      onMouseDown={(e) => handleResizeStart(win.id, 'sw', e)}
                      className="absolute bottom-0 left-0 w-3 h-3 cursor-nesw-resize z-30"
                      title="Redimensionar Diagonal (Inferior Esquerdo)"
                    />
                    <div
                      onMouseDown={(e) => handleResizeStart(win.id, 'se', e)}
                      className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize z-30"
                      title="Redimensionar Diagonal (Inferior Direito)"
                    >
                      <svg className="w-2.5 h-2.5 text-[#313244] hover:text-[#89b4fa] transition-colors absolute bottom-0.5 right-0.5 pointer-events-none" viewBox="0 0 10 10">
                        <path d="M10,0 L0,10 M10,4 L4,10 M10,8 L8,10" stroke="currentColor" strokeWidth="1.2" />
                      </svg>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Start Menu Popup (Estilo Imagem 2) */}
      {isStartMenuOpen && (
        <CockpitStartMenu
          theme={theme}
          activeRoleKey={activeRoleKey}
          onClose={() => setIsStartMenuOpen(false)}
          onOpenApp={(appType) => {
            if (appType === 'documents') {
              openOfficeWindow('new-doc', 'Novo Documento.docx', 'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,');
            } else if (appType === 'planilhas') {
              openOfficeWindow('new-cell', 'Nova Planilha.xlsx', 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,');
            } else if (appType === 'apresentacoes') {
              openOfficeWindow('new-slide', 'Nova Apresentação.pptx', 'data:application/vnd.openxmlformats-officedocument.presentationml.presentation;base64,');
            }
          }}
          onOpenFolder={() => {
            openItem('shortcut-cities', 'FOLDER', 'Cidades', undefined);
          }}
          onLogout={stopSimulation || (() => toast.info('Logout'))}
          onOpenTransitions={() => setIsTransitionsPanelOpen(true)}
        />
      )}

      {/* 3. Taskbar inferior modularizada */}
      <CockpitTaskbar
        theme={theme}
        activeRole={activeRoleKey}
        environmentName={activeRoleKey === 'administrador' ? 'Ambiente do Administrador' : `Ambiente do ${activeRoleKey.charAt(0).toUpperCase() + activeRoleKey.slice(1)}`}
        openWindows={windows.map(win => ({ id: win.id, title: win.title, type: win.type, isMinimized: win.isMinimized }))}
        activeWindowId={activeWindowId}
        desktopTime={desktopTime}
        rolePrimary={activeRoleConfig.primary}
        roleSecondary={activeRoleConfig.secondary}
        activePhoneProfiles={windows
          .filter((w) => w.type === 'MOBILE' && !w.isMinimized)
          .map((w) => w.profile)}
        onTogglePhoneSlot={(slot, profile) => togglePhoneSlot(slot, profile)}
        autoHidden={(() => {
          const active = windows.find((w) => w.id === activeWindowId);
          const maximizedActive = !!active && active.isMaximized && !active.isMinimized;
          if (!maximizedActive) return false;
          if (isStartMenuOpen || taskbarEdgeRevealed) return false;
          return true;
        })()}
        onThemeToggle={toggleTheme}
        onLogout={stopSimulation || (() => toast.info('Logout'))}
        onStartMenuToggle={() => setIsStartMenuOpen(prev => !prev)}
        onFocusWindow={focusWindow}
        onMinimizeWindow={minimizeWindow}
        onOpenApp={(appType) => {
          if (appType === 'documents') {
            openOfficeWindow('new-doc', 'Novo Documento.docx', 'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,');
          } else if (appType === 'planilhas') {
            openOfficeWindow('new-cell', 'Nova Planilha.xlsx', 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,');
          } else if (appType === 'apresentacoes') {
            openOfficeWindow('new-slide', 'Nova Apresentação.pptx', 'data:application/vnd.openxmlformats-officedocument.presentationml.presentation;base64,');
          }
        }}
        onRoleSelect={(roleId) => {
          // Auto-connect: verifica ator salvo em memoria
          const savedActorRaw = localStorage.getItem(`ac_selected_actor_${roleId}`);
          if (savedActorRaw) {
            try {
              const saved = JSON.parse(savedActorRaw);
              if (saved?.userId) {
                // Ator salvo encontrado — transicao direta
                setTransitionRoleKeys({ current: activeRoleKey, next: roleId });
                setIsTransitioning(true);
                return;
              }
            } catch { }
          }

          // Sem ator salvo: busca no MOCK_USERS_BY_ROLE
          const mockUsers = MOCK_USERS_BY_ROLE[roleId.toLowerCase()] || [];
          if (mockUsers.length > 0) {
            // Auto-seleciona o primeiro e salva
            const first = mockUsers[0];
            localStorage.setItem(`ac_selected_actor_${roleId}`, JSON.stringify({
              userId: first.id,
              userName: first.name,
            }));
            setTransitionRoleKeys({ current: activeRoleKey, next: roleId });
            setIsTransitioning(true);
            return;
          }

          // Nenhum usuario disponivel: abre seletor (fallback p/ cadastrar)
          setSelectedRoleToChange(roleId);
        }}
        onActorSelect={(roleId, actorId, actorName) => {
          // Chamado pelo hover tooltip do UserProfile
          localStorage.setItem(`ac_selected_actor_${roleId}`, JSON.stringify({
            userId: actorId,
            userName: actorName,
          }));
          setTransitionRoleKeys({ current: activeRoleKey, next: roleId });
          setIsTransitioning(true);
        }}
        onOpenProfile={() => {
          const activeRoleLower = activeRoleKey.toLowerCase();
          const mockUsers = MOCK_USERS_BY_ROLE[activeRoleLower] || [];
          let activeUser = mockUsers[0];
          try {
            const saved = JSON.parse(localStorage.getItem(`ac_selected_actor_${activeRoleLower}`) || 'null');
            if (saved && saved.userId) {
              const matched = mockUsers.find(u => u.id === saved.userId);
              if (matched) activeUser = matched;
            }
          } catch { }

          if (!activeUser) return;
          const profileKey = activeRoleKey.toUpperCase() as keyof typeof PROFILE_CONFIGS;
          
          const getProfileUrl = (role: string) => {
            const roleLower = role.toLowerCase();
            if (roleLower === 'prefeito') return '/municipal/perfil';
            if (roleLower === 'diretor' || roleLower === 'secretaria' || roleLower === 'coordenador') return '/equipe/perfil';
            return '/admin/perfil';
          };
          const url = getProfileUrl(activeRoleKey);
          
          const existing = windows.find(w => w.actorId === activeUser.id);
          if (existing) {
            setWindows(prev => prev.map(w => w.id === existing.id ? { ...w, customUrl: url, isMinimized: false } : w));
            focusWindow(existing.id);
          } else {
            openWindowForActor(profileKey, activeUser.id, activeUser.name, true, url);
          }
        }}
        onOpenSettings={() => {
          const activeRoleLower = activeRoleKey.toLowerCase();
          const mockUsers = MOCK_USERS_BY_ROLE[activeRoleLower] || [];
          let activeUser = mockUsers[0];
          try {
            const saved = JSON.parse(localStorage.getItem(`ac_selected_actor_${activeRoleLower}`) || 'null');
            if (saved && saved.userId) {
              const matched = mockUsers.find(u => u.id === saved.userId);
              if (matched) activeUser = matched;
            }
          } catch { }

          if (!activeUser) return;
          const profileKey = activeRoleKey.toUpperCase() as keyof typeof PROFILE_CONFIGS;
          
          const getSettingsUrl = (role: string) => {
            const roleLower = role.toLowerCase();
            if (roleLower === 'prefeito') return '/municipal/configuracoes';
            if (roleLower === 'diretor' || roleLower === 'secretaria' || roleLower === 'coordenador') return '/equipe/configuracoes';
            return '/admin/configuracoes';
          };
          const url = getSettingsUrl(activeRoleKey);
          
          const existing = windows.find(w => w.actorId === activeUser.id);
          if (existing) {
            setWindows(prev => prev.map(w => w.id === existing.id ? { ...w, customUrl: url, isMinimized: false } : w));
            focusWindow(existing.id);
          } else {
            openWindowForActor(profileKey, activeUser.id, activeUser.name, true, url);
          }
        }}
      />

      {/* 4. Menu de Contexto (Right-Click no Vazio) */}
      {contextMenu && (
        <div
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          className={`absolute z-[3000] w-48 rounded-xl border p-1.5 backdrop-blur-md shadow-2xl ${t.contextMenu} flex flex-col gap-0.5`}
        >
          <button
            onClick={createFolder}
            className={`w-full px-3 py-2 rounded-lg text-left bg-transparent border-none cursor-pointer text-inherit text-[10px] font-semibold flex items-center gap-2 ${t.contextMenuHover}`}
          >
            📁 Criar Pasta
          </button>
          <button
            onClick={createShortcut}
            className={`w-full px-3 py-2 rounded-lg text-left bg-transparent border-none cursor-pointer text-inherit text-[10px] font-semibold flex items-center gap-2 ${t.contextMenuHover}`}
          >
            🔗 Criar Pasta Músicas
          </button>
          <button
            onClick={() => setAutoArrange(prev => {
              const next = !prev;
              localStorage.setItem('ac_desktop_autoarrange', JSON.stringify(next));
              return next;
            })}
            className={`w-full px-3 py-2 rounded-lg text-left bg-transparent border-none cursor-pointer text-inherit text-[10px] font-semibold flex items-center gap-2 ${t.contextMenuHover}`}
          >
            {autoArrange ? '✅ Auto Organizar' : '⬜ Auto Organizar'}
          </button>
          <div className={`h-[1px] my-1 ${theme === 'light' ? 'bg-[#cbd5e1]' : 'bg-[#313244]/40'}`} />

          <div className="px-3 py-1 text-[8px] font-bold uppercase tracking-wider opacity-50">Tema</div>
          <button
            onClick={() => changeTheme('purple')}
            className={`w-full px-3 py-1.5 rounded-lg text-left bg-transparent border-none cursor-pointer text-inherit text-[10px] flex items-center justify-between ${t.contextMenuHover}`}
          >
            💜 Purple {theme === 'purple' && '✓'}
          </button>
          <button
            onClick={() => changeTheme('dark')}
            className={`w-full px-3 py-1.5 rounded-lg text-left bg-transparent border-none cursor-pointer text-inherit text-[10px] flex items-center justify-between ${t.contextMenuHover}`}
          >
            🖤 Dark {theme === 'dark' && '✓'}
          </button>
          <button
            onClick={() => changeTheme('light')}
            className={`w-full px-3 py-1.5 rounded-lg text-left bg-transparent border-none cursor-pointer text-inherit text-[10px] flex items-center justify-between ${t.contextMenuHover}`}
          >
            🤍 Light {theme === 'light' && '✓'}
          </button>
        </div>
      )}

      {/* 5. Menu de Contexto para Ícones (Right-Click no Ícone) */}
      {iconContextMenu && (
        <div
          style={{ top: `${iconContextMenu.y}px`, left: `${iconContextMenu.x}px` }}
          className={`absolute z-[3000] w-40 rounded-xl border p-1.5 backdrop-blur-md shadow-2xl ${t.contextMenu} flex flex-col gap-0.5`}
        >
          <button
            onClick={() => openItem(iconContextMenu.itemId, iconContextMenu.itemType, iconContextMenu.itemName, iconContextMenu.profileKey)}
            className={`w-full px-3 py-2 rounded-lg text-left bg-transparent border-none cursor-pointer text-inherit text-[10px] font-semibold flex items-center gap-2 ${t.contextMenuHover}`}
          >
            ⚡ Abrir
          </button>
          {iconContextMenu.itemType !== 'PROFILE' && iconContextMenu.itemId !== 'item-word' && iconContextMenu.itemId !== 'item-excel' && iconContextMenu.itemId !== 'item-powerpoint' && (
            <>
              <button
                onClick={() => renameDesktopItem(iconContextMenu.itemId)}
                className={`w-full px-3 py-2 rounded-lg text-left bg-transparent border-none cursor-pointer text-inherit text-[10px] font-semibold flex items-center gap-2 ${t.contextMenuHover}`}
              >
                🏷️ Renomear
              </button>
              <button
                onClick={() => deleteDesktopItem(iconContextMenu.itemId)}
                className={`w-full px-3 py-2 rounded-lg text-left bg-transparent border-none cursor-pointer text-inherit text-[10px] font-semibold flex items-center gap-2 ${t.contextMenuHover}`}
              >
                🗑️ Excluir
              </button>
            </>
          )}
        </div>
      )}
      {/* 6. Modal Seletor de Atores (para múltiplos usuários do mesmo perfil) */}
      {profileSelector && (
        <div className="absolute inset-0 bg-black/75 backdrop-blur-md z-[99999] flex items-center justify-center pointer-events-auto">
          <div className="bg-[#1e1e2e]/98 border border-[#4c1d95]/60 rounded-3xl p-6 shadow-[0_25px_60px_rgba(0,0,0,0.8)] w-[560px] max-w-[90vw] flex flex-col gap-4 text-[#cdd6f4]">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-[#313244] pb-3">
              <div className="flex flex-col">
                <span className="font-bold text-base text-[#cba6f7]">Selecionar Ator para Simular</span>
                <span className="text-xs text-[#a6adc8] mt-0.5">Escolha o usuário do perfil {profileSelector.profileKey}</span>
              </div>
              <button
                onClick={() => setProfileSelector(null)}
                className="text-white hover:text-red-400 font-bold bg-[#313244]/40 hover:bg-[#313244]/80 w-8 h-8 rounded-full border-none flex items-center justify-center cursor-pointer text-sm transition-all active:scale-90"
                title="Fechar Seletor"
              >
                ✕
              </button>
            </div>

            {/* List */}
            <div className="flex flex-col gap-3 max-h-[360px] overflow-y-auto pr-1">
              {profileSelector.actors.map(actor => (
                <button
                  key={actor.user.id}
                  onClick={() => {
                    openWindowForActor(profileSelector.profileKey, actor.user.id, actor.user.nome);
                    setProfileSelector(null);
                  }}
                  className="w-full flex justify-between items-center p-4 rounded-xl bg-[#181825]/90 border border-[#313244] hover:bg-[#4c1d95]/15 hover:border-[#4c1d95]/50 transition-all text-left text-inherit cursor-pointer active:scale-[0.99] gap-4"
                >
                  <div className="flex flex-col">
                    <span className="font-bold text-sm text-[#cdd6f4] tracking-wide">{actor.user.nome}</span>
                    <span className="text-xs text-[#a6adc8] font-mono mt-1 select-all">{actor.user.email || 'Sem e-mail'}</span>
                  </div>
                  <span className="px-4 py-1.5 text-xs font-bold text-[#cba6f7] hover:text-white bg-[#4c1d95]/30 hover:bg-[#4c1d95]/60 border border-[#4c1d95]/40 rounded-xl transition-all shadow">
                    Iniciar
                  </span>
                </button>
              ))}
            </div>

            {/* Footer Close Button */}
            <button
              onClick={() => setProfileSelector(null)}
              className="w-full mt-2 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 hover:text-white rounded-xl text-xs font-bold transition-all cursor-pointer text-center active:scale-95"
            >
              FECHAR
            </button>
          </div>
        </div>
      )}

      {/* ═══ OVERLAYS DE PREVIEW DE ENCAIXE (SNAPPING GHOST PREVIEW) ═══ */}
      {snapPreview && (
        <div
          className="absolute transition-all duration-200 ease-out border border-[#cba6f7]/60 bg-[#cba6f7]/10 z-[9999] pointer-events-none rounded-xl"
          style={{
            left: snapPreview === 'left' ? '8px' : snapPreview === 'right' ? 'calc(50% + 4px)' : '8px',
            top: '8px',
            width: snapPreview === 'top' ? 'calc(100% - 16px)' : 'calc(50% - 12px)',
            height: 'calc(100% - 64px)', // Desconto da taskbar + margem
            backdropFilter: 'blur(3px)',
            boxShadow: '0 0 30px rgba(203, 166, 247, 0.15) inset, 0 0 20px rgba(203, 166, 247, 0.1)',
          }}
        />
      )}

      {/* ═══ ESTILOS TÁTICOS PARA ARRASTO SUAVE E CURSOR GLOBAIS ═══ */}
      {draggingId && (
        <style dangerouslySetInnerHTML={{
          __html: `
          iframe { pointer-events: none !important; }
          body, * { cursor: grabbing !important; }
        ` }} />
      )}

      {/* 7. Modal de Configuração de Ambientes */}
      {isEnvModalOpen && (
        <div className="absolute inset-0 bg-black/75 backdrop-blur-md z-[99999] flex items-center justify-center pointer-events-auto">
          <div className="bg-[#1e1e2e]/98 border border-[#cba6f7]/40 rounded-3xl p-6 shadow-[0_25px_60px_rgba(0,0,0,0.8)] w-[500px] max-w-[90vw] flex flex-col gap-4 text-[#cdd6f4]">
            <div className="flex justify-between items-center border-b border-[#313244] pb-3">
              <div className="flex flex-col">
                <span className="font-bold text-base text-[#cba6f7]">Criar Novo Ambiente</span>
                <span className="text-xs text-[#a6adc8] mt-0.5">Isolar sessões por escola ou município</span>
              </div>
              <button
                onClick={() => setIsEnvModalOpen(false)}
                className="text-white hover:text-red-400 font-bold bg-[#313244]/40 hover:bg-[#313244]/80 w-8 h-8 rounded-full border-none flex items-center justify-center cursor-pointer text-sm transition-all active:scale-90"
              >
                ✕
              </button>
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-xs font-bold text-[#a6adc8] uppercase">Nome do Ambiente</label>
              <input
                type="text"
                placeholder="Ex: Prefeitura de Belo Horizonte"
                value={envForm.name}
                onChange={e => setEnvForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full bg-[#181825] border border-[#313244] rounded-xl px-4 py-2.5 text-sm text-[#cdd6f4] focus:outline-none focus:border-[#cba6f7] transition-all"
              />

              <label className="text-xs font-bold text-[#a6adc8] uppercase mt-2">Município (Cidade)</label>
              <select
                value={envForm.municipioId}
                onChange={e => {
                  const val = e.target.value;
                  setEnvForm(prev => ({ ...prev, municipioId: val, escolaId: '' }));
                }}
                className="w-full bg-[#181825] border border-[#313244] rounded-xl px-4 py-2.5 text-sm text-[#cdd6f4] focus:outline-none focus:border-[#cba6f7] transition-all"
              >
                <option value="">Selecione um Município</option>
                {Array.from(new Set(dbSchools.map(s => s.municipio?.nome).filter(Boolean))).map(munName => {
                  const schoolForMun = dbSchools.find(s => s.municipio?.nome === munName);
                  const munId = schoolForMun?.municipio?.id || '';
                  return <option key={munId} value={munId}>{munName}</option>;
                })}
              </select>

              <label className="text-xs font-bold text-[#a6adc8] uppercase mt-2">Escola Relacionada</label>
              <select
                value={envForm.escolaId}
                onChange={e => setEnvForm(prev => ({ ...prev, escolaId: e.target.value }))}
                className="w-full bg-[#181825] border border-[#313244] rounded-xl px-4 py-2.5 text-sm text-[#cdd6f4] focus:outline-none focus:border-[#cba6f7] transition-all"
              >
                <option value="">Sem Escola Específica (Escopo Prefeitura)</option>
                {dbSchools
                  .filter(s => s.municipio?.id === envForm.municipioId)
                  .map(school => (
                    <option key={school.id} value={school.id}>{school.nome}</option>
                  ))}
              </select>

              <label className="text-xs font-bold text-[#a6adc8] uppercase mt-2">Cor do Identificador</label>
              <div className="flex gap-2.5 mt-1">
                {['#cba6f7', '#f38ba8', '#89b4fa', '#a6e3a1', '#f9e2af', '#cba6f7'].map(c => (
                  <button
                    key={c}
                    onClick={() => setEnvForm(prev => ({ ...prev, cor: c }))}
                    style={{ backgroundColor: c }}
                    className={`w-6 h-6 rounded-full border-2 cursor-pointer transition-all active:scale-75 ${envForm.cor === c ? 'border-white scale-110' : 'border-transparent'}`}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-[#313244] pt-4 mt-2">
              <button
                onClick={() => setIsEnvModalOpen(false)}
                className="px-4 py-2 bg-[#313244] text-[#cdd6f4] rounded-xl border-none cursor-pointer text-xs font-bold transition-all hover:bg-[#45475a] active:scale-95"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateEnvironment}
                className="px-4 py-2 bg-[#cba6f7] text-[#11111b] rounded-xl border-none cursor-pointer text-xs font-black transition-all hover:bg-[#b4befe] active:scale-95"
              >
                Criar Ambiente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 8. Dock Lateral de Celulares (sempre visível para perfis autorizados) */}
      {ENABLE_SIDE_DOCK && isEmulatorAllowed && (
        <div className="fixed right-4 top-20 bottom-16 flex flex-col justify-center gap-4 z-[9999] pointer-events-auto select-none">
          {(['CELULAR1', 'CELULAR2', 'CELULAR3', 'CELULAR4'] as const).map((cellKey, idx) => {
            const win = windows.find(w => w.profile === cellKey && w.type === 'MOBILE');
            const isActive = win && !win.isMinimized;
            const config = PROFILE_CONFIGS[cellKey];

            return (
              <div
                key={cellKey}
                onClick={() => {
                  if (win) {
                    // Já existe: toggle visibilidade
                    if (win.isMinimized) {
                      setWindows(prev => prev.map(w => w.id === win.id ? { ...w, isMinimized: false } : w));
                      focusWindow(win.id);
                    } else {
                      // Já visível: foca
                      focusWindow(win.id);
                    }
                  } else {
                    // Não existe ainda: cria
                    openWindow(cellKey);
                  }
                }}
                className={`relative w-[90px] h-[160px] rounded-2xl border-2 shadow-2xl overflow-hidden cursor-pointer group transition-all duration-300 hover:scale-[2.2] hover:-translate-x-[60px] hover:z-[99999] ${isActive
                  ? 'bg-[#1e1e2e] border-[#cba6f7]/70 opacity-100 hover:border-[#cba6f7]'
                  : 'bg-[#11111b] border-[#45475a]/40 opacity-40 hover:opacity-80 hover:border-[#585b70]'
                  }`}
              >
                <div className={`py-1 px-2 border-b flex items-center justify-between text-[7px] font-bold ${isActive
                  ? 'bg-[#181825] border-[#313244] text-[#cba6f7]'
                  : 'bg-[#0d0d15] border-[#1e1e2e] text-[#585b70]'
                  }`}>
                  <span>CELULAR {idx + 1}</span>
                  <span className="text-[6px]">{isActive ? '🟢' : '⚫'}</span>
                </div>
                <div className={`w-full h-full pointer-events-none transition-opacity flex items-center justify-center ${isActive
                  ? 'opacity-85 group-hover:opacity-100 bg-black'
                  : 'opacity-50 bg-[#0a0a12]'
                  }`}>
                  <div className="text-center p-2">
                    {isActive ? (
                      <div className="text-[6px] text-[#a6adc8]">{config.title}</div>
                    ) : (
                      <>
                        <div className="text-[14px] mb-1 grayscale">📱</div>
                        <div className="text-[5px] text-[#585b70] uppercase tracking-wider">Desligado</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* 9. Seletor de Perfis Simulados (Cards de Trabalho) */}
      {selectedRoleToChange && (
        <SimulatedUserCardSelector
          roleKey={selectedRoleToChange}
          theme={theme}
          onClose={() => setSelectedRoleToChange(null)}
          onSelectUser={(user) => {
            setTransitionRoleKeys({ current: activeRoleKey, next: selectedRoleToChange });
            setIsTransitioning(true);
            setSelectedRoleToChange(null);
          }}
        />
      )}

      {/* 10. Transição 3D Cubo */}
      {isTransitioning && (
        <CockpitCubeTransition
          currentRoleKey={transitionRoleKeys.current}
          nextRoleKey={transitionRoleKeys.next}
          theme={theme}
          onComplete={() => {
            setActiveRoleKey(transitionRoleKeys.next);
            setIsTransitioning(false);

            const matchedEnvMap: Record<string, { name: string; cor: string }> = {
              administrador: { name: 'Administrador', cor: '#9b6cff' },
              prefeito: { name: 'Prefeito', cor: '#d8ad54' },
              diretor: { name: 'Diretor', cor: '#5971ff' },
              coordenador: { name: 'Coordenador', cor: '#2ccbba' },
              secretaria: { name: 'Secretaria', cor: '#ff7785' },
              professor: { name: 'Professor', cor: '#58e19d' },
              responsavel: { name: 'Responsável', cor: '#ff66a9' },
              aluno: { name: 'Aluno', cor: '#775fff' }
            };

            const nextKey = transitionRoleKeys.next.toLowerCase();
            const config = matchedEnvMap[nextKey];
            if (config) {
              const existingEnv = environments.find(e => e.name.toLowerCase() === nextKey);
              if (existingEnv) {
                handleSwitchEnvironment(existingEnv.id);
              } else {
                const newEnvId = `env-sim-${nextKey}-${Date.now()}`;
                const newEnv: AcEnvironment = {
                  id: newEnvId,
                  name: config.name,
                  cor: config.cor
                };
                const updated = [...environments, newEnv];
                setEnvironments(updated);
                localStorage.setItem('ac_environments', JSON.stringify(updated));
                handleSwitchEnvironment(newEnvId);
              }
            }
          }}
        />
      )}

      {pairingQr && (
        <SimActorPairingQrModal
          actorId={pairingQr.actorId}
          actorNome={pairingQr.actorNome}
          sessionId={pairingQr.sessionId}
          roleLabel={pairingQr.roleLabel}
          onClose={() => setPairingQr(null)}
        />
      )}

      <ACWalkthrough />
    </div>
  );
};

export const DesktopWindowManager = DesktopWindows;
